// askSmart-ui/src/pages/SMRTCOP.tsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";
import { isMobileApp } from "../utils/isMobileApp";
import { compressImage, ImageCompressionError } from "../utils/imageCompression";
import {
  isTxtrptSlot,
  openTxtrptReportPdf,
  copyTxtrptText,
} from "../utils/txtrptView";
import {
  copPhotoQueue,
  PhotoSlotStatus,
  PendingPhoto,
} from "./smrtcop/copPhotoQueue";
import {
  isSubmittedPhoto,
  sectionHasPhotos,
  groupSectionPhotos,
  leftoverPhotos,
} from "./smrtcop/submissionPhotos";
import {
  isFsAccessSupported,
  pickPhotoDirectory,
  ensureDirPermission,
  writeFileToDir,
  downloadFileName,
  fetchPhotoBytes,
} from "./smrtcop/photoDownload";
import { DownloadIcon } from "../components/DownloadIcon";
import {
  antennaOptionsForSector,
  type AntennaPositionsBySector,
} from "./smrtcop/antennaDescriptors";
import {
  SiteHistoryStepKey,
  SiteHistory,
  deriveProgressStatus,
  buildSiteTimeline,
} from "./smrtcop/siteHistory";
import "./ManageSMRTCOP.css";
// SMRT Docs upload styling — reused verbatim by the Upload Reports wizard
// step so the file-pick UI matches the rest of the platform.
import "./ManageSMRTDocs.css";

// ─── Types (mirror ManageSMRTCOP) ────────────────────────────────────────────

type COPQuestionType =
  | "text"
  | "textarea"
  | "number"
  | "select"
  | "checkbox"
  | "date"
  | "photo"
  | "heading"
  | "certification";

interface COPVisibilityRule {
  questionKey: string;
  operator: "equals" | "notEquals";
  value: string;
}

interface COPQuestion {
  key: string;
  label: string;
  helper?: string;
  type: COPQuestionType;
  required: boolean;
  options?: string[];
  minLength?: number;
  maxLength?: number;
  mask?: string;
  visibleWhen?: COPVisibilityRule[];
  // Slots carrying the same groupKey belong to one checklist instruction
  // (e.g. "Photos for each antenna" → Antenna 1 / Antenna 2). The renderer
  // groups consecutive slots with a matching groupKey into a single row with
  // a shared groupLabel header.
  groupKey?: string;
  groupLabel?: string;
  groupIndex?: number;
  groupTotal?: number;
  // Full original checklist row description (un-cleaned, may include <br>
  // line breaks). Rendered above the capture UI so the crew sees the
  // verbatim instruction. Shared by every slot derived from one row, so
  // the renderer only displays it on the first slot of a group.
  instruction?: string;
  // General-step rendering: the parent text before any inline _A_/_B_/_C_
  // markers, plus each parsed sub-instruction body. Set only on questions
  // emitted by the General Photos section; sector questions leave these
  // undefined and fall back to `instruction`.
  groupTitle?: string;
  subInstructions?: string[];
  // Verification-step bundling: when set on a checkbox question, the
  // renderer paints `verifyPhotoCount` photo capture slots inline beneath
  // the checkbox. Slots persist as `${q.key}_photo_${i}` in formData.
  verifyPhotoCount?: number;
  // v2 photo slots: the group label is the full category name (which may
  // itself contain "SPD 1/2/3"), so buildFilename must NOT renumber "SPD N"
  // by slot index the way legacy side-by-side SPD groups did.
  fixedLabel?: boolean;
}

// Evaluate visibility — AND-combined across all rules. Mirrors the
// Manage Profiles isFieldVisible pattern, COP-local.
function isQuestionVisible(
  q: COPQuestion,
  formData: Record<string, unknown>,
): boolean {
  if (!q.visibleWhen || q.visibleWhen.length === 0) return true;
  return q.visibleWhen.every((rule) => {
    if (!rule.questionKey) return true;
    const raw = formData[rule.questionKey];
    const actual = Array.isArray(raw) ? raw.join(",") : String(raw ?? "");
    const matches = actual === rule.value;
    return rule.operator === "equals" ? matches : !matches;
  });
}

interface COPSection {
  id: number;
  label: string;
  questions: COPQuestion[];
  type?: "manual" | "bom" | "photo" | "reports" | "alignment" | "jha";
  // Runtime-synthesized sections (e.g. checklist-derived photo groups) carry
  // this flag so renderers can skip static expansion and treat the authored
  // questions as the final list.
  generated?: boolean;
  // Discriminator for runtime-synthesized sections that need a custom render
  // path. The Verification step renders its checkbox lines inside grouped
  // cards (one card per source row) rather than going through the per-
  // question render — using this flag keeps the dispatch explicit and
  // avoids label-string matching.
  kind?: "verification";
  // A single template "reports" section is split at runtime into two wizard
  // steps: a RET step (uploads + RRH serial photos) and an Alignment step.
  // This flag tells the renderers which half to show.
  reportKind?: "ret" | "alignment";
}

interface ChecklistPhotoItem {
  label?: string;
  sector: string;
  description: string;
  // v2 (Ericsson new site-tool) format only — see backend parseChecklistV2.
  status?: string; // "ACCEPTED" | "NA" | "NO_STATUS" | …
  categoryId?: string; // stable slug shared by the same category across sectors
  key?: string; // per-sector item key ("1".."30")
}

// "legacy" = original named-header checklist → A/B/C/SPD/Verification steps.
// "v2" = new Ericsson site-tool → three sector steps (Alpha/Beta/Gamma) of
// first-class photo categories. Auto-detected by the backend /checklist route.
type ChecklistFormat = "legacy" | "v2";

// Per-site N/A exclusions for the v2 wizard, keyed by naKey(categoryId, sector).
// `true` = excluded from that sector's step. An explicit entry always wins;
// when a key is absent the wizard falls back to the row's own Excel status.
type NaOverridesMap = Record<string, boolean>;

function naKey(categoryId: string, sector: string): string {
  return `${categoryId}::${sector}`;
}

function sanitizeNaOverrides(raw: unknown): NaOverridesMap {
  if (!raw || typeof raw !== "object") return {};
  const out: NaOverridesMap = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === "boolean") out[k] = v;
  }
  return out;
}

// Map a raw /checklist row (legacy or v2 shape) into a ChecklistPhotoItem.
// v2-only fields (status/categoryId/key) are carried through when present.
function mapChecklistRow(x: {
  label?: unknown;
  sector?: unknown;
  description?: unknown;
  status?: unknown;
  categoryId?: unknown;
  key?: unknown;
}): ChecklistPhotoItem {
  return {
    label: x.label != null ? String(x.label) : undefined,
    sector: String(x.sector ?? ""),
    description: String(x.description ?? ""),
    status: x.status != null ? String(x.status) : undefined,
    categoryId: x.categoryId != null ? String(x.categoryId) : undefined,
    key: x.key != null ? String(x.key) : undefined,
  };
}

// Upload Reports section — RET (per sector + port) plus Alignment. The
// template editor lets admins place a "reports" section anywhere in the
// wizard, but the slot list is fixed in code (not editable per template).
// RET reports are exported as .TXTRPT text files, one per sector and port
// (P1 / P4); Alpha/Beta/Gamma are always shown, Delta is optional and
// revealed with the "+" control. Files are stored via the existing
// /smrtcop/files/:siteId/:fieldKey endpoint; the persisted metadata object
// lives at formData[fieldKey].
interface ReportSlot {
  fieldKey: string;
  label: string;
  accept: string;
  // RET/RRH slots carry their sector + which of the site's two RET positions
  // ("p1" = first/lower, "p4" = second/higher) so the displayed label can be
  // computed from the site's actual positions (e.g. "Alpha P3") rather than a
  // hardcoded P1/P4. The Alignment slot leaves these undefined.
  sector?: string;
  port?: "p1" | "p4";
}

// RET reports are exported as .TXTRPT text files.
const RET_ACCEPT = ".txtrpt,.txt,text/plain";

// Fallback when a site has no RFDS-derived positions yet.
const DEFAULT_RET_POSITIONS: number[] = [1, 4];

// The antenna positions a site's RET scans cover, from the RFDS. Usually TWO,
// but a site where only one antenna is being replaced needs only ONE RET report
// — see the RFDS antenna-change rule in smrtcopOcr.ts. `port` "p1" → first
// position, "p4" → second; "p4" is undefined on a single-position site, and
// every slot bound to it is omitted rather than rendered unlabelled.
function retPositionsOf(
  meta: { retPositions?: number[] } | undefined | null,
): number[] {
  const p = meta?.retPositions;
  if (!Array.isArray(p)) return [...DEFAULT_RET_POSITIONS];
  const nums = p.map(Number).filter((n) => Number.isFinite(n));
  return nums.length ? nums : [...DEFAULT_RET_POSITIONS];
}
function portNumber(
  port: "p1" | "p4" | undefined,
  positions: number[],
): number | undefined {
  if (port === "p1") return positions[0];
  if (port === "p4") return positions[1];
  return undefined;
}
// Whether a slot applies to this site at all. Port-less slots (Alignment) always
// do; a port-bound slot only does when the site has a position for that port.
function slotApplies(
  slot: { port?: "p1" | "p4" },
  positions: number[],
): boolean {
  return !slot.port || portNumber(slot.port, positions) !== undefined;
}
// Displayed label for a RET slot, e.g. "Alpha P3". Non-RET slots (Alignment)
// fall back to their static label.
function retSlotLabel(slot: ReportSlot, positions: number[]): string {
  const n = portNumber(slot.port, positions);
  return slot.sector && n !== undefined ? `${slot.sector} P${n}` : slot.label;
}

// Standard RET slots — three sectors, two ports each. Always shown.
const RET_SLOTS: ReportSlot[] = [
  { fieldKey: "ret_alpha_p1", label: "Alpha P1", accept: RET_ACCEPT, sector: "Alpha", port: "p1" },
  { fieldKey: "ret_alpha_p4", label: "Alpha P4", accept: RET_ACCEPT, sector: "Alpha", port: "p4" },
  { fieldKey: "ret_beta_p1",  label: "Beta P1",  accept: RET_ACCEPT, sector: "Beta",  port: "p1" },
  { fieldKey: "ret_beta_p4",  label: "Beta P4",  accept: RET_ACCEPT, sector: "Beta",  port: "p4" },
  { fieldKey: "ret_gamma_p1", label: "Gamma P1", accept: RET_ACCEPT, sector: "Gamma", port: "p1" },
  { fieldKey: "ret_gamma_p4", label: "Gamma P4", accept: RET_ACCEPT, sector: "Gamma", port: "p4" },
];

// Optional fourth-sector RET slots, revealed via the "+" control and
// remembered across reloads by the `ret_delta_added` flag (or by the
// presence of already-uploaded Delta files).
const RET_DELTA_SLOTS: ReportSlot[] = [
  { fieldKey: "ret_delta_p1", label: "Delta P1", accept: RET_ACCEPT, sector: "Delta", port: "p1" },
  { fieldKey: "ret_delta_p4", label: "Delta P4", accept: RET_ACCEPT, sector: "Delta", port: "p4" },
];

// Alignment report — unchanged single PDF/image slot, shown after RET.
const ALIGNMENT_SLOT: ReportSlot = {
  fieldKey: "alignment_report",
  label: "Alignment Report",
  accept: "application/pdf,image/*",
};

// JHA (Job Hazard Analysis) — one PDF per day the crew is onsite. Starts at 5
// day slots (Day 1…Day 5); the "+" adds more. Slots are ReportFileRef uploads
// keyed `jha_day_{n}`; the current count persists at `jha_day_count`.
const JHA_ACCEPT = "application/pdf,.pdf";
const JHA_MIN_DAYS = 5;
function jhaDayCount(data: Record<string, any>): number {
  const n = Number(data?.jha_day_count);
  return Number.isFinite(n) && n > JHA_MIN_DAYS ? Math.floor(n) : JHA_MIN_DAYS;
}
function jhaSlots(data: Record<string, any>): ReportSlot[] {
  return Array.from({ length: jhaDayCount(data) }, (_, i) => ({
    fieldKey: `jha_day_${i + 1}`,
    label: `Day ${i + 1}`,
    accept: JHA_ACCEPT,
  }));
}

// True when the Delta sector has been added (flag set) or already has files.
function isDeltaAdded(data: Record<string, any>): boolean {
  return (
    !!data["ret_delta_added"] ||
    !!data["ret_delta_p1"] ||
    !!data["ret_delta_p4"]
  );
}

// The active list of report slots for a given form state: RET (+ Delta when
// added) followed by the Alignment slot. Used by the wizard renderer, the
// section-complete gate, and the review screen so all three stay in sync.
// RET upload slots only (Alpha/Beta/Gamma + Delta) — the RET step's gate.
// The Alignment slot is handled separately now that Alignment is its own step.
function retUploadSlots(
  data: Record<string, any>,
  positions: number[],
): ReportSlot[] {
  return [...RET_SLOTS, ...(isDeltaAdded(data) ? RET_DELTA_SLOTS : [])].filter(
    (s) => slotApplies(s, positions),
  );
}

// RET slots grouped one row per sector, so each sector renders as a single
// side-by-side row. Delta is appended when added. Grouped BY SECTOR rather than
// chunked in twos: a single-position site contributes one slot per sector, and
// pair-chunking would have put Alpha and Beta on the same row.
function sectorRetRows(
  deltaAdded: boolean,
  positions: number[],
): ReportSlot[][] {
  const slots = [...RET_SLOTS, ...(deltaAdded ? RET_DELTA_SLOTS : [])].filter(
    (s) => slotApplies(s, positions),
  );
  const rows: ReportSlot[][] = [];
  const bySector = new Map<string, ReportSlot[]>();
  for (const slot of slots) {
    const key = slot.sector || slot.fieldKey;
    if (!bySector.has(key)) {
      const row: ReportSlot[] = [];
      bySector.set(key, row);
      rows.push(row);
    }
    bySector.get(key)!.push(slot);
  }
  return rows;
}

// Connected-RRH serial photo slots — 6 per sector (one per RET device: P1
// #1-3, P4 #1-3). The tech captures a photo of each RRH label; the OCR-
// extracted serial is stored at `${fieldKey}_serial` and shown beside the box
// ("N/A" until a photo is added). Delta tracks the RET upload Delta toggle.
interface RrhSector { key: string; label: string }
const RRH_SECTORS: RrhSector[] = [
  { key: "alpha", label: "Alpha" },
  { key: "beta", label: "Beta" },
  { key: "gamma", label: "Gamma" },
];
const RRH_DELTA_SECTOR: RrhSector = { key: "delta", label: "Delta" };

// Matches an RRH serial photo slot key (e.g. "rrh_alpha_p1_2"). Drives both the
// OCR trigger and the traffic-light badge derivation.
const RRH_SLOT_RE = /^rrh_[a-z]+_(p1|p4)_[1-3]$/;
// Shown on a red RRH badge when the label OCR couldn't read a serial number.
const RRH_SERIAL_FAIL_REASON =
  "Serial number not detected — retake the RRH label photo.";

// Azimuth alignment photos — every v2 checklist category whose id ends in
// "azimuth" (both "TowerTop Antenna Validation Azimuth" and "TowerTop Mount
// Validation Azimuth"). The alignment tool's centre reticle is GREEN when
// aligned (pass) and RED when not (fail); the backend OCRs that colour. Matches
// the photo-slot keys (`{sectionId}_v2_{A|B|C}_{…azimuth}_{n}` and their
// `__extra__` slots), anchored on `$` so the `_blob`/`_status` sidecars don't
// match.
const AZIMUTH_SLOT_RE =
  /_v2_[a-c]_[a-z0-9_]*azimuth(?:_\d+|__extra__[a-z0-9]+)$/i;
const AZIMUTH_RED_REASON =
  "Azimuth not aligned — the centre indicator is red. Retake once it reads green.";
const AZIMUTH_UNREADABLE_REASON =
  "Couldn't read the alignment screen — retake so the green centre indicator is clear.";

// Tape Drop photos — the two v2 checklist categories whose id starts
// "tape_drop" ("Tape Drop Antenna RAD" and "Tape Drop Mount RAD"). The client
// requires feet & inches. Since the model can't reliably read the tape's
// numbers, the backend judges by BLADE COLOUR (this crew's convention: white =
// feet & inches → pass, yellow = metric → fail). Matches the photo-slot keys
// (base + `__extra__`), `$`-anchored so the `_blob`/`_status` sidecars don't
// match.
const TAPE_DROP_SLOT_RE =
  /_v2_[a-c]_tape_drop_[a-z0-9_]*(?:_\d+|__extra__[a-z0-9]+)$/i;
const TAPE_DROP_METRIC_REASON =
  "This looks like the metric (yellow) tape — the client requires feet & inches. Retake using the feet-and-inches tape.";
const TAPE_DROP_UNCLEAR_REASON =
  "Couldn't identify the measuring tape — retake with the tape clearly in frame.";

// The FCC Registration photo in a manual section (e.g. Tape Drop): a photo
// question whose label mentions FCC. The wizard OCRs the Antenna Structure
// Registration number off it (stored at `${key}_fccNumber`) so there's no
// manual number entry. Matches the backend generator's own FCC-photo heuristic.
function isFccRegPhoto(q: { type?: string; label?: string }): boolean {
  return q.type === "photo" && /fcc/i.test(q.label || "");
}
// Shown on a red FCC badge when the OCR couldn't read the registration number.
const FCC_REG_FAIL_REASON =
  "Couldn't read the FCC registration number — enter it manually below.";

// Daily JHA report slots (jha_day_1, jha_day_2, …). The rule engine validates
// each uploaded JHA PDF for the "Working at Heights" conflict.
const JHA_SLOT_RE = /^jha_day_\d+$/;
// Shown on a red JHA badge (and blocks submission) when the crew ticked BOTH
// the "no persons at heights" certification and the "Working at Heights"
// hazard on the same daily JHA — a contradiction that must be corrected.
const JHA_HEIGHT_CONFLICT_REASON =
  'This JHA both certifies "no persons working at heights" and flags "Working at Heights" as a hazard. Untick one — they can\'t both be selected.';

interface RrhSlot { fieldKey: string; port: "p1" | "p4"; n: number }
// 3 devices per RET position — so 6 on a normal two-position site, 3 on a
// single-position one. `positions` omitted = both ports (used where the caller
// only needs the key shape, e.g. the Delta cleanup list).
function rrhSlotsForSector(sectorKey: string, positions?: number[]): RrhSlot[] {
  const slots: RrhSlot[] = [];
  (["p1", "p4"] as const).forEach((port) => {
    if (positions && portNumber(port, positions) === undefined) return;
    for (let n = 1; n <= 3; n++) {
      slots.push({ fieldKey: `rrh_${sectorKey}_${port}_${n}`, port, n });
    }
  });
  return slots;
}
// Displayed label for an RRH photo slot, e.g. "P3 #2".
function rrhSlotLabel(slot: RrhSlot, positions: number[]): string {
  return `P${portNumber(slot.port, positions)} #${slot.n}`;
}

function rrhSectors(deltaAdded: boolean): RrhSector[] {
  return deltaAdded ? [...RRH_SECTORS, RRH_DELTA_SECTOR] : [...RRH_SECTORS];
}

interface ReportFileRef {
  name: string;
  type: string;
  blobName: string;
  uploadedAt: string;
  // For RET .txtrpt scans, the sibling human-readable PDF generated on upload.
  // Absent on non-txtrpt uploads and on scans uploaded before PDFs were stored
  // (those fall back to on-demand generation via /smrtcop/txtrpt-pdf).
  pdfBlobName?: string;
  pdfName?: string;
}

// Per-site, per-Verification-LINE config the admin sets through the Verify
// modal. Keyed by `verifyLineId(rowId, lineIndex)` — one boolean per
// atomic line. `true` means "show a single photo capture next to this line
// in the wizard". Lazy-stored: `false` is encoded as a missing key.
type VerifyOverridesMap = Record<string, boolean>;

// Slugify for use in stable verify-item ids. Lowercases and collapses any
// non-alphanumeric run into a single underscore so equivalent strings hash
// identically across reloads.
function verifySlug(s: string): string {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

// Cheap 32-bit content hash (DJB2-style), printed as 8-char hex. Same input
// → same output, no collisions in practice for a checklist of this size.
function verifyHash8(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

// Stable id for a single Verification row. Same content (label + sector +
// description) produces the same id across page loads, so admin overrides
// stay attached to the right row even if the spreadsheet's row order shifts.
function verifyItemId(sector: string, label: string, description: string): string {
  const normalizedDesc = (description || "").trim().replace(/\s+/g, " ");
  return `verify::${verifySlug(sector)}::${verifySlug(label)}::${verifyHash8(normalizedDesc)}`;
}

// Stable id for a single atomic line within a Verification row.
// Format: `${rowId}__line_${letter}` where letter is A, B, C, … so the
// admin's per-line photo overrides stay readable in the persisted state
// document and survive line-content edits within the row (only the row's
// outer hash + line position determine the id).
function verifyLineId(rowId: string, lineIndex: number): string {
  const letter = lineIndex < 26
    ? String.fromCharCode(65 + lineIndex)
    : `A${String.fromCharCode(65 + (lineIndex - 26))}`;
  return `${rowId}__line_${letter}`;
}

// Coerce whatever shape we read from site-state into the current model.
// Older docs stored `{ photoCount: number }` per row id; those get silently
// dropped here so the UI never tries to read `.photoCount` off a boolean.
// Anything that isn't strictly `true` is omitted (lazy storage).
function sanitizeVerifyOverrides(raw: unknown): VerifyOverridesMap {
  if (!raw || typeof raw !== "object") return {};
  const out: VerifyOverridesMap = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (v === true) out[k] = true;
  }
  return out;
}

// Compute the deduped Verification-step item list from the two arrays the
// /checklist endpoint returns. Same logic as buildPhotoSectionsFromChecklist's
// verification bucket — extracted so the Verify modal renders exactly the
// same item set the wizard will (no surprise rows appearing only at runtime).
function collectVerificationItems(
  items: ChecklistPhotoItem[],
  verificationItems: ChecklistPhotoItem[],
): ChecklistPhotoItem[] {
  const out: ChecklistPhotoItem[] = [];
  const seen = new Set<string>();
  const push = (item: ChecklistPhotoItem) => {
    const normalized = item.description
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
    const key = `${item.sector.trim().toLowerCase()}|${normalized}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(item);
  };
  // Photo-content rows in non-A/B/C/SPD sectors get re-routed to Verification.
  for (const it of items) {
    if (classifyChecklistSector(it.sector) === null) push(it);
  }
  // Native verification rows (no photo intent) — drop A/B/C/SPD ones (they
  // never carry photo content; they wouldn't survive sector filtering).
  for (const it of verificationItems) {
    if (classifyChecklistSector(it.sector) === null) push(it);
  }
  return out;
}

type PhotoSectorGroup = "A" | "B" | "C" | "SPD";

const PHOTO_SECTOR_ORDER: Array<{ key: PhotoSectorGroup; label: string }> = [
  { key: "A", label: "Sector A" },
  { key: "B", label: "Sector B" },
  { key: "C", label: "Sector C" },
  { key: "SPD", label: "SPD" },
];

// Sectors routed into the photo wizard. Alpha/Beta/Gamma → A/B/C. Anything
// containing "spd" (SPD 1, SPD 2, …) → SPD. Everything else returns null and
// flows into the Verification step.
function classifyChecklistSector(sector: string): PhotoSectorGroup | null {
  const s = (sector || "").toLowerCase();
  if (s.includes("alpha")) return "A";
  if (s.includes("beta")) return "B";
  if (s.includes("gamma")) return "C";
  if (s.includes("spd")) return "SPD";
  return null;
}

// ─── Checklist row → slot expansion pipeline ────────────────────────────────
// Three derivations run against each filtered/deduped row:
//   (a) multiplier — "each X" noun lookup → how many photos per instruction
//   (b) sub-measurement list — "A, B, C of each …" fan-out into N categories
//   (c) label cleaning — strip boilerplate prefixes/tails for display
// Nothing here is keyed to specific rows; the structure of the output is a
// function of what the descriptions themselves contain.

type PhotoMultiplierNoun = "antenna" | "radio" | "sector" | "spd";

function detectPhotoMultiplier(description: string): {
  count: number;
  noun: PhotoMultiplierNoun | null;
} {
  const m = description.match(
    /\beach\s+(antennas?|radios?|sectors?|spds?)\b/i,
  );
  if (!m) return { count: 1, noun: null };
  const word = m[1].toLowerCase();
  if (word.startsWith("antenna"))
    return { count: PHOTO_ANTENNAS_PER_SECTOR, noun: "antenna" };
  if (word.startsWith("radio"))
    return { count: PHOTO_RADIOS_PER_SECTOR, noun: "radio" };
  // "each sector" → 3-photo overview expansion. The row is already photo-
  // filtered upstream, so reaching this branch means the crew owes 3 photos
  // for the sector overview instruction.
  if (word.startsWith("sector"))
    return { count: PHOTO_SECTOR_OVERVIEW_COUNT, noun: "sector" };
  if (word.startsWith("spd")) return { count: PHOTO_SPD_COUNT, noun: "spd" };
  return { count: 1, noun: null };
}

// Matches "X, Y, Z of each <noun>" where X/Y/Z are short capitalized tokens.
// Example: "Photos showing the Az, MDT, Plumb of each antenna …" →
//   capture = "Az, MDT, Plumb" → tokens = ["Az", "MDT", "Plumb"].
function detectSubMeasurements(description: string): string[] {
  const m = description.match(
    /\b([A-Z][A-Za-z]*(?:\s*,\s*[A-Z][A-Za-z]*){1,})\s+of\s+each\b/,
  );
  if (!m) return [];
  return m[1]
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

// Produce a compact group-header label from a noisy checklist description.
// Pure regex strip sequence — no lookup tables.
function cleanLabelForDisplay(description: string): string {
  let s = (description || "").trim();
  // 1. Truncate at the first <br> / <br/> / newline — drops the explanatory
  //    tail that typically follows the instruction.
  s = s.split(/<br\s*\/?>/i)[0];
  s = s.split(/\r?\n/)[0];
  // 2. Strip "For each <noun> in scope, " opening.
  s = s.replace(
    /^for each (antennas?|radios?|spds?|sectors?)\s+in scope,?\s*/i,
    "",
  );
  // 3. Strip "Photos (of|showing) (the)? " prefix.
  s = s.replace(/^photos?\s+(of|showing)(\s+the)?\s+/i, "");
  // 4. Strip trailing explanatory clauses.
  s = s.replace(
    /\b(to show|within the scope of work|in its entirety)\b.*$/i,
    "",
  );
  return s.replace(/\s+/g, " ").trim();
}

function expandChecklistItem(
  baseKey: string,
  item: ChecklistPhotoItem,
): COPQuestion[] {
  const { count: multiplier, noun } = detectPhotoMultiplier(item.description);
  const subs = detectSubMeasurements(item.description);
  // Prefer the spreadsheet's Label column when present; fall back to the
  // description-derived label otherwise.
  const baseLabel =
    (item.label && item.label.trim()) ||
    cleanLabelForDisplay(item.description) ||
    item.description;

  // Slot labels derive from the multiplier noun ("Antenna 1", "Radio 2").
  // For multiplier=1 (no multi-slot expansion), the slot label collapses to
  // the cleaned description.
  const slotLabel = (i: number): string => {
    if (noun === "antenna") return `Antenna ${i}`;
    if (noun === "radio") return `Radio ${i}`;
    if (noun === "spd") return `SPD ${i}`;
    if (noun === "sector") return `Photo ${i}`;
    return baseLabel;
  };

  const safeKeySegment = (s: string): string =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

  // Every slot derived from this row carries the same full instruction
  // text. The renderer only paints it on the first slot of a group.
  const instruction = item.description;

  // Case A — inline sub-measurement list. Emit N sibling groups, each with
  // `multiplier` side-by-side slots (e.g. Az / MDT / Plumb, each with
  // Antenna 1 / Antenna 2).
  if (subs.length > 0) {
    const count = multiplier > 1 ? multiplier : 1;
    const result: COPQuestion[] = [];
    for (const sub of subs) {
      const subKey = `${baseKey}__${safeKeySegment(sub)}`;
      if (count <= 1) {
        result.push({
          key: subKey,
          label: sub,
          type: "photo",
          required: true,
          instruction,
        });
        continue;
      }
      for (let i = 1; i <= count; i++) {
        result.push({
          key: `${subKey}_${i}`,
          label: slotLabel(i),
          type: "photo",
          required: true,
          groupKey: subKey,
          groupLabel: sub,
          groupIndex: i - 1,
          groupTotal: count,
          instruction,
        });
      }
    }
    return result;
  }

  // Case B — single-slot row (no multiplier or multiplier=1).
  if (multiplier <= 1) {
    return [
      {
        key: baseKey,
        label: baseLabel,
        type: "photo",
        required: true,
        instruction,
      },
    ];
  }

  // Case C — multi-slot row under one group. E.g. "Photos of the Antenna
  // manufacturer label capturing the Make/Model" with `each antenna` →
  // 2 side-by-side slots under one header.
  return Array.from({ length: multiplier }, (_, i) => ({
    key: `${baseKey}_${i + 1}`,
    label: slotLabel(i + 1),
    type: "photo" as const,
    required: true,
    groupKey: baseKey,
    groupLabel: baseLabel,
    groupIndex: i,
    groupTotal: multiplier,
    instruction,
  }));
}

// Parse a non-sector checklist row into a parent title plus sub-instructions
// keyed by inline _A_/_B_/_C_… markers. Pure regex — no NLP.
//   "Verify DC cables…<br>_A_ Verify supported<br>_B_ Verify labeling"
//     → title: "Verify DC cables…"
//       subItems: ["Verify supported", "Verify labeling"]
// Falls back to { title: <whole description>, subItems: [] } when no markers
// are present, so any non-sector row degrades to a single-photo group with
// just the original instruction.
function parseGeneralGroup(description: string): {
  title: string;
  subItems: string[];
} {
  const text = (description || "").replace(/<br\s*\/?>/gi, "\n");
  const markerRe = /_([A-Z])_\s*([\s\S]*?)(?=_[A-Z]_|$)/g;
  const matches: Array<{ body: string; index: number }> = [];
  let m: RegExpExecArray | null;
  while ((m = markerRe.exec(text)) !== null) {
    matches.push({ body: m[2].trim(), index: m.index });
  }
  if (matches.length === 0) {
    return { title: text.trim(), subItems: [] };
  }
  const title = text.slice(0, matches[0].index).trim();
  const subItems = matches.map((x) => x.body).filter(Boolean);
  return { title, subItems };
}

// Display-only remap for short-form group labels that crews find hard to
// scan ("AZ" / "MDT"). The source template still carries the abbreviated
// tokens — we just spell them out at render time. Any label not in this
// map renders verbatim.
const EXPANDED_GROUP_LABELS: Record<string, string> = {
  AZ: "Azimuth",
  Az: "Azimuth",
  MDT: "Mechanical Down Tilt",
};

function expandGroupLabel(label: string): string {
  return EXPANDED_GROUP_LABELS[label.trim()] ?? label;
}

// Group consecutive questions that share a groupKey so the renderer can lay
// them out side-by-side under a single item header. Ungrouped questions each
// become a one-element group.
function groupPhotoQuestions(questions: COPQuestion[]): COPQuestion[][] {
  const groups: COPQuestion[][] = [];
  for (const q of questions) {
    const last = groups[groups.length - 1];
    if (last && q.groupKey && last[0].groupKey === q.groupKey) {
      last.push(q);
    } else {
      groups.push([q]);
    }
  }
  return groups;
}

// Group SPD-bucket rows by normalized description so a single instruction
// repeated across "SPD 1" / "SPD 2" / "SPD 3" / "SPD 4" rows renders as one
// multi-slot question (one slot per SPD, side-by-side) rather than several
// stacked photo cards. Falls back to a single-slot photo when only one SPD
// row carries the description. Non-SPD buckets are unaffected — they keep
// going through `expandChecklistItem`.
function expandSpdItems(
  items: ChecklistPhotoItem[],
  baseKey: string,
): COPQuestion[] {
  // Insertion order is preserved; we just collect items that share a
  // description and emit them as one group.
  const groupsByDesc = new Map<string, ChecklistPhotoItem[]>();
  for (const item of items) {
    const norm = item.description.trim().replace(/\s+/g, " ").toLowerCase();
    const list = groupsByDesc.get(norm);
    if (list) list.push(item);
    else groupsByDesc.set(norm, [item]);
  }

  const result: COPQuestion[] = [];
  let groupIdx = 0;
  for (const groupItems of groupsByDesc.values()) {
    // Stable SPD 1, 2, 3, 4 ordering when sectors are like "SPD <n>".
    groupItems.sort((a, b) => a.sector.localeCompare(b.sector));
    const first = groupItems[0];
    const groupKey = `${baseKey}_g${groupIdx++}`;
    const baseLabel =
      (first.label && first.label.trim()) ||
      cleanLabelForDisplay(first.description) ||
      first.description;

    if (groupItems.length === 1) {
      result.push({
        key: groupKey,
        label: baseLabel,
        type: "photo",
        required: true,
        instruction: first.description,
      });
      continue;
    }

    groupItems.forEach((item, i) => {
      result.push({
        key: `${groupKey}_${i + 1}`,
        // Slot label = the row's own sector ("SPD 1", "SPD 2", …) so the
        // crew can tell which physical SPD each photo belongs to.
        label: item.sector || `SPD ${i + 1}`,
        type: "photo",
        required: true,
        groupKey,
        groupLabel: baseLabel,
        groupIndex: i,
        groupTotal: groupItems.length,
        instruction: first.description,
      });
    });
  }
  return result;
}

// Compute the atomic lines for a single Verification row. The row's
// description is split by `_A_/_B_/_C_` markers (via parseGeneralGroup) when
// present; otherwise it's split by newline (parseGeneralGroup already
// converted <br>→\n upstream). Single-body rows degrade to a 1-line list so
// every row consistently emits at least one line.
function verifyAtomicLines(item: ChecklistPhotoItem): string[] {
  const { title, subItems } = parseGeneralGroup(item.description);
  const fallback = title
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (subItems.length > 0) return subItems;
  if (fallback.length > 0) return fallback;
  // Empty title (rare) — fall back to the row's display label so the line
  // never renders as blank.
  return [
    (item.label && item.label.trim()) ||
      cleanLabelForDisplay(item.description) ||
      item.description,
  ];
}

// Compute the row's display title for the Verification step (and for the
// Verify modal). Mirrors the title logic the previous heading-style render
// used so the on-screen label doesn't shift between views.
function verifyRowTitle(item: ChecklistPhotoItem): string {
  const { title } = parseGeneralGroup(item.description);
  return (
    (item.label && item.label.trim()) ||
    cleanLabelForDisplay(item.description) ||
    title ||
    item.description
  );
}

// Build the Verification COPQuestions for a single checklist row. One
// question per atomic line, all sharing the same groupKey so the
// Verification render path can collapse them into one card per row. The
// step is informational unless the admin has flagged a line via the Verify
// modal — in which case the question carries `verifyPhotoCount: 1` and
// the renderer paints a single inline photo alongside the line. The
// section-level `isSectionComplete` branch gates Continue only on those
// flagged photos; lines with no override don't block the wizard.
function buildVerificationLines(
  rowId: string,
  item: ChecklistPhotoItem,
  overrides: VerifyOverridesMap,
): COPQuestion[] {
  const lines = verifyAtomicLines(item);
  const groupLabel = verifyRowTitle(item);
  return lines.map((line, i) => {
    const lineId = verifyLineId(rowId, i);
    const photoFlagged = overrides[lineId] === true;
    return {
      key: lineId,
      label: line,
      type: "checkbox",
      required: false,
      groupKey: rowId,
      groupLabel,
      groupIndex: i,
      groupTotal: lines.length,
      verifyPhotoCount: photoFlagged ? 1 : undefined,
    };
  });
}

// Expand a template's photo section into sector-scoped sections plus a
// Verification step, based on rows extracted from the site's Checklist Excel.
// - Photo rows in Alpha/Beta/Gamma/SPD sectors → A/B/C/SPD photo steps.
// - Photo-content rows in any other sector (Compound, Tower, etc.) are
//   appended to the Verification bucket — they default to checkbox-only and
//   the admin can opt them into photo capture via the Verify modal.
// - Verification rows (everything that didn't match the photo regex at the
//   backend) → final Verification step. Each row's atomic lines become a
//   set of checkbox questions sharing the row's groupKey. Lines flagged in
//   `verifyOverrides` (boolean per line id) get a single inline photo slot.
// - Groups with zero items are skipped entirely (no empty step rendered).
// - If neither bucket has any rows, the photo section is dropped from the
//   wizard.
function buildPhotoSectionsFromChecklist(
  original: COPSection,
  items: ChecklistPhotoItem[],
  verificationItems: ChecklistPhotoItem[],
  verifyOverrides: VerifyOverridesMap,
): COPSection[] {
  if (items.length === 0 && verificationItems.length === 0) return [];

  const groups: Record<PhotoSectorGroup, ChecklistPhotoItem[]> = {
    A: [],
    B: [],
    C: [],
    SPD: [],
  };
  const seen: Record<PhotoSectorGroup, Set<string>> = {
    A: new Set(),
    B: new Set(),
    C: new Set(),
    SPD: new Set(),
  };
  // Photo-bucket rows whose sector doesn't classify into A/B/C/SPD are
  // re-routed into Verification so they remain configurable instead of
  // being silently dropped. Same dedupe rule as the verification bucket.
  const verification: ChecklistPhotoItem[] = [];
  const verificationSeen = new Set<string>();
  const pushVerification = (item: ChecklistPhotoItem) => {
    const normalized = item.description
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
    const key = `${item.sector.trim().toLowerCase()}|${normalized}`;
    if (verificationSeen.has(key)) return;
    verificationSeen.add(key);
    verification.push(item);
  };

  // Photo bucket — only Alpha/Beta/Gamma/SPD survive. Dedupe is by
  // description for A/B/C, but by sector+description for SPD so that
  // SPD 1's "Cable Penetrations" row and SPD 2's identically-worded row
  // are both kept.
  for (const item of items) {
    const bucket = classifyChecklistSector(item.sector);
    if (!bucket) {
      // Photo-content row in a non-A/B/C/SPD sector — keep it in Verification.
      pushVerification(item);
      continue;
    }
    const normalized = item.description
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
    const dedupeKey =
      bucket === "SPD"
        ? `${item.sector.trim().toLowerCase()}|${normalized}`
        : normalized;
    if (seen[bucket].has(dedupeKey)) continue;
    seen[bucket].add(dedupeKey);
    groups[bucket].push(item);
  }

  // Verification bucket — anything not routable to A/B/C/SPD.
  for (const item of verificationItems) {
    if (classifyChecklistSector(item.sector) !== null) continue;
    pushVerification(item);
  }

  const result: COPSection[] = [];
  PHOTO_SECTOR_ORDER.forEach((g, idx) => {
    const groupItems = groups[g.key];
    if (groupItems.length === 0) return;

    // SPD rows route through expandSpdItems so duplicate descriptions across
    // SPD 1..N collapse into one multi-slot question (slots labeled by the
    // row's own sector). A/B/C continue using the existing per-row pipeline
    // (multiplier + sub-measurement expansion).
    const questions: COPQuestion[] =
      g.key === "SPD"
        ? expandSpdItems(groupItems, `${original.id}_photo_${g.key}`)
        : groupItems.flatMap((item, i) =>
            expandChecklistItem(`${original.id}_photo_${g.key}_${i}`, item),
          );

    result.push({
      // Synthetic id derived from the original to stay stable across renders
      // but not collide with any other template section.
      id: original.id * 1000 + idx + 1,
      label: `${original.label} — ${g.label}`,
      type: "photo",
      questions,
      generated: true,
    });
  });

  // Verification — appended after all photo sectors. Each row's atomic
  // lines (split by _A_/_B_/_C_ markers, then by <br>/newline) become one
  // checkbox COPQuestion each, all sharing a per-row groupKey. The
  // Verification render path collapses them into one card per row. Lines
  // flagged in `verifyOverrides` (boolean per line id) carry
  // `verifyPhotoCount: 1` so the wizard paints an inline photo widget
  // beside that line, and isSectionComplete gates Continue on the photo's
  // presence in addition to the checkbox.
  if (verification.length > 0) {
    const verificationQuestions: COPQuestion[] = verification.flatMap((item) => {
      const rowId = verifyItemId(
        item.sector,
        item.label || "",
        item.description,
      );
      return buildVerificationLines(rowId, item, verifyOverrides);
    });
    result.push({
      id: original.id * 1000 + PHOTO_SECTOR_ORDER.length + 1,
      label: "Verification",
      type: "photo",
      questions: verificationQuestions,
      generated: true,
      kind: "verification",
    });
  }

  return result;
}

// RET and Alignment are two independently orderable template sections: a
// "reports" section (RET) and an "alignment" section. Older templates only
// carry a single "reports" section, so ensure an Alignment section exists
// right after it. This runs identically here and on the admin side, so the
// injected id (max(ids)+1) is deterministic — reveal/step ids line up whether
// or not the admin has re-saved the migrated template.
function ensureAlignmentSection(sections: COPSection[]): COPSection[] {
  if (sections.some((s) => s.type === "alignment")) return sections;
  const reportsIdx = sections.findIndex((s) => s.type === "reports");
  if (reportsIdx === -1) return sections;
  const nextId = sections.length
    ? Math.max(...sections.map((s) => s.id)) + 1
    : 1;
  const out = [...sections];
  out.splice(reportsIdx + 1, 0, {
    id: nextId,
    label: "Alignment Report",
    questions: [],
    type: "alignment",
  });
  return out;
}

// ─── v2 checklist → sector steps ────────────────────────────────────────────
// v2 rows are already one-per-category (30 per sector), so there's no NLP,
// SPD bucketing, or Verification step. Each included category becomes a photo
// group with two default slots plus the existing "+" add control.

const V2_SECTOR_STEPS: Array<{ code: "A" | "B" | "C"; label: string }> = [
  { code: "A", label: "Sector Alpha" },
  { code: "B", label: "Sector Beta" },
  { code: "C", label: "Sector Gamma" },
];

// A (categoryId, sector) is excluded when an explicit naOverride says so;
// otherwise it falls back to the row's own Excel status === "NA".
function isV2Excluded(
  item: ChecklistPhotoItem,
  naOverrides: NaOverridesMap,
): boolean {
  const key = naKey(item.categoryId || "", item.sector);
  if (key in naOverrides) return naOverrides[key];
  return (item.status || "").toUpperCase() === "NA";
}

function buildPhotoSectionsFromChecklistV2(
  original: COPSection,
  items: ChecklistPhotoItem[],
  naOverrides: NaOverridesMap,
): COPSection[] {
  if (items.length === 0) return [];
  const result: COPSection[] = [];
  V2_SECTOR_STEPS.forEach((sec, idx) => {
    const rows = items.filter(
      (it) => it.sector === sec.code && !isV2Excluded(it, naOverrides),
    );
    if (rows.length === 0) return;
    const questions: COPQuestion[] = [];
    rows.forEach((item) => {
      const groupKey = `${original.id}_v2_${sec.code}_${item.categoryId}`;
      const groupLabel = (item.label && item.label.trim()) || item.description;
      // Two default slots per category; the renderer's "+" appends extras.
      // Slot 1 is required (≥1 photo per included category); slot 2 is an
      // optional second capture so the crew isn't forced to duplicate.
      for (let i = 0; i < 2; i++) {
        questions.push({
          key: `${groupKey}_${i + 1}`,
          label: `Photo ${i + 1}`,
          type: "photo",
          required: i === 0,
          groupKey,
          groupLabel,
          groupIndex: i,
          groupTotal: 2,
          instruction: item.description,
          fixedLabel: true,
        });
      }
    });
    result.push({
      id: original.id * 1000 + idx + 1,
      label: `${original.label} — ${sec.label}`,
      type: "photo",
      questions,
      generated: true,
    });
  });
  return result;
}

// One row per category (deduped by categoryId, ordered by numeric key) for the
// "Verify Photo's" modal, plus a per-(categoryId, sector) Excel-status lookup so
// each A/B/C box can seed from the CM's status when no saved override exists.
interface V2Category {
  categoryId: string;
  key: string;
  label: string;
}
function v2Categories(items: ChecklistPhotoItem[]): {
  categories: V2Category[];
  statusByKey: Record<string, string>;
} {
  const categories: V2Category[] = [];
  const seen = new Set<string>();
  const statusByKey: Record<string, string> = {};
  for (const it of items) {
    const cid = it.categoryId || "";
    statusByKey[naKey(cid, it.sector)] = (it.status || "").toUpperCase();
    if (!seen.has(cid)) {
      seen.add(cid);
      categories.push({
        categoryId: cid,
        key: it.key || "",
        label: (it.label && it.label.trim()) || it.description,
      });
    }
  }
  categories.sort((a, b) => (Number(a.key) || 0) - (Number(b.key) || 0));
  return { categories, statusByKey };
}

function expandTemplateSections(
  templateSections: COPSection[],
  checklistItems: ChecklistPhotoItem[],
  verificationItems: ChecklistPhotoItem[],
  verifyOverrides: VerifyOverridesMap,
  checklistFormat: ChecklistFormat,
  naOverrides: NaOverridesMap,
): COPSection[] {
  return ensureAlignmentSection(templateSections).flatMap((section) => {
    if (section.type === "photo") {
      return checklistFormat === "v2"
        ? buildPhotoSectionsFromChecklistV2(section, checklistItems, naOverrides)
        : buildPhotoSectionsFromChecklist(
            section,
            checklistItems,
            verificationItems,
            verifyOverrides,
          );
    }
    // The "reports" template section becomes the RET wizard step (uploads +
    // RRH serial photos). The "alignment" section becomes the Alignment step.
    // Both keep runtime type "reports" so the shared reportKind-based render /
    // completion logic applies; the generated id maps back to the template
    // section (id = origId*1000 + 1) for admin step-reveal.
    if (section.type === "reports") {
      return [
        {
          ...section,
          id: section.id * 1000 + 1,
          label: "RET",
          type: "reports" as const,
          reportKind: "ret" as const,
          generated: true,
        },
      ];
    }
    if (section.type === "alignment") {
      return [
        {
          ...section,
          id: section.id * 1000 + 1,
          label: section.label || "Alignment Report",
          type: "reports" as const,
          reportKind: "alignment" as const,
          generated: true,
        },
      ];
    }
    return [section];
  });
}

// Photo slot expansion — some checklist items require multiple photos
// ("each antenna", "each radio", "each SPD"). A single COPQuestion is
// expanded into N synthetic COPQuestions so every slot has its own
// upload row, unique form key, and its own required check. Counts are
// static defaults for now; scope parsing happens in a later pass.
type PhotoItemType = "antenna" | "radio" | "spd" | "fixed";

const PHOTO_SECTORS = ["A", "B", "C"] as const;
const PHOTO_ANTENNAS_PER_SECTOR = 2;
const PHOTO_RADIOS_PER_SECTOR = 2;
const PHOTO_SPD_COUNT = 1;
const PHOTO_SECTOR_OVERVIEW_COUNT = 3;

function classifyPhotoItem(q: COPQuestion): PhotoItemType {
  const text = `${q.label ?? ""} ${q.helper ?? ""}`.toLowerCase();
  if (text.includes("each antenna")) return "antenna";
  if (text.includes("each radio")) return "radio";
  if (text.includes("each spd")) return "spd";
  return "fixed";
}

function expandPhotoQuestion(q: COPQuestion): COPQuestion[] {
  const type = classifyPhotoItem(q);
  if (type === "fixed") return [q];

  if (type === "spd") {
    if (PHOTO_SPD_COUNT <= 1) {
      return [{ ...q, label: `${q.label} — SPD` }];
    }
    return Array.from({ length: PHOTO_SPD_COUNT }, (_, i) => ({
      ...q,
      key: `${q.key}__spd_${i + 1}`,
      label: `${q.label} — SPD ${i + 1}`,
    }));
  }

  const perSector =
    type === "antenna" ? PHOTO_ANTENNAS_PER_SECTOR : PHOTO_RADIOS_PER_SECTOR;
  const itemLabel = type === "antenna" ? "Antenna" : "Radio";

  const slots: COPQuestion[] = [];
  for (const sector of PHOTO_SECTORS) {
    for (let i = 1; i <= perSector; i++) {
      slots.push({
        ...q,
        key: `${q.key}__sector_${sector}_${type}_${i}`,
        label: `${q.label} — Sector ${sector} – ${itemLabel} ${i}`,
      });
    }
  }
  return slots;
}

function expandPhotoQuestions(
  section: COPSection | undefined,
): COPQuestion[] {
  if (!section) return [];
  if (section.type !== "photo") return section.questions;
  // Generated (checklist-derived) sections already carry one question per
  // checklist row — no static antenna/radio/spd multiplication.
  if (section.generated) return section.questions;
  return section.questions.flatMap((q) =>
    q.type === "photo" ? expandPhotoQuestion(q) : [q],
  );
}

interface COPTemplate {
  id: string;
  name: string;
  version: number;
  status: "draft" | "active";
  sections: COPSection[];
  createdAt: string;
  updatedAt: string;
}

interface COPAzimuths { A: string; B: string; C: string; D?: string }

interface COPSourceFile {
  name: string;
  type: string;
  blobName?: string;
  slot?: "fixed" | "additional";
  docKey?: string;
  docLabel?: string;
}

interface COPSiteMeta {
  siteId: string;
  faLocationCode?: string;
  address: string;
  city?: string;
  county?: string;
  state?: string;
  zip?: string;
  coordinates?: string;
  // Legacy records may still have string azimuths; new records use the object form.
  azimuths: COPAzimuths | string;
  retPositions?: number[];
  // Model + linked cells per antenna column, from the RFDS "Antenna Position
  // Details - Sector X (Final)" pages. Decoded into the tape drop
  // Model/Technology options. Absent on sites ingested before this shipped —
  // those fall back to the template's static list.
  antennaPositionsBySector?: AntennaPositionsBySector;
  scopeOfWork?: string;
  sourceFiles: COPSourceFile[];
}

// Fixed-slot keys whose documents are surfaced to the crew (read-only view).
// DE 130 / DE 111 / BOMs and additional uploads are intentionally hidden.
const CREW_VISIBLE_DOC_KEYS = new Set([
  "construction_drawings",
  "mount_analysis",
  "rfds",
]);

// Fallback labels used when a source-file record pre-dates docLabel persistence.
const CREW_DOC_FALLBACK_LABELS: Record<string, string> = {
  construction_drawings: "Construction Drawings",
  mount_analysis: "Mount Analysis",
  rfds: "RFDS",
};

interface COPSite {
  id: string;
  name: string;
  clientId: string;
  templateId: string;
  assignedUserIds: string[];
  // The single crew lead (only assignee who may certify). Older sites predate
  // this field — fall back to assignedUserIds[0].
  crewLeadId?: string;
  // id → display name for assigned crew (stamped admin-side at assign time).
  crewNames?: Record<string, string>;
  // stepId (section.id as string) → assignee userId. Lead-managed per-step lock.
  stepAssignments?: Record<string, string>;
  status: string;
  createdAt: string;
  updatedAt: string;
  meta: COPSiteMeta;
  history?: SiteHistory;
}

// Derived progression status — mirrors the admin side so the crew sees the same
// tag (Verify Site Details → Assign Crew → Tower In Progress → … → pending
// client approval). Prefers the recorded history; falls back to inferring from
// the legacy status enum for sites with no history yet.
function progressStatusFor(site: COPSite): string {
  if (site.history && Object.keys(site.history).length > 0) {
    return deriveProgressStatus(site.history);
  }
  switch (site.status) {
    case "Approved":
      return "Client Approved";
    case "Complete":
    case "Submitted":
      return "Send Tape Drop";
    case "Active":
      return "Tower In Progress";
    case "Assign Crew":
      return "Assign Crew";
    default:
      return "Verify Site Details";
  }
}

// ─── EN ⇆ ES translation (wizard, user-side only) ─────────────────────────────
//
// Static dictionary handles UI chrome and common short technical labels —
// resolves instantly with no flicker. Anything else (admin-authored
// section/question/option text, descriptions) flows through a free public
// translate API and is memoised in-memory + localStorage so each unique
// phrase is fetched at most once per device.

type WizardLang = "en" | "es";

const ES_STATIC: Record<string, string> = {
  // Per-step crew assignment (multi-crew)
  "Assigned to": "Asignado a",
  Assign: "Asignar",
  "Assign Crew": "Asignar Cuadrilla",
  "Assign this step to a crew member.":
    "Asigna este paso a un miembro de la cuadrilla.",
  "Select crew member": "Seleccionar miembro de la cuadrilla",
  "Change assignment": "Cambiar asignación",
  Unassign: "Desasignar",
  Close: "Cerrar",
  Return: "Devolver",
  "Return this step to the crew lead":
    "Devolver este paso al líder de cuadrilla",
  "Return step?": "¿Devolver paso?",
  "This returns the step to the crew lead. You won't be able to edit it again unless it's reassigned to you.":
    "Esto devuelve el paso al líder de cuadrilla. No podrás editarlo de nuevo a menos que te lo reasignen.",
  "A photo is still uploading. Wait for it to finish before returning, or it may not be saved.":
    "Una foto aún se está subiendo. Espera a que termine antes de devolver, o podría no guardarse.",
  Unassigned: "Sin asignar",
  "Only the crew lead can certify this site":
    "Solo el líder de cuadrilla puede certificar este sitio",
  // Wizard chrome
  Exit: "Salir",
  Back: "Atrás",
  Skip: "Omitir",
  Continue: "Continuar",
  Submit: "Enviar",
  Cancel: "Cancelar",
  "Submit COP": "Enviar COP",
  "Are you sure you want to submit? You will not be able to edit after submission.":
    "¿Estás seguro de que deseas enviar? No podrás editar después de enviar.",
  "Loading BOM…": "Cargando BOM…",
  "Final BOM file not uploaded for this site":
    "Archivo Final BOM no subido para este sitio",
  "Failed to load Final BOM. Try again later.":
    "Error al cargar el BOM final. Inténtalo más tarde.",
  Item: "Artículo",
  Description: "Descripción",
  UOM: "UM",
  Qty: "Cant.",
  "Actual Installed": "Instalado Real",
  "Actual Installed Quantities Verified":
    "Cantidades Instaladas Reales Verificadas",
  "— Select —": "— Seleccionar —",
  "Tap to take photo": "Toca para tomar foto",
  "Tap to retake": "Toca para volver a tomar",
  "Certified By": "Certificado Por",
  "Timestamp: —": "Fecha y hora: —",
  "Submitted on": "Enviado el",
  "Last saved": "Guardado por última vez",
  Expand: "Expandir",
  Collapse: "Contraer",
  "Upload photos before marking them sent.":
    "Sube las fotos antes de marcarlas como enviadas.",
  "Download Photo": "Descargar foto",
  "Download failed": "Error al descargar",
  "Download Photos": "Descargar fotos",
  "Download all photos": "Descargar todas las fotos",
  "Change download folder": "Cambiar carpeta de descarga",
  "Download All Photos": "Descargar todas las fotos",
  "Downloading…": "Descargando…",
  "Save every photo into your chosen folder":
    "Guarda todas las fotos en la carpeta que elijas",
  "Saving photos to a folder needs Google Chrome or Microsoft Edge on desktop.":
    "Guardar fotos en una carpeta requiere Google Chrome o Microsoft Edge en el escritorio.",
  // Common short technical labels seen on COP templates.
  Az: "Az",
  Azimuth: "Azimut",
  MDT: "MDT",
  Plumb: "Plomada",
  Antenna: "Antena",
  Radio: "Radio",
  Sector: "Sector",
  Yes: "Sí",
  No: "No",
  // View Site Info screen.
  "Site ID": "ID del Sitio",
  "FA Location Code": "Código de Ubicación FA",
  "Physical Address": "Dirección Física",
  "Co-ordinates (Lat/Long)": "Coordenadas (Lat/Long)",
  Azimuths: "Azimuts",
  "View Scope of Work": "Ver Alcance del Trabajo",
  "View BOM": "Ver BOM",
  "View Verify": "Ver Verificación",
  "Scope of Work": "Alcance del Trabajo",
  "Final BOM": "BOM Final",
  "Verify Setup": "Configuración de Verificación",
  "Loading Verify items…": "Cargando ítems de verificación…",
  "No verification items found for this site":
    "No se encontraron ítems de verificación para este sitio",
  "Failed to load Verify setup. Try again later.":
    "No se pudo cargar la configuración de verificación. Inténtelo más tarde.",
  "Failed to save Verify setup. Try again.":
    "No se pudo guardar la configuración de verificación. Inténtelo de nuevo.",
  "Photo required for this item": "Foto requerida para este ítem",
  Photos: "Fotos",
  Alignment: "Alineación",
  // ── v2 checklist: sector steps + Verify Photo's modal ──
  "Sector Alpha": "Sector Alfa",
  "Sector Beta": "Sector Beta",
  "Sector Gamma": "Sector Gamma",
  "Verify Photo's": "Verificar Fotos",
  "No photo categories found for this site":
    "No se encontraron categorías de fotos para este sitio",
  "Tick the sectors (A / B / C) that need this photo. Untick to mark it N/A — it won't appear in the wizard.":
    "Marca los sectores (A / B / C) que necesitan esta foto. Desmarca para indicar N/A — no aparecerá en el asistente.",
  "Photo 1": "Foto 1",
  "Photo 2": "Foto 2",
  // v2 category labels (Checklist item names, sector-stripped)
  "Tower Top Sector Overview": "Vista General del Sector (Cima de Torre)",
  "SectorOverview from-the-Ground": "Vista General del Sector desde el Suelo",
  "CornerSpacing Verification": "Verificación de Separación de Esquinas",
  "AntennaSpacing Verification": "Verificación de Separación de Antenas",
  "Ancillary Equipment Clearance Validation":
    "Validación de Holgura de Equipos Auxiliares",
  "TowerTop Antenna Validation Azimuth": "Validación de Antena — Azimut",
  "TowerTop-AntennaValidation MDT": "Validación de Antena — MDT (Inclinación)",
  "TowerTop-AntennaValidation Plumb": "Validación de Antena — Plomada",
  "Tape Drop Antenna RAD": "Medición con Cinta — RAD de Antena",
  "TowerTop Mount Validation Azimuth": "Validación de Montaje — Azimut",
  "TowerTop-MountValidation MDT": "Validación de Montaje — MDT (Inclinación)",
  "TowerTop-MountValidation Plumb": "Validación de Montaje — Plomada",
  "Tape Drop Mount RAD": "Medición con Cinta — RAD de Montaje",
  "Tower Top Antenna Make-Model-Position":
    "Antena — Marca/Modelo/Posición",
  "Tower Top Radio Make-Model-Position": "Radio — Marca/Modelo/Posición",
  "Tower Top-SPD 1-Cable Penetrations": "SPD 1 — Penetraciones de Cable",
  "Tower Top-SPD 1-Interior Terminations": "SPD 1 — Terminaciones Interiores",
  "Tower Top-SPD 1-Lexan Cover and O-Ring Installed":
    "SPD 1 — Cubierta Lexan y Junta Tórica Instaladas",
  "Tower Top-SPD 1-Dome Installed": "SPD 1 — Domo Instalado",
  "Tower Top-SPD 1-SPD Grounded": "SPD 1 — SPD Conectado a Tierra",
  "Tower Top-SPD 2-Cable Penetrations": "SPD 2 — Penetraciones de Cable",
  "Tower Top-SPD 2-Interior Terminations": "SPD 2 — Terminaciones Interiores",
  "Tower Top-SPD 2-Lexan Cover and O-Ring Installed":
    "SPD 2 — Cubierta Lexan y Junta Tórica Instaladas",
  "Tower Top-SPD 2-Dome Installed": "SPD 2 — Domo Instalado",
  "Tower Top-SPD 2-SPD Grounded": "SPD 2 — SPD Conectado a Tierra",
  "Tower Top-SPD 3-Cable Penetrations": "SPD 3 — Penetraciones de Cable",
  "Tower Top-SPD 3-Interior Terminations": "SPD 3 — Terminaciones Interiores",
  "Tower Top-SPD 3-Lexan Cover and O-Ring Installed":
    "SPD 3 — Cubierta Lexan y Junta Tórica Instaladas",
  "Tower Top-SPD 3-Dome Installed": "SPD 3 — Domo Instalado",
  "Tower Top-SPD 3-SPD Grounded": "SPD 3 — SPD Conectado a Tierra",
  // v2 instruction descriptions (shown under the "?" info toggle)
  "Photos of each sector from behind the sector to show the final equipment locations.":
    "Fotos de cada sector desde atrás del sector para mostrar la ubicación final de los equipos.",
  "Photos showing sector overview from the ground":
    "Fotos que muestren la vista general del sector desde el suelo.",
  "Photo showing the corner spacing measurement between antennas, verifying the minimum required distance (24 inches) between sector components, measured from pipe to pipe as per installation guidelines.":
    "Foto que muestre la medición de separación de esquinas entre antenas, verificando la distancia mínima requerida (24 pulgadas) entre los componentes del sector, medida de tubo a tubo según las pautas de instalación.",
  "Photo showing the spacing between antennas, verifying the minimum required distance (24 inches) between antenna edges as per installation guidelines.":
    "Foto que muestre la separación entre antenas, verificando la distancia mínima requerida (24 pulgadas) entre los bordes de las antenas según las pautas de instalación.",
  'Verify through photos that ancillary equipment (RRU, SPD, Triplexers...) maintains a minimum 12" clearance from the horizontal mounting pipe and MLA/lease area boundaries. If the minimum separation is not met, RAN Approval will be required prior to acceptance.':
    'Verifique mediante fotos que los equipos auxiliares (RRU, SPD, Triplexores...) mantengan una holgura mínima de 12" respecto al tubo de montaje horizontal y a los límites del área MLA/arrendada. Si no se cumple la separación mínima, se requerirá la aprobación de RAN antes de la aceptación.',
  "Photos showing the Azimuth must be within 3.0 deg of each antenna within the scope of work. Verify AZ matches the RFDS/CD's. Verify that the AZ tool is mounted to the antenna correctly (Required for photo exception)":
    "Fotos que muestren que el Azimut esté dentro de 3.0 grados de cada antena dentro del alcance del trabajo. Verifique que el AZ coincida con el RFDS/CD. Verifique que la herramienta de AZ esté montada correctamente en la antena (Requerido para excepción de foto).",
  "Take clear photos of the tape measurement for all antennas within the Supplier scope of work, including Rad Center and Tip measurements. Ensure the inches side of the tape is visible in contact with the antenna, the nearest foot marking appears in the frame, and include a reference identifying the corresponding sector and antenna position. Any deviation greater than 6 inches requires RAN Approval prior to acceptance.":
    "Tome fotos claras de la medición con cinta de todas las antenas dentro del alcance del Proveedor, incluyendo las mediciones de Centro de Radiación (Rad Center) y Punta (Tip). Asegúrese de que el lado en pulgadas de la cinta esté visible en contacto con la antena, que aparezca la marca del pie más cercano en el encuadre, e incluya una referencia que identifique el sector y la posición de antena correspondientes. Cualquier desviación mayor a 6 pulgadas requiere la aprobación de RAN antes de la aceptación.",
  "For each antenna in scope, Photos of the Antenna manufacturer label capturing the Make_Model. (label of the Site ID/Sector/Position must be in the photo capture); AT&T Requirement":
    "Para cada antena dentro del alcance, fotos de la etiqueta del fabricante de la antena capturando la Marca_Modelo. (la etiqueta del ID del Sitio/Sector/Posición debe aparecer en la foto); Requisito de AT&T.",
  "For each radio in scope, Photos of the Radio manufacturer label capturing the Make_Model_Asset Tag. (label of the Site ID/Sector must be in the photo capture); AT&T Requirement":
    "Para cada radio dentro del alcance, fotos de la etiqueta del fabricante del radio capturando la Marca_Modelo_Etiqueta de Activo. (la etiqueta del ID del Sitio/Sector debe aparecer en la foto); Requisito de AT&T.",
  "For each SPD, Photos of all cable penetrations (inside and out showing no daylight)":
    "Para cada SPD, fotos de todas las penetraciones de cable (interior y exterior, sin dejar pasar luz).",
  "For each SPD, Photos of all terminations inside of SPD (Grounding, Fiber, & DC connections)":
    "Para cada SPD, fotos de todas las terminaciones dentro del SPD (conexiones de Tierra, Fibra y CC).",
  "Photo of the Lexan Cover and O-Ring installed prior to closing the unit":
    "Foto de la Cubierta Lexan y la Junta Tórica instaladas antes de cerrar la unidad.",
  "For each SPD, Photo with dome installed and secured.":
    "Para cada SPD, foto con el domo instalado y asegurado.",
  "For each SPD, Photo with ground connection":
    "Para cada SPD, foto con la conexión a tierra.",
  "(label of the Site ID/Sector must be in the photo capture)":
    "(la etiqueta del ID del Sitio/Sector debe aparecer en la foto)",
  // Upload-error popout + validation reasons (shown on hover and in the modal)
  "Resubmission requested": "Reenvío solicitado",
  "Serial number not detected — retake the RRH label photo.":
    "No se detectó el número de serie — vuelva a tomar la foto de la etiqueta del RRH.",
  "Azimuth not aligned — the centre indicator is red. Retake once it reads green.":
    "Azimut no alineado — el indicador central está en rojo. Vuelva a tomar la foto cuando esté en verde.",
  "Couldn't read the alignment screen — retake so the green centre indicator is clear.":
    "No se pudo leer la pantalla de alineación — vuelva a tomar la foto para que el indicador central verde se vea con claridad.",
  "This looks like the metric (yellow) tape — the client requires feet & inches. Retake using the feet-and-inches tape.":
    "Esta parece ser la cinta métrica (amarilla) — el cliente requiere pies y pulgadas. Vuelva a tomar la foto usando la cinta de pies y pulgadas.",
  "Couldn't identify the measuring tape — retake with the tape clearly in frame.":
    "No se pudo identificar la cinta métrica — vuelva a tomar la foto con la cinta claramente a la vista.",
  "Photo flagged": "Foto marcada",
  "The system has flagged a possible issue with this tape drop photo — it may be showing the metric tape instead of feet & inches.":
    "El sistema ha marcado un posible problema con esta foto de tape drop — podría estar mostrando la cinta métrica en lugar de pies y pulgadas.",
  "Retake the photo, or override the flag if the feet-and-inches tape is actually shown.":
    "Vuelva a tomar la foto, o anule la marca si en realidad se muestra la cinta de pies y pulgadas.",
  Retake: "Volver a tomar",
  Override: "Anular",
  "Couldn't read the FCC registration number — enter it manually below.":
    "No se pudo leer el número de registro FCC — ingréselo manualmente abajo.",
  'This JHA both certifies "no persons working at heights" and flags "Working at Heights" as a hazard. Untick one — they can\'t both be selected.':
    'Este JHA certifica que "no habrá personas trabajando en altura" y a la vez marca "Trabajo en Altura" como peligro. Desmarque una — no pueden estar ambas seleccionadas.',
  // Upload Reports section
  "Upload Reports": "Subir Reportes",
  "RET Report": "Informe RET",
  "Alignment Report": "Informe de Alineación",
  "Delta Sector": "Sector Delta",
  "Add Delta sector": "Agregar sector Delta",
  "Remove Delta sector": "Quitar sector Delta",
  "RRH Serial Numbers": "Números de serie del RRH",
  "Connected RRH Serial Number": "Número de serie del RRH conectado",
  "Photo": "Foto",
  "Pending…": "Pendiente…",
  "Scanning…": "Escaneando…",
  "FCC Registration #": "N.º de registro FCC",
  "Enter number": "Ingrese el número",
  "AT&T RET Worksheet": "Hoja de trabajo RET de AT&T",
  "Generate RET Form": "Generar formulario RET",
  "RET Sent": "RET Enviado",
  "Alignment Sent": "Alineación Enviada",
  "RET form generation is coming soon.": "La generación del formulario RET estará disponible pronto.",
  Upload: "Subir",
  Replace: "Reemplazar",
  "Failed to upload file. Try again.": "Error al subir el archivo. Inténtelo de nuevo.",
  "Failed to open file. Try again.": "Error al abrir el archivo. Inténtelo de nuevo.",
  Save: "Guardar",
  Sent: "Enviado",
  "Saving…": "Guardando…",
  "No scope of work available for this site.":
    "No hay alcance del trabajo disponible para este sitio.",
  "Preview not supported": "Vista previa no compatible",
  "Loading preview…": "Cargando vista previa…",
  "No documents": "Sin documentos",
};

const TRANSLATE_CACHE_KEY = "smrtcop-trans-es-v1";
const translateCache = new Map<string, string>();
const translatePending = new Set<string>();

(function hydrateTranslateCache() {
  try {
    const raw = localStorage.getItem(TRANSLATE_CACHE_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw);
    for (const [k, v] of Object.entries(obj)) {
      if (typeof v === "string") translateCache.set(k, v);
    }
  } catch {
    /* ignore — cache is best-effort */
  }
})();

function persistTranslateCache(): void {
  try {
    const obj: Record<string, string> = {};
    translateCache.forEach((v, k) => {
      obj[k] = v;
    });
    localStorage.setItem(TRANSLATE_CACHE_KEY, JSON.stringify(obj));
  } catch {
    /* localStorage may be unavailable (private mode) — fall back to memory */
  }
}

// Custom hook: returns a stable `t(text)` for the current language.
// Async API misses trigger a single bump() re-render once translation arrives.
function useT(lang: WizardLang): (text: string | null | undefined) => string {
  const [, bump] = useState(0);
  return useCallback(
    (text: string | null | undefined): string => {
      if (text == null) return "";
      if (lang === "en") return text;
      const trimmed = text.trim();
      if (!trimmed) return text;
      const stat = ES_STATIC[trimmed];
      if (stat !== undefined) return stat;
      const cached = translateCache.get(trimmed);
      if (cached !== undefined) return cached;
      if (!translatePending.has(trimmed)) {
        translatePending.add(trimmed);
        fetch(
          `https://api.mymemory.translated.net/get?q=${encodeURIComponent(trimmed)}&langpair=en|es`,
        )
          .then((r) => r.json())
          .then((d) => {
            const out = d?.responseData?.translatedText;
            if (typeof out === "string" && out.length > 0) {
              translateCache.set(trimmed, out);
              persistTranslateCache();
              bump((x) => x + 1);
            }
          })
          .catch(() => {
            /* leave the original English in place on failure */
          })
          .finally(() => translatePending.delete(trimmed));
      }
      return text;
    },
    [lang],
  );
}

// Render-time keyword highlighting for the scope of work display. Wraps just
// the matched action word — never the surrounding sentence — in a colored
// span: green for build actions (Prep / Prepare / Install), red for teardown
// actions (Remove / Decom / Decommission). Case-insensitive, whole-word, and
// purely cosmetic — stored data is never modified.
const SCOPE_GREEN_RE =
  /^(?:prep(?:are|ared|aring)?|install(?:ed|ing|ation|s)?)$/i;
const SCOPE_RED_RE =
  /^(?:remov(?:e|ed|es|ing|al)|decom(?:mission(?:ed|ing|s)?)?)$/i;
// One split pattern covering both groups; the capturing group keeps the
// matched words in the resulting array so they can be wrapped individually.
const SCOPE_SPLIT_RE =
  /(\b(?:prep(?:are|ared|aring)?|install(?:ed|ing|ation|s)?|remov(?:e|ed|es|ing|al)|decom(?:mission(?:ed|ing|s)?)?)\b)/gi;

function highlightScopeKeywords(text: string): React.ReactNode[] {
  const parts = text.split(SCOPE_SPLIT_RE);
  return parts.map((part, i) => {
    if (SCOPE_GREEN_RE.test(part)) {
      return (
        <span key={i} style={{ color: "#1faa59", fontWeight: 700 }}>
          {part}
        </span>
      );
    }
    if (SCOPE_RED_RE.test(part)) {
      return (
        <span key={i} style={{ color: "#c62828", fontWeight: 700 }}>
          {part}
        </span>
      );
    }
    return part;
  });
}

// ─── Component ───────────────────────────────────────────────────────────────

interface SMRTCOPProps {
  // ── Admin "Wizard Viewer" ──────────────────────────────────────────────────
  // When set, this renders ONE site's crew wizard READ-ONLY, exactly as the
  // site's crew lead sees it, so an admin can check what the crew actually did
  // ("you still owe photo B") without acting on their behalf.
  //
  // It reuses the real wizard rather than reproducing it: the admin has to see
  // the same screen the tech sees, and a second implementation would drift the
  // moment either changed. Absent = the normal crew experience, untouched.
  viewSiteId?: string;
  // Rendered as the viewer's close control (the crew's own Back/close is gone
  // in viewer mode, since there is no site list behind it).
  onCloseViewer?: () => void;
}

export default function SMRTCOP({
  viewSiteId,
  onCloseViewer,
}: SMRTCOPProps = {}) {
  const { user, roles } = useAuth();
  const userId = user?.userID;
  const isCopAdmin = !!roles?.permissions?.includes("cop.admin");
  // Viewer mode is admin-only and read-only. Gated here as well as at the call
  // site so mounting it can never become a write path for a non-admin.
  const viewerMode = !!viewSiteId && isCopAdmin;
  const readOnly = viewerMode;

  // Record a site progression step (timestamp + actor) — drives the admin
  // Status History. Fire-and-forget; failures are non-fatal.
  const recordHistory = (siteId: string, step: SiteHistoryStepKey) => {
    if (readOnly) return;
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/smrtcop/history/${encodeURIComponent(siteId)}`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ step, byName: user?.name || user?.email || "" }),
      },
    ).catch((err) => console.error("Failed to record history:", err));
  };

  const mobileMode =
    isMobileApp() ||
    window.navigator.userAgent.includes("iPhone") ||
    window.navigator.userAgent.includes("iPad");

  const [sites, setSites] = useState<COPSite[]>([]);
  const [templates, setTemplates] = useState<COPTemplate[]>([]);
  const [activeSite, setActiveSite] = useState<COPSite | null>(null);
  const [activeTemplate, setActiveTemplate] = useState<COPTemplate | null>(
    null,
  );
  const [formData, setFormData] = useState<Record<string, any>>({});
  const fileRefs = useRef<Record<string, { file: File; url: string }>>({});
  // Object-URL previews for photos that exist only as a blob reference in
  // formData (a reopened site carries `${key}_blob` = blobName, not the
  // bytes). Loaded lazily for the visible section only (see the effect
  // below) so we never hold all 50-70 site photos in memory at once. Kept in
  // a ref — not state — so the lazy-load effect doesn't depend on it; a tick
  // bump forces the re-render once a preview lands.
  const photoBlobPreviews = useRef<Record<string, string>>({});
  const photoPreviewLoading = useRef<Set<string>>(new Set());
  const [, setPhotoPreviewTick] = useState(0);
  const resetPhotoBlobPreviews = () => {
    Object.values(photoBlobPreviews.current).forEach((u) =>
      URL.revokeObjectURL(u),
    );
    photoBlobPreviews.current = {};
    photoPreviewLoading.current.clear();
  };
  // Per-slot upload status from the durable photo queue (keyed by fieldKey).
  // A non-empty map means uploads are still in flight for this site, which
  // gates submission (locked decision: block submit until all uploaded).
  const [photoUploadStatus, setPhotoUploadStatus] = useState<
    Record<string, PhotoSlotStatus>
  >({});
  // Open state for the per-step "Assign Crew" modal (targets the current step).
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  // Section pending a "Return this step to the crew lead" confirmation.
  const [returnConfirm, setReturnConfirm] = useState<COPSection | null>(null);
  // Tape Drop photo the metric/imperial rule flagged, awaiting the crew's
  // Retake-or-Override decision. Tape drop is a soft warning (not a hard reject)
  // because the decimal-dot marker isn't legible on every valid shot (e.g. a
  // zoomed-out full-antenna frame), so the crew can override and move on.
  const [tapeWarn, setTapeWarn] = useState<{ key: string } | null>(null);
  // id→name for the active site's crew, resolved from the backend (covers sites
  // assigned before crewNames was stamped, and members without a stored name).
  const [crewNamesResolved, setCrewNamesResolved] = useState<
    Record<string, string>
  >({});
  useEffect(() => {
    if (!activeSite?.id) {
      setCrewNamesResolved({});
      return;
    }
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/smrtcop/crew-names/${encodeURIComponent(activeSite.id)}`,
      { credentials: "include" },
    )
      .then((r) => r.json())
      .then((d) => setCrewNamesResolved(d.crewNames || {}))
      .catch(() => {});
  }, [activeSite?.id]);
  // Live mirror of formData + a serialized PUT chain, so the queue's async
  // upload completions persist the blob reference against the latest answers
  // without racing each other or the autosave.
  const formDataRef = useRef<Record<string, any>>({});
  const persistChainRef = useRef<Promise<void>>(Promise.resolve());

  // ── Concurrency-safe answer saving ──────────────────────────────────────────
  // The site-state doc is shared by every assigned crew member. Sending the
  // whole answers map (last-write-wins) would let one crew clobber another's
  // work, so we send only what THIS client changed since its last successful
  // save, and the backend merges it. `baselineAnswersRef` is our best-effort
  // picture of what the server already has from us; the delta is diffed against
  // it. Keys another crew added never enter our baseline, so we never delete
  // them. See PUT /smrtcop/site-state (answersPatch/answersRemove).
  const baselineAnswersRef = useRef<Record<string, any>>({});
  // Step-completion trackers are arrays that multiple crew append to; they must
  // be add-unioned server-side (never overwritten), while a reject-driven
  // un-save still needs to remove a single id — handled explicitly below.
  const UNION_ANSWER_KEYS = ["__completedSteps", "__savedStepIds"];

  const eqValue = (a: any, b: any) =>
    JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

  // Diff current answers against the synced baseline.
  const buildAnswersDelta = () => {
    const S = formDataRef.current || {};
    const B = baselineAnswersRef.current || {};
    const patch: Record<string, any> = {};
    const unionRemove: Record<string, any[]> = {};

    for (const k of Object.keys(S)) {
      if (UNION_ANSWER_KEYS.includes(k)) continue;
      if (!eqValue(S[k], B[k])) patch[k] = S[k];
    }
    // We deliberately DO NOT infer key removals from (baseline − formData). A
    // momentarily stale/empty formDataRef — e.g. a flush that runs just after a
    // setFormData({}) reset, or chained behind an in-flight flush — would
    // otherwise compute "remove everything" and WIPE another crew member's saved
    // work (this is exactly how a returned assignee's photo was being deleted).
    // Explicit clears set the value to `null` instead, which rides through
    // `patch` safely and never deletes unrelated keys.
    //
    // The union keys still need diff-driven removal (a reject-un-saving one
    // step), but they were exposed to the SAME staleness: a flush landing after
    // the wizard closed sees S = {} while the baseline still lists every saved
    // step, and computes "remove every step" — which deleted __savedStepIds /
    // __completedSteps server-side, so each step went green on Save and came
    // back orange after logging out and in. Removals are therefore only
    // inferred from a form that is actually seeded.
    const formSeeded = Object.keys(S).length > 0;
    for (const uk of UNION_ANSWER_KEYS) {
      const sArr = Array.isArray(S[uk]) ? S[uk] : [];
      const bArr = Array.isArray(B[uk]) ? B[uk] : [];
      const added = sArr.filter((x: any) => !bArr.includes(x));
      const removed = formSeeded
        ? bArr.filter((x: any) => !sArr.includes(x))
        : [];
      if (added.length) patch[uk] = added;
      if (removed.length) unionRemove[uk] = removed;
    }
    return { patch, unionRemove, snapshot: { ...S } };
  };

  // Flush pending answer changes for a site, serialized behind persistChainRef
  // so per-photo saves and the debounced autosave can't race each other.
  const flushAnswers = (siteId: string): Promise<void> => {
    // THE write guard for viewer mode. Every answers write funnels through
    // here — the debounced autosave, per-photo persists, close-on-exit — so
    // blocking it here is what makes the viewer genuinely read-only. Effects
    // that mutate formData on load (saved-step reconciliation, OCR sidecars)
    // stay local and never reach the server. Guarding the buttons instead
    // would have left those effects writing.
    if (readOnly) return persistChainRef.current;
    // Snapshot the delta NOW, synchronously, against the form as it stands at
    // the moment the flush is requested. Computing it inside the chain below
    // meant a flush queued behind an in-flight save read formDataRef only after
    // it resolved — by which time closeWizard's setFormData({}) had reset it, so
    // the crew's last edits were diffed away instead of sent.
    const { patch, unionRemove, snapshot } = buildAnswersDelta();
    if (
      Object.keys(patch).length === 0 &&
      Object.keys(unionRemove).length === 0
    )
      return persistChainRef.current;
    persistChainRef.current = persistChainRef.current
      .catch(() => undefined)
      .then(async () => {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/smrtcop/site-state/${encodeURIComponent(siteId)}`,
          {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              answersPatch: patch,
              answersUnionRemove: unionRemove,
            }),
          },
        );
        // Advance the baseline to what we just reconciled only on success, so a
        // failed save is retried (nothing is lost) on the next flush.
        if (res.ok) baselineAnswersRef.current = snapshot;
      });
    return persistChainRef.current;
  };

  // Push our own pending edits, then read back the server's MERGED answers (all
  // crew's work). The crew lead certifies this — not their own screen, which may
  // be missing another crew member's just-uploaded photos. Falls back to local.
  const fetchMergedAnswers = async (
    siteId: string,
  ): Promise<Record<string, any>> => {
    await flushAnswers(siteId);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/smrtcop/site-state/${encodeURIComponent(siteId)}`,
        { credentials: "include" },
      );
      const data = await res.json();
      return data.answers || {};
    } catch {
      return { ...formDataRef.current };
    }
  };
  // Per-row hidden input refs, keyed by question key. Used by the photo
  // row's camera / upload buttons to trigger the underlying <input>
  // without wrapping it in a <label> (which can't be disabled).
  const camInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const upInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [viewingSite, setViewingSite] = useState<COPSite | null>(null);
  const [viewDocIndex, setViewDocIndex] = useState(0);
  const [viewPreviewUrl, setViewPreviewUrl] = useState<string | null>(null);
  const [reviewSite, setReviewSite] = useState<COPSite | null>(null);
  const [reviewTemplate, setReviewTemplate] = useState<COPTemplate | null>(null);
  const [reviewData, setReviewData] = useState<Record<string, any>>({});
  // Assigned site ids for which the crew has saved ≥1 wizard step (draft in
  // progress). Drives showing BOTH the green "Start/Continue COP" and the
  // yellow "View Submission" buttons on the main list before final submit.
  const [startedSiteIds, setStartedSiteIds] = useState<Set<string>>(new Set());
  // Section ids collapsed to just their header on the View Submission screen.
  const [reviewCollapsed, setReviewCollapsed] = useState<Set<number>>(new Set());
  const toggleReviewSection = (id: number) =>
    setReviewCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // Portal-upload confirmations ({ photos?: {at,by}, bom?: {at,by} }) — set
  // once the crew has uploaded those artifacts to the Ericsson portal.
  const [reviewPortal, setReviewPortal] = useState<Record<string, any>>({});
  // Per-photo clipboard-copy status (keyed by photo key) — drives the
  // Copy/Copied/Failed label on the RET TXTRPT copy-to-clipboard buttons.
  const [copyStatus, setCopyStatus] = useState<Record<string, "copying" | "done" | "error">>({});
  // ── Photo download-to-folder (Ericsson portal blocks clipboard paste) ──────
  // Per-photo transient write status ("saving" | "error"); a persisted ✓ comes
  // from `reviewDownloaded` instead so it survives a reload.
  const [downloadStatus, setDownloadStatus] = useState<Record<string, "saving" | "error">>({});
  // Field-keys of photos already written to the crew's folder for the site
  // being reviewed. Loaded from site-state on openReview, updated per download.
  const [reviewDownloaded, setReviewDownloaded] = useState<Set<string>>(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);
  // Session-scoped chosen folder, kept per site: re-prompt when the site changes
  // or the folder's read-write permission is lost. Not persisted (handles can't
  // be, and the spec wants a fresh pick per session/site).
  const dirHandleRef = useRef<{ siteId: string; handle: any } | null>(null);
  // Site whose Status History modal is open (null = closed).
  const [historySite, setHistorySite] = useState<COPSite | null>(null);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  // Reason shown in the upload-error popout (tapping a red badge). Already
  // translated when set, so the modal renders it verbatim.
  const [errorModal, setErrorModal] = useState<string | null>(null);

  // Wizard language toggle (user-side, render-only — no backend impact).
  const [wizardLang, setWizardLang] = useState<WizardLang>("en");
  const t = useT(wizardLang);

  // Per-question instruction-block visibility. Crews see only the label by
  // default; tapping the "?" beside the label expands the full instruction.
  // Keyed by groupKey when the question is one of several slots in a group
  // (so all slots share one open/closed state), otherwise by question key.
  const [openHelp, setOpenHelp] = useState<Set<string>>(new Set());
  const toggleHelp = useCallback((key: string) => {
    setOpenHelp((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  // Per-row overflow menu (the ⋮ on the right of each photo row). At
  // most one is open at a time. Mirrors SMRT Tasks' openMenuId pattern.
  const [openPhotoMenuKey, setOpenPhotoMenuKey] = useState<string | null>(null);
  const togglePhotoMenu = useCallback((key: string) => {
    setOpenPhotoMenuKey((prev) => (prev === key ? null : key));
  }, []);
  // Click-outside closes the open menu. Bound only while one is open so
  // we don't run a global listener for free.
  useEffect(() => {
    if (!openPhotoMenuKey) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || !target.closest(".smrtcop-photo-row-actions")) {
        setOpenPhotoMenuKey(null);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [openPhotoMenuKey]);
  // Rename modal — open on a specific row's question key, with a working
  // input value the user types into. null = closed.
  const [renamingKey, setRenamingKey] = useState<string | null>(null);
  const [renamingValue, setRenamingValue] = useState("");
  // In-app photo lightbox. iOS Safari/WKWebView silently ignores
  // window.open() of a blob: URL, so "View" renders the image in this modal
  // instead of a new tab — works on both mobile and desktop.
  const [photoLightboxUrl, setPhotoLightboxUrl] = useState<string | null>(null);
  const [showScopeModal, setShowScopeModal] = useState(false);

  // Verify Setup modal — admin-side editor for per-Verification-LINE photo
  // overrides. Loaded lazily on first View Verify click for the currently-
  // viewing site; the draft is what the line checkboxes mutate, the
  // committed map is what's already in Cosmos. On Save we PUT just
  // verifyOverrides; the backend leaves answers/submitted untouched.
  type VerifyModalStatus = "idle" | "loading" | "loaded" | "missing" | "error";
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyModalStatus, setVerifyModalStatus] = useState<VerifyModalStatus>("idle");
  const [verifyModalItems, setVerifyModalItems] = useState<ChecklistPhotoItem[]>([]);
  const [verifyModalDraft, setVerifyModalDraft] = useState<VerifyOverridesMap>({});
  const [verifyModalSaving, setVerifyModalSaving] = useState(false);
  // Which checklist format the currently-open modal is showing. v2 renders the
  // "Verify Photo's" A/B/C exclusion grid; legacy keeps the verification-line
  // photo editor.
  const [verifyModalFormat, setVerifyModalFormat] = useState<ChecklistFormat>("legacy");
  // v2 modal working state: the deduped category rows, the per-(categoryId,
  // sector) Excel-status seed, and the excluded-map draft the A/B/C boxes edit.
  const [naModalCategories, setNaModalCategories] = useState<V2Category[]>([]);
  const [naModalDraft, setNaModalDraft] = useState<NaOverridesMap>({});
  // Photo-step blocker list: collapsed by default so the summary line never
  // buries the step it is describing.
  const [blockersOpen, setBlockersOpen] = useState(false);

  // BOM section state — one row per line in the site's Final BOM file.
  // Completion is gated by a single "Actual Installed Quantities Verified"
  // checkbox persisted into formData (keyed by section id), NOT by per-row
  // validation. Crew can freely edit Actual Installed values.
  interface BomRow {
    item: string;
    description: string;
    uom: string;
    plannedQty: string;
    actualInstalled: string;
  }
  type BomStatus = "idle" | "loading" | "loaded" | "missing" | "error";
  const [bomRows, setBomRows] = useState<BomRow[]>([]);
  const [bomStatus, setBomStatus] = useState<BomStatus>("idle");

  // Read-only BOM viewer reachable from the user-side site detail panel
  // (next to the View Scope of Work button). Kept independent of the
  // wizard's bomRows state so opening one cannot mutate the other.
  const [showBomModal, setShowBomModal] = useState(false);
  const [bomViewRows, setBomViewRows] = useState<BomRow[]>([]);
  const [bomViewStatus, setBomViewStatus] = useState<BomStatus>("idle");

  // Checklist state — drives the Photo section's dynamic sector steps.
  // Populated once per wizard-open via GET /smrtcop/checklist/:siteId.
  const [checklistItems, setChecklistItems] = useState<ChecklistPhotoItem[]>([]);
  const [verificationItems, setVerificationItems] = useState<ChecklistPhotoItem[]>([]);

  // Per-site Verify config — boolean per Verification line id (see
  // verifyLineId). Loaded alongside answers/submitted on Site Details
  // mount, written through the Verify modal save. Backend respects per-
  // field undefined on PUT, so concurrent answer saves don't clobber it.
  const [verifyOverrides, setVerifyOverrides] = useState<VerifyOverridesMap>({});

  // Which checklist format the active site uses, and — for v2 — the per-site
  // N/A exclusions. Both feed runtimeSections. `naOverrides` is loaded from
  // site-state on wizard open and written through the Verify Photo's modal.
  const [checklistFormat, setChecklistFormat] = useState<ChecklistFormat>("legacy");
  const [naOverrides, setNaOverrides] = useState<NaOverridesMap>({});

  // Runtime wizard sections — same as the template's sections, but each
  // photo section is replaced with its checklist-derived sector groups
  // (A / B / C / SPD) plus a Verification step. Every consumer (BOM loader,
  // current-step render, completion checks) MUST read from this, not from
  // activeTemplate.sections, so step indices stay consistent after photo
  // expansion.
  const runtimeSections = useMemo<COPSection[]>(
    () =>
      activeTemplate
        ? expandTemplateSections(
            activeTemplate.sections,
            checklistItems,
            verificationItems,
            verifyOverrides,
            checklistFormat,
            naOverrides,
          )
        : [],
    [
      activeTemplate,
      checklistItems,
      verificationItems,
      verifyOverrides,
      checklistFormat,
      naOverrides,
    ],
  );

  // Which sector each question sits under. The tape drop step is a FLAT
  // question list broken up by "Sector A/B/C" headings (plus a "Sector D"
  // Yes/N-A select that gates its own rows), so a question's sector is
  // whichever sector marker most recently preceded it. Mirrors the walk the
  // tape drop builder does server-side (tapeDropBuilder.ts) so the form and
  // the generated workbook agree on which antenna belongs to which sector.
  // Question keys are unique across the template, so one flat map is enough.
  const sectorByQuestionKey = useMemo(() => {
    const map: Record<string, string> = {};
    for (const section of runtimeSections) {
      let sector: string | null = null;
      for (const q of section.questions || []) {
        const m = /^sector ([a-d])$/.exec((q.label || "").trim().toLowerCase());
        if (m) {
          sector = m[1].toUpperCase();
          continue;
        }
        if (sector) map[q.key] = sector;
      }
    }
    return map;
  }, [runtimeSections]);

  // ── Load from backend (with polling) ────────────────────────────────────────
  const lastSitesHash = useRef("");
  useEffect(() => {
    const fetchData = () => {
      Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtcop/sites`, { credentials: "include" }).then(r => r.json()),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtcop/templates`, { credentials: "include" }).then(r => r.json()),
      ]).then(([sitesData, tplData]) => {
        const newSites = sitesData.sites || [];
        const hash = JSON.stringify(newSites);
        if (hash !== lastSitesHash.current) {
          setSites(newSites);
          lastSitesHash.current = hash;
        }
        setTemplates(tplData.templates || []);
      }).catch(err => console.error("Failed to load SMRT COP data:", err));
    };
    fetchData();
    let interval = setInterval(fetchData, 12000);
    const onVisibility = () => {
      clearInterval(interval);
      if (document.visibilityState === "visible") {
        fetchData();
        interval = setInterval(fetchData, 12000);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => { clearInterval(interval); document.removeEventListener("visibilitychange", onVisibility); };
  }, []);

  // Reset the user-side BOM viewer when switching between sites so opening
  // the modal on a new site can't show the previous site's rows.
  useEffect(() => {
    setShowBomModal(false);
    setBomViewRows([]);
    setBomViewStatus("idle");
  }, [viewingSite?.id]);

  // Same reset for the Verify modal — switching sites must drop any draft
  // and item list from the previous site so the modal opens fresh.
  useEffect(() => {
    setShowVerifyModal(false);
    setVerifyModalItems([]);
    setVerifyModalDraft({});
    setVerifyModalStatus("idle");
    setVerifyModalSaving(false);
  }, [viewingSite?.id]);

  // Load preview from backend blob when viewing site doc changes.
  // The document list is filtered to the 3 crew-visible fixed slots; indices
  // here are into that filtered list.
  useEffect(() => {
    setViewPreviewUrl(null);
    if (!viewingSite) return;
    const docs = (viewingSite.meta.sourceFiles || []).filter(
      (f) => f.slot === "fixed" && f.docKey && CREW_VISIBLE_DOC_KEYS.has(f.docKey),
    );
    const doc = docs[viewDocIndex];
    if (!doc || !doc.blobName) return;
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/smrtcop/files/${encodeURIComponent(viewingSite.id)}/${encodeURIComponent(doc.name)}?blobName=${encodeURIComponent(doc.blobName)}&format=base64`,
      { credentials: "include" }
    )
      .then(r => r.json())
      .then(({ base64, contentType }) => {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: contentType });
        const url = URL.createObjectURL(blob);
        setViewPreviewUrl(url);
      })
      .catch(err => console.error("Failed to load file preview:", err));
  }, [viewingSite, viewDocIndex]);

  // ── Assigned sites only ────────────────────────────────────────────────────
  // Viewer mode addresses exactly one site — the crew's assignment filter would
  // hide it, since an admin is not on the crew.
  const mySites = viewerMode
    ? sites.filter((s) => s.id === viewSiteId)
    : sites.filter((s) => userId && s.assignedUserIds.includes(userId));

  // A step-by-step wizard means the crew can save individual steps before the
  // whole COP is submitted. For each in-progress (non-Complete) assigned site,
  // check its site-state for any saved/completed step so the list can offer the
  // yellow "View Submission" button alongside the green start button. Re-runs
  // when the assigned set changes or when the wizard closes (a step may have
  // just been saved). `Complete` sites always show View Submission regardless.
  const inProgressKey = mySites
    .filter((s) => s.status !== "Complete")
    .map((s) => s.id)
    .join(",");
  useEffect(() => {
    const targets = mySites.filter((s) => s.status !== "Complete");
    if (activeSite || targets.length === 0) {
      if (targets.length === 0) setStartedSiteIds(new Set());
      return;
    }
    let cancelled = false;
    Promise.all(
      targets.map((s) =>
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/smrtcop/site-state/${encodeURIComponent(s.id)}`,
          { credentials: "include" },
        )
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => {
            const a = (data && data.answers) || {};
            const saved = Array.isArray(a.__savedStepIds) ? a.__savedStepIds : [];
            const done = Array.isArray(a.__completedSteps)
              ? a.__completedSteps
              : [];
            return saved.length > 0 || done.length > 0 ? s.id : null;
          })
          .catch(() => null),
      ),
    ).then((ids) => {
      if (cancelled) return;
      setStartedSiteIds(new Set(ids.filter((x): x is string => !!x)));
    });
    return () => {
      cancelled = true;
    };
  }, [inProgressKey, activeSite]);

  // ── Open / close wizard ──────────────────────────────────────────────────────
  const openWizard = (site: COPSite) => {
    // Submitted sites are closed to the wizard — including the viewer. Submit
    // clears `answers` (the map the wizard renders) into `submitted`, so a
    // Complete site would open as a blank wizard. The viewer surfaces this as a
    // message instead; see the viewerMode branch near the bottom of the file.
    if (site.status === "Complete") {
      if (!readOnly) alert("This site has already been submitted.");
      return;
    }
    const tpl = templates.find((t) => t.id === site.templateId);
    if (!tpl) {
      alert("No template assigned to this site.");
      return;
    }
    // Clear file refs from previous site
    Object.values(fileRefs.current).forEach(entry => URL.revokeObjectURL(entry.url));
    fileRefs.current = {};
    resetPhotoBlobPreviews();
    setActiveSite(site);
    setActiveTemplate(JSON.parse(JSON.stringify(tpl)));
    setPhotoUploadStatus({});
    const queueHandlers = {
      onUploaded: onPhotoUploaded,
      onStatusChange: setPhotoUploadStatus,
    };
    // Load draft answers + verify overrides from backend
    fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtcop/site-state/${encodeURIComponent(site.id)}`, { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        const answers = data.answers || {};
        setFormData(answers);
        formDataRef.current = answers;
        // Seed the save baseline with what the server already holds, so the
        // first delta only carries this session's own edits.
        baselineAnswersRef.current = { ...answers };
        setVerifyOverrides(sanitizeVerifyOverrides(data.verifyOverrides));
        setNaOverrides(sanitizeNaOverrides(data.naOverrides));
        // Resume any photo uploads parked from a prior session. Slots whose
        // reference is already saved are skipped (not re-uploaded).
        const savedKeys = new Set(
          Object.keys(answers)
            .filter((k) => k.endsWith("_blob"))
            .map((k) => k.slice(0, -"_blob".length)),
        );
        // Never in viewer mode: activating the queue would RESUME upload work
        // parked by a crew member's device, so an admin merely looking at a
        // site could complete uploads on their behalf.
        if (!readOnly)
          void copPhotoQueue.setActiveSite(site.id, queueHandlers, savedKeys);
      })
      .catch(() => {
        setFormData({});
        formDataRef.current = {};
        baselineAnswersRef.current = {};
        setVerifyOverrides({});
        setNaOverrides({});
        if (!readOnly) void copPhotoQueue.setActiveSite(site.id, queueHandlers);
      });
    setCurrentStepIndex(0);
  };

  // Viewer mode opens straight into the site's wizard — there is no site list
  // to pick from. Waits for both sites and templates, since openWizard needs
  // the template to build the steps.
  const viewerOpenedRef = useRef(false);
  useEffect(() => {
    if (!viewerMode || viewerOpenedRef.current) return;
    if (sites.length === 0 || templates.length === 0) return;
    const site = sites.find((s) => s.id === viewSiteId);
    if (!site) return;
    viewerOpenedRef.current = true;
    openWizard(site);
    // openWizard is recreated each render; the ref guard is what keeps this to
    // a single run rather than the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerMode, viewSiteId, sites, templates]);

  const closeWizard = () => {
    // Cancel the debounced auto-save before flushing. Left armed, it fired up to
    // 2s AFTER the wizard closed — reading a formDataRef that setFormData({})
    // had already emptied — and the resulting delta told the server to drop
    // every saved/completed step. The explicit flush below covers the same
    // pending edits, synchronously and against the real form.
    if (answersSaveTimer.current) {
      clearTimeout(answersSaveTimer.current);
      answersSaveTimer.current = null;
    }
    // Persist on close so user can resume. Flush only this session's own
    // changes (delta) — a full-map write here would clobber another crew
    // member's concurrent edits to the same site.
    if (activeSite) {
      flushAnswers(activeSite.id).catch((err) =>
        console.error("Failed to save draft:", err),
      );
    }
    Object.values(fileRefs.current).forEach(entry => URL.revokeObjectURL(entry.url));
    fileRefs.current = {};
    resetPhotoBlobPreviews();
    // Pause photo uploads for this site (parked items resume on reopen).
    void copPhotoQueue.setActiveSite(null, null);
    setPhotoUploadStatus({});
    setActiveSite(null);
    setActiveTemplate(null);
    setFormData({});
    setVerifyOverrides({});
    setNaOverrides({});
    setChecklistFormat("legacy");
    setCurrentStepIndex(0);
  };

  // Lazily fetch blob previews for the CURRENT section's photos. A reopened
  // site carries only a `${key}_blob` reference (no bytes), so previews must
  // be pulled from blob on demand. Scoped to the visible section — and it
  // prunes previews for slots no longer visible — to bound memory on
  // 50-70-photo sites. Live captures (fileRefs) and legacy inline base64
  // (`${key}_dataUrl`) short-circuit this path.
  useEffect(() => {
    if (!activeSite) return;
    const section = runtimeSections[currentStepIndex];
    if (!section) return;

    // Every photo slot key rendered in this section, matching how the wizard
    // and isSectionComplete derive them: base photo expansions, their extras
    // (`${groupKey}__extra__${id}`), and verification/bundled photo slots
    // (`${key}_photo_${i}`).
    const visible = new Set<string>();
    const groupKeys = new Set<string>();
    expandPhotoQuestions(section).forEach((q) => {
      if (q.type === "photo") {
        visible.add(q.key);
        groupKeys.add(q.groupKey ?? q.key);
      }
      const n = q.verifyPhotoCount ?? 0;
      for (let i = 1; i <= n; i++) visible.add(`${q.key}_photo_${i}`);
    });
    groupKeys.forEach((gk) => {
      const extras = formData[`__extras::${gk}`];
      if (Array.isArray(extras)) {
        extras.forEach((id: string) => visible.add(`${gk}__extra__${id}`));
      }
    });

    // The RET reports step renders RRH serial photos inline (synthetic photo
    // questions, not part of the section's authored questions), so add their
    // keys explicitly. Otherwise their previews get pruned here and are never
    // fetched on reopen, so the captured-photo (green) state wouldn't persist.
    if (section.type === "reports" && section.reportKind !== "alignment") {
      rrhSectors(isDeltaAdded(formData)).forEach((sec) => {
        rrhSlotsForSector(sec.key, retPositionsOf(activeSite?.meta)).forEach(
          (slot) => visible.add(slot.fieldKey),
        );
      });
    }

    // Prune previews for slots not in this section (free their memory).
    let changed = false;
    for (const key of Object.keys(photoBlobPreviews.current)) {
      if (!visible.has(key)) {
        URL.revokeObjectURL(photoBlobPreviews.current[key]);
        delete photoBlobPreviews.current[key];
        changed = true;
      }
    }
    if (changed) setPhotoPreviewTick((t) => t + 1);

    // Load any visible slot that has a blob ref but no preview yet.
    visible.forEach((key) => {
      const blobName = formData[`${key}_blob`] as string | undefined;
      if (!blobName) return;
      if (fileRefs.current[key]) return; // live capture preview wins
      if (photoBlobPreviews.current[key]) return; // already loaded
      if (photoPreviewLoading.current.has(key)) return;
      photoPreviewLoading.current.add(key);
      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/smrtcop/files/${encodeURIComponent(activeSite.id)}/${encodeURIComponent(key)}?blobName=${encodeURIComponent(blobName)}&format=base64`,
        { credentials: "include" },
      )
        .then((r) =>
          r.ok ? r.json() : Promise.reject(new Error(`preview ${r.status}`)),
        )
        .then(({ base64, contentType }) => {
          const binary = atob(base64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          const blob = new Blob([bytes], { type: contentType || "image/jpeg" });
          photoBlobPreviews.current[key] = URL.createObjectURL(blob);
          setPhotoPreviewTick((t) => t + 1);
        })
        .catch((err) =>
          console.warn("[cop] photo preview load failed:", err),
        )
        .finally(() => photoPreviewLoading.current.delete(key));
    });
  }, [activeSite, runtimeSections, currentStepIndex, formData]);

  // Keep a live mirror of formData for the photo queue's async persists.
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // ── "Scanning" tone for every upload ────────────────────────────────────────
  // Ahead of a real validation-rules engine, every wizard upload (photo or file)
  // shows a brief yellow "Scanning…" beat before settling green — so the crew
  // learns the yellow→scan→green rhythm now and a future rule slots straight in
  // where this timer is (it'll set a real pass/fail instead of auto-green). Keys
  // in `scanningKeys` read as pending (yellow); the timer clears them → green.
  // Purely in-memory: nothing is persisted, so a reload shows settled uploads
  // green with no re-scan. RRH serial photos are excluded — their real OCR
  // already drives the light.
  const SCAN_MS = 1100;
  const [scanningKeys, setScanningKeys] = useState<Set<string>>(new Set());
  const scanTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const runScan = useCallback((key: string) => {
    setScanningKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    const existing = scanTimersRef.current.get(key);
    if (existing) clearTimeout(existing);
    const tid = setTimeout(() => {
      scanTimersRef.current.delete(key);
      setScanningKeys((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }, SCAN_MS);
    scanTimersRef.current.set(key, tid);
  }, []);
  useEffect(
    () => () => {
      scanTimersRef.current.forEach((t) => clearTimeout(t));
      scanTimersRef.current.clear();
    },
    [],
  );

  // Persist a completed photo's blob reference into the site's answers. The
  // durable queue calls this once an upload lands. Serialized via
  // persistChainRef so two concurrent completions (concurrency 2) can't
  // clobber each other's whole-answers PUT, and it resolves only after the
  // PUT succeeds — the queue removes the durable record only then, so a photo
  // is never lost between upload and reference-save.
  const onPhotoUploaded = useCallback(
    async (item: PendingPhoto, blobName: string): Promise<void> => {
      // Start the yellow "Scanning…" beat the moment the blob lands (RRH photos
      // are OCR-driven, so they run their own light). Fired before the blob
      // reference is written so the badge never flashes green in between.
      if (!RRH_SLOT_RE.test(item.fieldKey)) runScan(item.fieldKey);
      await new Promise<void>((resolve) => {
        setFormData((prev) => {
          const next = {
            ...prev,
            [item.fieldKey]: item.fileName,
            [`${item.fieldKey}_blob`]: blobName,
            // null (not delete) so clearing the local preview rides through the
            // save delta safely — removals are no longer inferred.
            [`${item.fieldKey}_dataUrl`]: null,
          };
          formDataRef.current = next;
          resolve();
          return next;
        });
      });
      // Persist only the just-added keys (delta) so a concurrent crew save
      // can't be clobbered; flushAnswers shares persistChainRef, so this still
      // resolves only after the blob reference is durably saved.
      await flushAnswers(item.siteId);
    },
    [runScan],
  );

  // Debounced auto-save for answers
  const answersSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveAnswers = useCallback(() => {
    if (!activeSite) return;
    if (answersSaveTimer.current) clearTimeout(answersSaveTimer.current);
    const siteId = activeSite.id;
    answersSaveTimer.current = setTimeout(() => {
      // Draft auto-save: send only this session's delta (answersPatch/Remove),
      // merged server-side. A whole-map write would clobber another crew
      // member editing the same site; the merge also leaves `submitted`
      // untouched.
      flushAnswers(siteId).catch((err) =>
        console.error("Failed to auto-save:", err),
      );
    }, 2000);
  }, [activeSite, formData]);
  useEffect(() => { saveAnswers(); }, [saveAnswers]);

  const updateField = (key: string, value: any) => {
    setFormData((prev) => {
      const next = { ...prev, [key]: value instanceof File ? value.name : value };
      return next;
    });
  };

  // A newly uploaded / retaken file supersedes any admin rejection, so wipe the
  // `${key}_status`/`${key}_statusReason` sidecar. The badge resets to its fresh
  // (green) state and the stale "resubmit" notice disappears. Auto-save persists
  // the cleared answers.
  const clearRejection = (key: string) =>
    setFormData((prev) => {
      if (prev[`${key}_status`] == null && prev[`${key}_statusReason`] == null)
        return prev;
      // Set to null (not delete) so the cleared state rides through the save
      // delta's `patch` — removals are no longer inferred from missing keys.
      return {
        ...prev,
        [`${key}_status`]: null,
        [`${key}_statusReason`]: null,
      };
    });

  // Record a wizard step as completed when the crew presses "Continue", so the
  // admin View can populate that step's data before the whole COP is submitted.
  // Stored inside answers (`__completedSteps`) so the existing draft auto-save
  // persists it — no separate endpoint. Generated photo sub-sections map back
  // to their originating template section id (id = origId*1000 + n).
  const markSectionComplete = (section: COPSection | undefined) => {
    if (!section) return;
    const originId = section.generated
      ? Math.floor(section.id / 1000)
      : section.id;
    const apply = (prev: Record<string, any>) => {
      const done: number[] = Array.isArray(prev.__completedSteps)
        ? prev.__completedSteps
        : [];
      const next = { ...prev };
      if (!done.includes(originId)) next.__completedSteps = [...done, originId];
      // Also record the exact RUNTIME step id the crew saved (not the collapsed
      // origin id), so the progress bar can green each step — and only green the
      // "Photos" circle once every one of its sub-steps has been saved.
      const savedIds: number[] = Array.isArray(prev.__savedStepIds)
        ? prev.__savedStepIds
        : [];
      if (!savedIds.includes(section.id))
        next.__savedStepIds = [...savedIds, section.id];
      // Stamp the moment the crew saves the RET step — this drives the RET
      // output's Installation Date (col W), not the later generation date.
      // Re-saving the step updates the stamp.
      if (section.type === "reports") next.ret_saved_at = new Date().toISOString();
      return next;
    };
    // Mirror into the ref synchronously too, so a flush fired right after Save
    // (e.g. the assignee's auto-return) sees the just-recorded saved-step ids
    // instead of the pre-Save snapshot.
    formDataRef.current = apply(formDataRef.current || {});
    setFormData(apply);
  };

  // Reject-aware un-save. When an admin (or a live vision rule) fails a photo in
  // a step the crew had already saved ("Sent"), that step is no longer valid and
  // must be re-sent after the crew fixes it. We drop it from `__savedStepIds`
  // while the rejection is present, so that once the crew resubmits (which clears
  // the `_status:"fail"` sidecar) the Save button returns to the YELLOW "Save"
  // state — prompting a fresh save — instead of snapping straight back to GREEN
  // "Sent". Pressing Save re-adds it. Scoped to photo steps (Sector A/B/C), and
  // mirrors the rejected-photo detection used by isSectionComplete.
  useEffect(() => {
    if (!activeSite) return;
    const savedIds: number[] = Array.isArray(formData.__savedStepIds)
      ? formData.__savedStepIds
      : [];
    if (savedIds.length === 0) return;
    const kept = savedIds.filter((id) => {
      const section = runtimeSections.find((s) => s.id === id);
      if (!section || section.type !== "photo") return true;
      const groupKeys = (section.questions || [])
        .map((q) => q.groupKey)
        .filter((g): g is string => !!g);
      const hasRejected = Object.keys(formData).some((k) => {
        if (!k.endsWith("_status") || formData[k] !== "fail") return false;
        const base = k.slice(0, -"_status".length);
        // A flag only counts while its slot still HOLDS a photo. Removing a
        // photo does not clear its status sidecar, so a deleted crew-added
        // extra used to leave an orphan "fail" behind — blocking the step
        // forever with no slot left to fix it.
        if (!formData[base]) return false;
        return groupKeys.some((gk) => base === gk || base.startsWith(`${gk}_`));
      });
      return !hasRejected;
    });
    if (kept.length !== savedIds.length) {
      setFormData((prev) => ({ ...prev, __savedStepIds: kept }));
    }
  }, [formData, runtimeSections, activeSite]);

  // Photo-question keys that are FCC Registration signs (matched by label
  // across every section), so the OCR effect + badge can find them without a
  // key-name convention like the RRH slots have.
  const fccPhotoKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const s of runtimeSections)
      for (const q of s.questions || []) if (isFccRegPhoto(q)) keys.add(q.key);
    return keys;
  }, [runtimeSections]);

  // ── FCC Registration OCR ────────────────────────────────────────────────────
  // When an FCC Registration photo's blob lands, OCR the sign to extract the
  // Antenna Structure Registration number and store it at `${key}_fccNumber`
  // (crew-editable below the photo). `${key}_fccBlob` records which blob it came
  // from so a retake re-triggers extraction and a failed read doesn't loop or
  // clobber a value the crew has already corrected by hand.
  const fccOcrInFlight = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!activeSite) return;
    // Viewer mode reflects STORED verdicts — it must not re-run the vision
    // rules. Re-running would spend API calls on a look, and a check that
    // errored would repaint a stored pass as a fail in the admin's view.
    if (readOnly) return;
    const siteId = activeSite.id;
    for (const key of fccPhotoKeys) {
      const blobName = formData[`${key}_blob`] as string | undefined;
      if (!blobName) continue; // photo not uploaded yet
      if (formData[`${key}_fccBlob`] === blobName) continue; // already OCR'd
      if (fccOcrInFlight.current.has(key)) continue;
      fccOcrInFlight.current.add(key);
      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/smrtcop/fcc-registration/${encodeURIComponent(siteId)}/${encodeURIComponent(key)}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ blobName }),
        },
      )
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`ocr ${r.status}`))))
        .then((data) => {
          const num = (data?.number && String(data.number).trim()) || "";
          updateField(`${key}_fccNumber`, num);
          updateField(`${key}_fccBlob`, blobName);
        })
        .catch((err) => {
          console.warn("FCC registration OCR failed:", err);
          // Leave the number blank so the crew types it; mark the blob attempted
          // so it doesn't retry in a loop (a retake re-triggers extraction).
          updateField(`${key}_fccNumber`, "");
          updateField(`${key}_fccBlob`, blobName);
        })
        .finally(() => {
          fccOcrInFlight.current.delete(key);
        });
    }
  }, [formData, activeSite, fccPhotoKeys]);

  // ── JHA "Working at Heights" rule (rule engine — first rule) ────────────────
  // When a daily JHA PDF lands, validate it server-side: the crew must not tick
  // BOTH "I certify no persons working at heights" AND the "Working at Heights"
  // hazard. On a violation the slot goes red (with a reason) and the JHA step's
  // completion gate blocks submission until it's corrected. `${key}_jhaBlob`
  // records which blob was checked so a re-upload re-validates and a read error
  // doesn't loop. Fail-open: a transient error leaves the slot green so it can't
  // wrongly block a legitimate submission.
  const jhaValidateInFlight = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!activeSite) return;
    // Viewer shows the stored JHA verdict; no re-validation. See the vision
    // rules above.
    if (readOnly) return;
    const siteId = activeSite.id;
    for (const key of Object.keys(formData)) {
      if (!JHA_SLOT_RE.test(key)) continue;
      const ref = formData[key] as ReportFileRef | undefined;
      const blobName =
        ref && typeof ref === "object" ? ref.blobName : undefined;
      if (!blobName) continue; // not uploaded yet
      if (formData[`${key}_jhaBlob`] === blobName) continue; // already validated
      if (jhaValidateInFlight.current.has(key)) continue;
      jhaValidateInFlight.current.add(key);
      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/smrtcop/jha-validate/${encodeURIComponent(siteId)}/${encodeURIComponent(key)}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ blobName }),
        },
      )
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`jha ${r.status}`))))
        .then((data) => {
          const violation = !!data?.violation;
          updateField(`${key}_jhaViolation`, violation);
          updateField(`${key}_jhaBlob`, blobName);
          updateField(`${key}_status`, violation ? "fail" : "pass");
          updateField(
            `${key}_statusReason`,
            violation ? JHA_HEIGHT_CONFLICT_REASON : null,
          );
        })
        .catch((err) => {
          console.warn("JHA validation failed:", err);
          // Fail-open: don't block on a read error. Mark the blob checked so it
          // doesn't loop; a re-upload re-validates.
          updateField(`${key}_jhaViolation`, false);
          updateField(`${key}_jhaBlob`, blobName);
          updateField(`${key}_status`, "pass");
          updateField(`${key}_statusReason`, null);
        })
        .finally(() => {
          jhaValidateInFlight.current.delete(key);
        });
    }
  }, [formData, activeSite]);

  // ── RRH serial OCR ────────────────────────────────────────────────────────
  // When an RRH photo's blob lands (uploaded via the durable photo queue), OCR
  // the label to extract the serial number and store it at `${key}_serial`.
  // `${key}_serialBlob` records which blob produced the serial, so a retaken
  // photo (new blob) re-triggers extraction and a failed attempt doesn't loop.
  const rrhOcrInFlight = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!activeSite) return;
    // Viewer mode reflects STORED verdicts — it must not re-run the vision
    // rules. Re-running would spend API calls on a look, and a check that
    // errored would repaint a stored pass as a fail in the admin's view.
    if (readOnly) return;
    const siteId = activeSite.id;
    for (const key of Object.keys(formData)) {
      if (!RRH_SLOT_RE.test(key)) continue;
      const blobName = formData[`${key}_blob`] as string | undefined;
      if (!blobName) continue; // photo not uploaded yet
      if (formData[`${key}_serialBlob`] === blobName) continue; // already OCR'd
      if (rrhOcrInFlight.current.has(key)) continue;
      rrhOcrInFlight.current.add(key);
      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/smrtcop/rrh-serial/${encodeURIComponent(siteId)}/${encodeURIComponent(key)}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ blobName }),
        },
      )
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`ocr ${r.status}`))))
        .then((data) => {
          const serial = (data?.serial && String(data.serial).trim()) || "N/A";
          const ok = serial !== "N/A";
          updateField(`${key}_serial`, serial);
          updateField(`${key}_serialBlob`, blobName);
          // Traffic light: serial read → green, none extracted → red. Persist
          // the status/reason sidecar so the admin View + rejection seam see it
          // (the live badge also derives this from the serial while in flight).
          updateField(`${key}_status`, ok ? "pass" : "fail");
          updateField(
            `${key}_statusReason`,
            ok ? null : RRH_SERIAL_FAIL_REASON,
          );
        })
        .catch((err) => {
          console.warn("RRH serial OCR failed:", err);
          // Mark this blob attempted so it doesn't retry in a loop; a retake
          // (new blob) will re-trigger extraction.
          updateField(`${key}_serial`, "N/A");
          updateField(`${key}_serialBlob`, blobName);
          updateField(`${key}_status`, "fail");
          updateField(`${key}_statusReason`, RRH_SERIAL_FAIL_REASON);
        })
        .finally(() => {
          rrhOcrInFlight.current.delete(key);
        });
    }
  }, [formData, activeSite]);

  // ── Mount-azimuth photo rule: check the alignment tool's centre reticle
  // colour (green = pass, red = fail). Applies ONLY to the v2 "TowerTop Mount
  // Validation Azimuth" category slots. `${key}_azBlob` records which blob was
  // checked so a retaken photo re-triggers and a failed attempt doesn't loop.
  const azOcrInFlight = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!activeSite) return;
    // Viewer mode reflects STORED verdicts — it must not re-run the vision
    // rules. Re-running would spend API calls on a look, and a check that
    // errored would repaint a stored pass as a fail in the admin's view.
    if (readOnly) return;
    const siteId = activeSite.id;
    for (const key of Object.keys(formData)) {
      if (!AZIMUTH_SLOT_RE.test(key)) continue;
      const blobName = formData[`${key}_blob`] as string | undefined;
      if (!blobName) continue; // photo not uploaded yet
      if (formData[`${key}_azBlob`] === blobName) continue; // already checked
      if (azOcrInFlight.current.has(key)) continue;
      azOcrInFlight.current.add(key);
      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/smrtcop/azimuth-check/${encodeURIComponent(siteId)}/${encodeURIComponent(key)}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ blobName }),
        },
      )
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`az ${r.status}`))))
        .then((data) => {
          const result = String(data?.result || "unknown").toLowerCase();
          const pass = result === "green";
          updateField(`${key}_azResult`, result);
          updateField(`${key}_azBlob`, blobName);
          // Persist the status/reason sidecar (like RRH) so the admin View +
          // rejection seam see the verdict, not just the live crew badge.
          updateField(`${key}_status`, pass ? "pass" : "fail");
          updateField(
            `${key}_statusReason`,
            pass
              ? null
              : result === "red"
                ? AZIMUTH_RED_REASON
                : AZIMUTH_UNREADABLE_REASON,
          );
        })
        .catch((err) => {
          // On error, treat as unreadable so the tech retakes rather than a
          // silent pass. The blob is marked so it doesn't retry in a loop.
          console.warn("Azimuth check failed:", err);
          updateField(`${key}_azResult`, "unknown");
          updateField(`${key}_azBlob`, blobName);
          updateField(`${key}_status`, "fail");
          updateField(`${key}_statusReason`, AZIMUTH_UNREADABLE_REASON);
        })
        .finally(() => {
          azOcrInFlight.current.delete(key);
        });
    }
  }, [formData, activeSite]);

  // ── Tape Drop photo rule: check which side of the tape measure is shown
  // (imperial = pass, metric = fail). Applies ONLY to the v2 "Tape Drop
  // Antenna/Mount RAD" category slots. `${key}_tapeBlob` records which blob was
  // checked so a retaken photo re-triggers and a failed attempt doesn't loop.
  const tapeOcrInFlight = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!activeSite) return;
    // Viewer mode reflects STORED verdicts — it must not re-run the vision
    // rules. Re-running would spend API calls on a look, and a check that
    // errored would repaint a stored pass as a fail in the admin's view.
    if (readOnly) return;
    const siteId = activeSite.id;
    for (const key of Object.keys(formData)) {
      if (!TAPE_DROP_SLOT_RE.test(key)) continue;
      const blobName = formData[`${key}_blob`] as string | undefined;
      if (!blobName) continue; // photo not uploaded yet
      if (formData[`${key}_tapeBlob`] === blobName) continue; // already checked
      if (formData[`${key}_tapeOverride`]) continue; // crew manually passed it
      if (tapeOcrInFlight.current.has(key)) continue;
      tapeOcrInFlight.current.add(key);
      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/smrtcop/tape-drop-check/${encodeURIComponent(siteId)}/${encodeURIComponent(key)}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ blobName }),
        },
      )
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`tape ${r.status}`))))
        .then((data) => {
          const result = String(data?.result || "unknown").toLowerCase();
          const pass = result === "imperial";
          updateField(`${key}_tapeResult`, result);
          updateField(`${key}_tapeBlob`, blobName);
          // Persist the status/reason sidecar (like the other rules) so the
          // admin View + rejection seam see the verdict, not just the badge.
          updateField(`${key}_status`, pass ? "pass" : "fail");
          updateField(
            `${key}_statusReason`,
            pass
              ? null
              : result === "metric"
                ? TAPE_DROP_METRIC_REASON
                : TAPE_DROP_UNCLEAR_REASON,
          );
          // Tape drop is a SOFT warning: rather than hard-blocking, flash a
          // Retake-or-Override prompt so a valid-but-hard-to-read shot can be
          // passed manually. The photo stays "fail" (blocks Save) until the
          // crew resolves it here.
          if (!pass) setTapeWarn({ key });
        })
        .catch((err) => {
          // On error, treat as unclear so the tech retakes rather than a
          // silent pass. The blob is marked so it doesn't retry in a loop.
          console.warn("Tape drop check failed:", err);
          updateField(`${key}_tapeResult`, "unclear");
          updateField(`${key}_tapeBlob`, blobName);
          updateField(`${key}_status`, "fail");
          updateField(`${key}_statusReason`, TAPE_DROP_UNCLEAR_REASON);
          setTapeWarn({ key });
        })
        .finally(() => {
          tapeOcrInFlight.current.delete(key);
        });
    }
  }, [formData, activeSite]);

  // Crew chose "Override" on a flagged Tape Drop photo: manually pass it and
  // move on. `_tapeOverride` also keeps the tape rule from re-firing on the
  // same blob and makes the badge read green.
  const overrideTapePhoto = (key: string) => {
    updateField(`${key}_tapeOverride`, true);
    updateField(`${key}_status`, "pass");
    updateField(`${key}_statusReason`, null);
    setTapeWarn(null);
  };

  // Crew chose "Retake": drop the flagged photo (and its blob) so the slot is
  // empty for a fresh shot, clearing every tape sidecar so the new photo
  // re-triggers the check cleanly.
  const retakeTapePhoto = (key: string) => {
    // Read-only viewer: never mutate the crew's files. A disabled <fieldset>
    // covers form controls but not click handlers on styled divs, so the
    // destructive handlers guard themselves.
    if (readOnly) return;
    if (photoBlobPreviews.current[key]) {
      URL.revokeObjectURL(photoBlobPreviews.current[key]);
      delete photoBlobPreviews.current[key];
    }
    if (fileRefs.current[key]) {
      URL.revokeObjectURL(fileRefs.current[key].url);
      delete fileRefs.current[key];
    }
    if (activeSite) void copPhotoQueue.removePending(activeSite.id, key);
    const removedBlob = formData[`${key}_blob`] as string | undefined;
    if (activeSite && removedBlob) {
      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/smrtcop/files/${encodeURIComponent(activeSite.id)}/${encodeURIComponent(key)}?blobName=${encodeURIComponent(removedBlob)}`,
        { method: "DELETE", credentials: "include" },
      ).catch((err) => console.warn("Tape photo blob delete failed:", err));
    }
    updateField(key, null);
    updateField(`${key}_blob`, null);
    updateField(`${key}_dataUrl`, null);
    updateField(`${key}_tapeResult`, null);
    updateField(`${key}_tapeBlob`, null);
    updateField(`${key}_tapeOverride`, null);
    updateField(`${key}_status`, null);
    updateField(`${key}_statusReason`, null);
    setTapeWarn(null);
  };

  // ── Upload status badge — consistent across EVERY upload ────────────────────
  // Same look as the photo checkmark. Most uploads have no validation rules yet,
  // so any uploaded file is a green "pass". The RRH serial photos are the first
  // live rule: yellow while the label OCR is in flight, then green if a serial
  // was read or red if none could be extracted. The per-slot `${key}_status`
  // sidecar (admin rejection / future rules) is read first, so those always win.
  const uploadStatus = (
    key: string,
    hasFile: boolean,
  ): "none" | "pending" | "pass" | "fail" => {
    if (!hasFile) return "none";
    const explicit = formData[`${key}_status`] as
      | "pending"
      | "pass"
      | "fail"
      | undefined;
    if (explicit) return explicit;
    // RRH serial photos: derive the light from the OCR sidecars so it turns
    // yellow the instant the photo lands (before the OCR round-trip persists a
    // status) and tracks retakes for free.
    if (RRH_SLOT_RE.test(key)) {
      const blobName = formData[`${key}_blob`] as string | undefined;
      const serialBlob = formData[`${key}_serialBlob`] as string | undefined;
      const serial = (formData[`${key}_serial`] as string) || "";
      if (!blobName || serialBlob !== blobName) return "pending"; // OCR in flight
      return serial && serial !== "N/A" ? "pass" : "fail";
    }
    // FCC Registration photos: yellow while the sign OCR is in flight, then
    // green once a registration number exists (read or hand-entered) or red if
    // it's still blank. Derived from the sidecars so a manual correction flips
    // the light for free.
    if (fccPhotoKeys.has(key)) {
      const blobName = formData[`${key}_blob`] as string | undefined;
      const fccBlob = formData[`${key}_fccBlob`] as string | undefined;
      const num = String(formData[`${key}_fccNumber`] || "").trim();
      if (!blobName || fccBlob !== blobName) return "pending"; // OCR in flight
      return num ? "pass" : "fail";
    }
    // Mount-azimuth photos: yellow while the reticle-colour check is in flight,
    // then green if the centre indicator is green, else red (red reticle or
    // unreadable). Derived from the sidecars so a retake flips the light.
    if (AZIMUTH_SLOT_RE.test(key)) {
      const blobName = formData[`${key}_blob`] as string | undefined;
      const azBlob = formData[`${key}_azBlob`] as string | undefined;
      const result = (formData[`${key}_azResult`] as string) || "";
      if (!blobName || azBlob !== blobName) return "pending"; // check in flight
      return result === "green" ? "pass" : "fail";
    }
    // Tape Drop photos: yellow while the tape-side check is in flight, then
    // green if the imperial (feet & inches) side is shown, else red (metric or
    // unreadable). Derived from the sidecars so a retake flips the light.
    if (TAPE_DROP_SLOT_RE.test(key)) {
      if (formData[`${key}_tapeOverride`]) return "pass"; // manually passed
      const blobName = formData[`${key}_blob`] as string | undefined;
      const tapeBlob = formData[`${key}_tapeBlob`] as string | undefined;
      const result = (formData[`${key}_tapeResult`] as string) || "";
      if (!blobName || tapeBlob !== blobName) return "pending"; // check in flight
      return result === "imperial" ? "pass" : "fail";
    }
    // JHA daily forms: yellow while the "Working at Heights" rule validates the
    // PDF, then red if it found the certify/hazard conflict, else green.
    if (JHA_SLOT_RE.test(key)) {
      const ref = formData[key] as { blobName?: string } | undefined;
      const blobName =
        ref && typeof ref === "object" ? ref.blobName : undefined;
      const jhaBlob = formData[`${key}_jhaBlob`] as string | undefined;
      if (!blobName || jhaBlob !== blobName) return "pending"; // validating
      return formData[`${key}_jhaViolation`] ? "fail" : "pass";
    }
    // Generic yellow→scan→green tone for every other upload: a photo whose blob
    // hasn't landed yet is still uploading (yellow), and any upload inside its
    // brief post-upload scan window shows pending before settling green. No real
    // rule yet, so it always resolves to pass.
    const val = formData[key];
    if (typeof val === "string" && !formData[`${key}_blob`]) return "pending";
    if (scanningKeys.has(key)) return "pending";
    return "pass";
  };
  // Sidecar reason for a fail (or any) status. Manual rejection / future rules
  // write `${key}_statusReason` alongside `${key}_status`; read here so the
  // badge can surface it as a tooltip without changing the status shape. RRH
  // serial fails fall back to the OCR reason so the red badge is self-explaining.
  const uploadStatusReason = (key: string): string | undefined => {
    const explicit = (formData[`${key}_statusReason`] as string) || undefined;
    if (explicit) return explicit;
    if (RRH_SLOT_RE.test(key) && uploadStatus(key, true) === "fail")
      return RRH_SERIAL_FAIL_REASON;
    if (fccPhotoKeys.has(key) && uploadStatus(key, true) === "fail")
      return FCC_REG_FAIL_REASON;
    if (JHA_SLOT_RE.test(key) && uploadStatus(key, true) === "fail")
      return JHA_HEIGHT_CONFLICT_REASON;
    if (AZIMUTH_SLOT_RE.test(key) && uploadStatus(key, true) === "fail")
      return (formData[`${key}_azResult`] as string) === "red"
        ? AZIMUTH_RED_REASON
        : AZIMUTH_UNREADABLE_REASON;
    if (TAPE_DROP_SLOT_RE.test(key) && uploadStatus(key, true) === "fail")
      return (formData[`${key}_tapeResult`] as string) === "metric"
        ? TAPE_DROP_METRIC_REASON
        : TAPE_DROP_UNCLEAR_REASON;
    return undefined;
  };
  const renderUploadBadge = (
    status: "none" | "pending" | "pass" | "fail",
    reason?: string,
    // Optional tap handler for a failed badge (overrides the default
    // reason-popup). Tape Drop uses this to reopen the Retake/Override prompt.
    onFailClick?: () => void,
  ) => {
    // A rejected badge is tappable: `title` covers desktop hover, but mobile has
    // no hover, so a tap surfaces the reason directly.
    const rejected = status === "fail" && (!!reason || !!onFailClick);
    return (
      <span
        className={`smrtcop-photo-row-status${
          status === "pass"
            ? " done"
            : status === "none"
              ? ""
              : ` ${status}`
        }`}
        aria-hidden={!rejected}
        role={rejected ? "button" : undefined}
        title={rejected && reason ? t(reason) : undefined}
        style={rejected ? { cursor: "pointer" } : undefined}
        onClick={
          rejected
            ? onFailClick
              ? onFailClick
              : () => setErrorModal(t(reason!))
            : undefined
        }
      >
        {status === "pass" ? "✓" : status === "fail" ? "✕" : ""}
      </span>
    );
  };

  // Renders a single report upload slot (RET or Alignment). Shared by every
  // slot in the reports section so the Delta add/remove control can be
  // interleaved without duplicating the uploader markup.
  const renderReportSlot = (slot: ReportSlot, required = true) => {
    const ref = formData[slot.fieldKey] as ReportFileRef | undefined;
    const hasFile = !!ref && !!ref.blobName;
    const positions = retPositionsOf(activeSite?.meta);

    // Best-effort delete of the existing blob, then clear the slot so the empty
    // state re-appears for re-upload. Shared by the txtrpt and non-txtrpt rows.
    const handleReplace = async () => {
    // Read-only viewer: never mutate the crew's files. A disabled <fieldset>
    // covers form controls but not click handlers on styled divs, so the
    // destructive handlers guard themselves.
    if (readOnly) return;
      if (!activeSite) return;
      try {
        await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/smrtcop/files/${encodeURIComponent(activeSite.id)}/${encodeURIComponent(slot.fieldKey)}?blobName=${encodeURIComponent(ref!.blobName)}`,
          { method: "DELETE", credentials: "include" },
        );
      } catch (err) {
        console.warn("Report delete failed; clearing slot anyway:", err);
      }
      updateField(slot.fieldKey, null);
    };

    // Open the readable PDF for a RET scan in a new tab (pre-opened so popup
    // blockers don't swallow it).
    const viewTxtrptReport = () => {
      if (!activeSite) return;
      const win = window.open("", "_blank");
      openTxtrptReportPdf(win, activeSite.id, slot.fieldKey, ref!).catch(
        (err) => {
          console.error("Failed to open report PDF:", err);
          if (win) win.close();
          alert(t("Failed to open report. Try again."));
        },
      );
    };

    return (
      <div key={slot.fieldKey}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 4,
          }}
        >
          {renderUploadBadge(
            uploadStatus(slot.fieldKey, hasFile),
            uploadStatusReason(slot.fieldKey),
          )}
          <span
            style={{
              fontSize: "0.75rem",
              color: "#9aa0a6",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {t(retSlotLabel(slot, positions))}{required && !hasFile ? " *" : ""}
            {/* Yellow "Scanning…" beat after a file lands, before it settles
                green — same tone as the photo uploads. */}
            {uploadStatus(slot.fieldKey, hasFile) === "pending" && (
              <span
                style={{
                  marginLeft: 6,
                  color: "#d4a017",
                  textTransform: "none",
                  letterSpacing: 0,
                }}
              >
                {t("Scanning…")}
              </span>
            )}
          </span>
        </div>
        {hasFile ? (
          isTxtrptSlot(slot.fieldKey) ? (
            // RET .txtrpt slot: the blue link opens the readable PDF in a tab
            // (view/print/save from there). The .txtrpt itself is preserved
            // untouched as the source of truth for extraction; R replaces it.
            <div className="smrtdocs-upload-inner smrtdocs-upload-file">
              <span
                className="smrtdocs-file-link"
                onClick={viewTxtrptReport}
                title={t("View Report (PDF)")}
              >
                {ref!.name}
              </span>
              <button
                className="btn-icon smrtdocs-btn-multi"
                style={{ background: "#d4a017", color: "#fff" }}
                title={t("Replace")}
                onClick={handleReplace}
              >
                R
              </button>
            </div>
          ) : (
          <div className="smrtdocs-upload-inner smrtdocs-upload-file">
            <span
              className="smrtdocs-file-link"
              onClick={async () => {
                if (!activeSite) return;
                try {
                  const r = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/smrtcop/files/${encodeURIComponent(activeSite.id)}/${encodeURIComponent(slot.fieldKey)}?blobName=${encodeURIComponent(ref!.blobName)}&format=base64`,
                    { credentials: "include" },
                  );
                  if (!r.ok) throw new Error(`Preview failed: ${r.status}`);
                  const data = await r.json();
                  if (data?.base64) {
                    const binary = atob(data.base64);
                    const bytes = new Uint8Array(binary.length);
                    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                    const blob = new Blob([bytes], {
                      type: data.contentType || ref!.type || "application/pdf",
                    });
                    const url = URL.createObjectURL(blob);
                    window.open(url, "_blank", "noopener");
                  }
                } catch (err) {
                  console.error("Failed to open report:", err);
                  alert(t("Failed to open file. Try again."));
                }
              }}
              title={ref!.name}
            >
              {ref!.name}
            </span>
            <button
              className="btn-icon smrtdocs-btn-multi"
              style={{ background: "#d4a017", color: "#fff" }}
              title={t("Replace")}
              onClick={handleReplace}
            >
              R
            </button>
          </div>
          )
        ) : (
          <label className="smrtdocs-upload-inner">
            {t("Upload")}
            <input
              type="file"
              accept={slot.accept}
              style={{ display: "none" }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.currentTarget.value = "";
                if (!file || !activeSite) return;
                const formDataBody = new FormData();
                formDataBody.append("file", file);
                // RET slots carry a sector + port, so rename the stored file to
                // the slot label (e.g. Alpha P3 → "AlphaP3.txtrpt") — the backend
                // keeps the original extension. Other slots (alignment) send no
                // desiredName and keep their original filename.
                if (slot.sector && slot.port) {
                  const base = retSlotLabel(slot, positions).replace(/\s+/g, "");
                  formDataBody.append("desiredName", base);
                }
                try {
                  const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/smrtcop/files/${encodeURIComponent(activeSite.id)}/${encodeURIComponent(slot.fieldKey)}`,
                    {
                      method: "POST",
                      credentials: "include",
                      body: formDataBody,
                    },
                  );
                  if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
                  const meta = (await res.json()) as ReportFileRef;
                  // Yellow "Scanning…" beat before green — started before the ref
                  // is written so the badge goes straight to yellow, not green.
                  runScan(slot.fieldKey);
                  updateField(slot.fieldKey, meta);
                  clearRejection(slot.fieldKey);
                } catch (err) {
                  console.error("Failed to upload report:", err);
                  alert(t("Failed to upload file. Try again."));
                }
              }}
            />
          </label>
        )}
      </div>
    );
  };

  // Add one more JHA day slot (Day 6, 7, …). The count persists so the added
  // slots stay visible on reload even before a file is uploaded.
  const addJhaDay = () =>
    updateField("jha_day_count", jhaDayCount(formData) + 1);

  // Reveal the optional Delta sector RET slots. The flag persists the choice
  // so the slots stay visible on reload even before any file is uploaded.
  const addDeltaSector = () => updateField("ret_delta_added", true);

  // Hide the Delta sector again, clearing the flag and best-effort deleting
  // any uploaded Delta blobs so a mistaken add leaves no orphaned files.
  const removeDeltaSector = async () => {
    // Read-only viewer: never mutate the crew's files. A disabled <fieldset>
    // covers form controls but not click handlers on styled divs, so the
    // destructive handlers guard themselves.
    if (readOnly) return;
    // Delta TXTRPT uploads store a ReportFileRef object; Delta RRH serial
    // photos use the photo format (filename + `${key}_blob`). Resolve the blob
    // name from whichever shape applies, best-effort delete, then clear every
    // associated key so a mistaken add leaves nothing behind.
    const deltaKeys = [
      ...RET_DELTA_SLOTS.map((s) => s.fieldKey),
      ...rrhSlotsForSector("delta").map((s) => s.fieldKey),
    ];
    for (const fieldKey of deltaKeys) {
      const val = formData[fieldKey];
      const blobName =
        val && typeof val === "object"
          ? (val as ReportFileRef).blobName
          : (formData[`${fieldKey}_blob`] as string | undefined);
      if (activeSite && blobName) {
        try {
          await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/smrtcop/files/${encodeURIComponent(activeSite.id)}/${encodeURIComponent(fieldKey)}?blobName=${encodeURIComponent(blobName)}`,
            { method: "DELETE", credentials: "include" },
          );
        } catch (err) {
          console.warn("Delta file delete failed; clearing slot anyway:", err);
        }
      }
      updateField(fieldKey, null);
      updateField(`${fieldKey}_serial`, null);
      updateField(`${fieldKey}_serialBlob`, null);
      updateField(`${fieldKey}_blob`, null);
      updateField(`${fieldKey}_dataUrl`, null);
    }
    updateField("ret_delta_added", false);
  };

  // ── Checklist: fetch photo requirements once per wizard-open. Parses the
  // site's Checklist Excel via GET /smrtcop/checklist/:siteId. Results feed
  // runtimeSections, which replaces the template's photo section with the
  // generated steps: legacy → Sector A/B/C/SPD/Verification; v2 → Sector
  // Alpha/Beta/Gamma (N/A categories excluded).
  useEffect(() => {
    if (!activeSite) {
      setChecklistItems([]);
      setVerificationItems([]);
      setChecklistFormat("legacy");
      return;
    }
    setChecklistItems([]);
    setVerificationItems([]);
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/smrtcop/checklist/${encodeURIComponent(activeSite.id)}`,
      { credentials: "include" },
    )
      .then((r) => r.json())
      .then((data) => {
        setChecklistFormat(data?.format === "v2" ? "v2" : "legacy");
        if (data?.missing) {
          setChecklistItems([]);
          setVerificationItems([]);
          return;
        }
        setChecklistItems((data?.items || []).map(mapChecklistRow));
        setVerificationItems((data?.verificationItems || []).map(mapChecklistRow));
      })
      .catch((err) => {
        console.error("Failed to load Checklist:", err);
        setChecklistItems([]);
        setVerificationItems([]);
        setChecklistFormat("legacy");
      });
  }, [activeSite]);

  // ── BOM section: load rows when the wizard lands on a bom-type section ──
  useEffect(() => {
    if (!activeSite) return;
    const section = runtimeSections[currentStepIndex];
    if (!section || section.type !== "bom") return;

    const bomKey = `${section.id}_bom`;
    const saved = formData[bomKey];
    if (Array.isArray(saved) && saved.length > 0) {
      setBomRows(saved as BomRow[]);
      setBomStatus("loaded");
      return;
    }

    setBomStatus("loading");

    // DEBUG — what the wizard sees locally for this site's uploaded files
    console.log("[BOM DEBUG] activeSite.id:", activeSite.id);
    console.log("[BOM DEBUG] activeSite.meta.sourceFiles:", activeSite.meta?.sourceFiles);
    console.log("[BOM DEBUG] looking for slot='fixed' && docKey='final_bom' via GET /smrtcop/bom/:siteId");

    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/smrtcop/bom/${encodeURIComponent(activeSite.id)}`,
      { credentials: "include" },
    )
      .then(async (r) => {
        console.log("[BOM DEBUG] fetch status:", r.status, r.statusText);
        const text = await r.text();
        try {
          return JSON.parse(text);
        } catch (e) {
          console.error("[BOM DEBUG] response was not JSON — parse failed:", e);
          throw e;
        }
      })
      .then((data) => {
        if (data?.missing) {
          setBomRows([]);
          setBomStatus("missing");
          return;
        }
        const rows: BomRow[] = (data?.rows || []).map((r: { item?: string; description?: string; uom?: string; qty?: string }) => ({
          item: r.item ?? "",
          description: r.description ?? "",
          uom: r.uom ?? "",
          plannedQty: r.qty ?? "",
          actualInstalled: r.qty ?? "",
        }));
        setBomRows(rows);
        setBomStatus(rows.length > 0 ? "loaded" : "missing");
        if (rows.length > 0) updateField(bomKey, rows);
      })
      .catch((err) => {
        console.error("Failed to load BOM:", err);
        setBomStatus("error");
      });
  }, [activeSite, runtimeSections, currentStepIndex]);

  const updateBomRow = (
    sectionId: number,
    rowIndex: number,
    actualInstalled: string,
  ) => {
    setBomRows((prev) => {
      const next = prev.map((r, i) =>
        i === rowIndex ? { ...r, actualInstalled } : r,
      );
      updateField(`${sectionId}_bom`, next);
      return next;
    });
  };

  const openReview = (site: COPSite) => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtcop/site-state/${encodeURIComponent(site.id)}`, { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        // A submitted site renders against the template SNAPSHOT frozen at
        // sign-off, so later template edits never change what was submitted.
        // Falls back to the live template (e.g. legacy submissions with no snap).
        const snap = data.templateSnapshot;
        const tpl =
          snap && Array.isArray(snap.sections) && snap.sections.length
            ? snap
            : templates.find((t) => t.id === site.templateId);
        if (!tpl) return;
        setReviewSite(site);
        setReviewTemplate(JSON.parse(JSON.stringify(tpl)));
        // Complete sites render the frozen `submitted` snapshot; in-progress
        // sites (step-by-step) have no snapshot yet, so fall back to the live
        // `answers` so the crew can review the steps they've saved so far.
        const submitted = data.submitted || {};
        setReviewData(
          Object.keys(submitted).length > 0 ? submitted : data.answers || {},
        );
        setReviewPortal(data.portalUploads || {});
        // Restore the crew's own ✓ markers (kept separate from the admin's).
        setReviewDownloaded(new Set<string>(data.downloadedPhotos?.crew || []));
        setDownloadStatus({});
      })
      .catch(() => {
        const tpl = templates.find((t) => t.id === site.templateId);
        if (!tpl) return;
        setReviewSite(site);
        setReviewTemplate(JSON.parse(JSON.stringify(tpl)));
        setReviewData({});
        setReviewPortal({});
        setReviewDownloaded(new Set());
        setDownloadStatus({});
      });
  };

  const closeReview = () => {
    setReviewSite(null);
    setReviewTemplate(null);
    setReviewData({});
    setReviewPortal({});
    setReviewDownloaded(new Set());
    setDownloadStatus({});
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // Read-only Site Info view
  if (viewingSite) {
    const docs = (viewingSite.meta.sourceFiles || []).filter(
      (f) => f.slot === "fixed" && f.docKey && CREW_VISIBLE_DOC_KEYS.has(f.docKey),
    );
    const activeDoc = docs[viewDocIndex] ?? null;

    const azRaw = viewingSite.meta.azimuths;
    const azSrc = azRaw && typeof azRaw === "object" ? (azRaw as COPAzimuths) : null;
    const azObj: Record<string, string> = {
      A: String(azSrc?.A ?? ""),
      B: String(azSrc?.B ?? ""),
      C: String(azSrc?.C ?? ""),
      // Include Delta only when the record carries it.
      ...(azSrc?.D !== undefined ? { D: String(azSrc.D) } : {}),
    };
    const azSectors = Object.keys(azObj);

    return (
      <div className="smrtcop-page">
        <div className="smrtcop-ingest-fullscreen">
          {/* LEFT — viewer panel (desktop only; mobile crew skip document review) */}
          {!mobileMode && (
            <div className="smrtcop-viewer">
              {docs.length > 0 && (
                <div className="smrtcop-thumb-strip">
                  {docs.map((f, i) => {
                    const label =
                      f.docLabel ||
                      (f.docKey ? CREW_DOC_FALLBACK_LABELS[f.docKey] : "") ||
                      "Document";
                    return (
                      <div
                        key={i}
                        className={`smrtcop-thumb-item${i === viewDocIndex ? " active" : ""}`}
                        onClick={() => setViewDocIndex(i)}
                      >
                        <div className="smrtcop-thumb-content">
                          <div className="smrtcop-thumb-title">{label}</div>
                          <span className="smrtcop-thumb-name" title={f.name}>
                            {f.name}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="smrtcop-main-preview">
                {activeDoc && viewPreviewUrl ? (
                  activeDoc.type === "application/pdf" ? (
                    <iframe src={viewPreviewUrl} title={activeDoc.name} style={{ width: "100%", height: "100%", border: "none" }} />
                  ) : activeDoc.type?.startsWith("image/") ? (
                    <img src={viewPreviewUrl} alt={activeDoc.name} style={{ maxWidth: "100%", maxHeight: "100%" }} />
                  ) : (
                    <div className="smrtcop-viewer-empty">
                      <span>{activeDoc.name}</span>
                      <span style={{ fontSize: "12px", color: "#555" }}>{t("Preview not supported")}</span>
                    </div>
                  )
                ) : activeDoc ? (
                  <div className="smrtcop-viewer-empty">
                    <span style={{ fontSize: "1rem" }}>📄 {activeDoc.name}</span>
                    <span style={{ fontSize: "12px", color: "#555", marginTop: 4 }}>{t("Loading preview…")}</span>
                  </div>
                ) : (
                  <div className="smrtcop-viewer-empty">{t("No documents")}</div>
                )}
              </div>
            </div>
          )}

          {/* RIGHT — site info (all disabled) */}
          <div className="smrtcop-review-form">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              {/* EN | ES segmented toggle — same style as the wizard's. */}
              <div
                style={{
                  display: "inline-flex",
                  height: 28,
                  borderRadius: 6,
                  overflow: "hidden",
                  border: "1px solid #2a2a2a",
                  fontSize: "0.75rem",
                }}
              >
                {(["en", "es"] as const).map((code) => {
                  const active = wizardLang === code;
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setWizardLang(code)}
                      style={{
                        padding: "0 10px",
                        height: "100%",
                        background: active ? "#007bff" : "#1c1c1c",
                        color: active ? "#fff" : "#9aa0a6",
                        border: "none",
                        cursor: "pointer",
                        fontWeight: active ? 700 : 500,
                        fontFamily: "inherit",
                      }}
                    >
                      {code.toUpperCase()}
                    </button>
                  );
                })}
              </div>
              <button
                className="smrtcop-btn-action smrtcop-btn-cancel"
                style={{ flex: "none", width: "auto", padding: "0 12px", height: 28, fontSize: "0.78rem" }}
                onClick={() => {
                  setViewingSite(null);
                  setViewDocIndex(0);
                  setShowScopeModal(false);
                  setShowBomModal(false);
                }}
              >
                {t("Back")}
              </button>
            </div>
            <div className="smrtcop-review-form-grid">
              {/* Site ID | FA Location Code */}
              <div className="smrtcop-field">
                <label className="smrtcop-field-label">{t("Site ID")}</label>
                <input
                  className="smrtcop-input"
                  value={viewingSite.meta.siteId || ""}
                  disabled
                />
              </div>
              <div className="smrtcop-field">
                <label className="smrtcop-field-label">{t("FA Location Code")}</label>
                <input
                  className="smrtcop-input"
                  value={viewingSite.meta.faLocationCode || ""}
                  disabled
                />
              </div>

              {/* Physical Address — full width. On mobile use a 2-row textarea
                  so long addresses wrap instead of getting clipped (an <input>
                  is HTML-spec single-line and cannot wrap). */}
              <div className="smrtcop-field smrtcop-field-span">
                <label className="smrtcop-field-label">{t("Physical Address")}</label>
                {mobileMode ? (
                  <textarea
                    className="smrtcop-input"
                    value={viewingSite.meta.address || ""}
                    disabled
                    rows={2}
                    style={{ resize: "none" }}
                  />
                ) : (
                  <input
                    className="smrtcop-input"
                    value={viewingSite.meta.address || ""}
                    disabled
                  />
                )}
              </div>

              {/* Co-ordinates — full width */}
              <div className="smrtcop-field smrtcop-field-span">
                <label className="smrtcop-field-label">{t("Co-ordinates (Lat/Long)")}</label>
                <input
                  className="smrtcop-input"
                  value={viewingSite.meta.coordinates || ""}
                  disabled
                />
              </div>

              {/* Azimuths — read-only boxes (Delta shown when captured) */}
              <div className="smrtcop-field smrtcop-field-span">
                <label className="smrtcop-field-label">{t("Azimuths")}</label>
                <div className="smrtcop-azimuth-row">
                  {azSectors.map((sector) => (
                    <div key={sector} className="smrtcop-azimuth-cell">
                      <div className="smrtcop-azimuth-sublabel">{t("Sector")} {sector}</div>
                      <input
                        className="smrtcop-input"
                        value={azObj[sector]}
                        disabled
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Scope of Work button — opens modal. Parent .smrtcop-field has
                  flex-direction: column, so the cross-axis is horizontal —
                  use alignItems (not justifyContent) to center the button. */}
              <div
                className="smrtcop-field smrtcop-field-span"
                style={{ marginTop: 20, display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="smrtcop-btn-action"
                  style={{
                    flex: "none",
                    width: 180,
                    height: 40,
                    minHeight: 40,
                    fontSize: "0.85rem",
                    padding: "0 14px",
                    background: "#007bff",
                    color: "#ffffff",
                    lineHeight: 1.2,
                  }}
                  onClick={() => setShowScopeModal(true)}
                >
                  {t("View Scope of Work")}
                </button>
              </div>

              {/* View BOM button — opens read-only Final BOM modal. Same
                  shape/size as View Scope of Work, yellow styling. */}
              <div
                className="smrtcop-field smrtcop-field-span"
                style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="smrtcop-btn-action"
                  style={{
                    flex: "none",
                    width: 180,
                    height: 40,
                    minHeight: 40,
                    fontSize: "0.85rem",
                    padding: "0 14px",
                    background: "#d4a017",
                    color: "#ffffff",
                    lineHeight: 1.2,
                  }}
                  onClick={() => {
                    setShowBomModal(true);
                    if (bomViewStatus === "loaded" || bomViewStatus === "loading") return;
                    setBomViewStatus("loading");
                    fetch(
                      `${process.env.REACT_APP_BACKEND_URL}/smrtcop/bom/${encodeURIComponent(viewingSite.id)}`,
                      { credentials: "include" },
                    )
                      .then((r) => r.json())
                      .then((data) => {
                        if (data?.missing) {
                          setBomViewRows([]);
                          setBomViewStatus("missing");
                          return;
                        }
                        const rows: BomRow[] = (data?.rows || []).map(
                          (r: { item?: string; description?: string; uom?: string; qty?: string }) => ({
                            item: r.item ?? "",
                            description: r.description ?? "",
                            uom: r.uom ?? "",
                            plannedQty: r.qty ?? "",
                            actualInstalled: "",
                          }),
                        );
                        setBomViewRows(rows);
                        setBomViewStatus(rows.length > 0 ? "loaded" : "missing");
                      })
                      .catch((err) => {
                        console.error("Failed to load BOM:", err);
                        setBomViewStatus("error");
                      });
                  }}
                >
                  {t("View BOM")}
                </button>
              </div>

              {/* Verify Photo's button — for v2 checklists it opens the A/B/C
                  N/A exclusion grid; for legacy checklists it keeps the
                  verification-line photo editor. Green, to differentiate from
                  blue Scope and yellow BOM. */}
              <div
                className="smrtcop-field smrtcop-field-span"
                style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="smrtcop-btn-action"
                  style={{
                    flex: "none",
                    width: 180,
                    height: 40,
                    minHeight: 40,
                    fontSize: "0.85rem",
                    padding: "0 14px",
                    background: "#1faa59",
                    color: "#ffffff",
                    lineHeight: 1.2,
                  }}
                  onClick={() => {
                    setShowVerifyModal(true);
                    if (verifyModalStatus === "loading") return;
                    setVerifyModalStatus("loading");
                    Promise.all([
                      fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/smrtcop/checklist/${encodeURIComponent(viewingSite.id)}`,
                        { credentials: "include" },
                      ).then((r) => r.json()),
                      fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/smrtcop/site-state/${encodeURIComponent(viewingSite.id)}`,
                        { credentials: "include" },
                      ).then((r) => r.json()),
                    ])
                      .then(([checklistData, stateData]) => {
                        const fmt: ChecklistFormat =
                          checklistData?.format === "v2" ? "v2" : "legacy";
                        setVerifyModalFormat(fmt);
                        if (checklistData?.missing) {
                          setVerifyModalItems([]);
                          setVerifyModalDraft({});
                          setNaModalCategories([]);
                          setNaModalDraft({});
                          setVerifyModalStatus("missing");
                          return;
                        }
                        const items: ChecklistPhotoItem[] =
                          (checklistData?.items || []).map(mapChecklistRow);
                        if (fmt === "v2") {
                          // Build the deduped category rows and seed each
                          // (category, sector) box: a saved override wins,
                          // otherwise fall back to the Excel status (NA → off).
                          const { categories, statusByKey } = v2Categories(items);
                          const saved = sanitizeNaOverrides(stateData?.naOverrides);
                          const draft: NaOverridesMap = {};
                          for (const cat of categories) {
                            for (const sec of ["A", "B", "C"] as const) {
                              const k = naKey(cat.categoryId, sec);
                              draft[k] =
                                k in saved ? saved[k] : statusByKey[k] === "NA";
                            }
                          }
                          setNaModalCategories(categories);
                          setNaModalDraft(draft);
                          setVerifyModalStatus(
                            categories.length > 0 ? "loaded" : "missing",
                          );
                          return;
                        }
                        const verificationRows: ChecklistPhotoItem[] =
                          (checklistData?.verificationItems || []).map(mapChecklistRow);
                        const collected = collectVerificationItems(items, verificationRows);
                        setVerifyModalItems(collected);
                        setVerifyModalDraft(sanitizeVerifyOverrides(stateData?.verifyOverrides));
                        setVerifyModalStatus(collected.length > 0 ? "loaded" : "missing");
                      })
                      .catch((err) => {
                        console.error("Failed to load Verify setup:", err);
                        setVerifyModalStatus("error");
                      });
                  }}
                >
                  {t("Verify Photo's")}
                </button>
              </div>
            </div>

          </div>
        </div>

        {/* ── Scope of Work modal ── */}
        {showScopeModal && (
          <div className="smrtdocs-modal-overlay">
            <div
              className="smrtdocs-modal"
              style={{ width: 600, maxHeight: "80vh", display: "flex", flexDirection: "column" }}
            >
              <div className="smrtdocs-modal-header">
                <h2 style={{ margin: 0, fontSize: "1rem" }}>{t("Scope of Work")}</h2>
                <button
                  className="btn-cancel"
                  onClick={() => setShowScopeModal(false)}
                >
                  {t("Close")}
                </button>
              </div>
              <div
                className="smrtdocs-modal-body"
                style={{
                  overflowY: "auto",
                  flex: 1,
                  // Stop iOS from rubber-banding into the page underneath when
                  // scroll hits the modal's bottom — without this the parent
                  // .content-container scrolls along with the modal.
                  overscrollBehavior: "contain",
                  WebkitOverflowScrolling: "touch",
                  paddingBottom: 24,
                }}
              >
                {viewingSite.meta.scopeOfWork && viewingSite.meta.scopeOfWork.trim() ? (
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontFamily: "inherit",
                      color: "#fff",
                      fontSize: "0.9rem",
                      margin: 0,
                      lineHeight: 1.5,
                    }}
                  >
                    {highlightScopeKeywords(viewingSite.meta.scopeOfWork)}
                  </pre>
                ) : (
                  <div style={{ color: "#9aa0a6", fontSize: "0.9rem", textAlign: "center", padding: "20px 0" }}>
                    {t("No scope of work available for this site.")}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Final BOM modal (read-only) ── */}
        {showBomModal && (
          <div className="smrtdocs-modal-overlay">
            <div
              className="smrtdocs-modal"
              style={{ width: 720, maxHeight: "80vh", display: "flex", flexDirection: "column" }}
            >
              <div className="smrtdocs-modal-header">
                <h2 style={{ margin: 0, fontSize: "1rem" }}>{t("Final BOM")}</h2>
                <button
                  className="btn-cancel"
                  onClick={() => setShowBomModal(false)}
                >
                  {t("Close")}
                </button>
              </div>
              <div
                className="smrtdocs-modal-body"
                style={{
                  overflowY: "auto",
                  flex: 1,
                  overscrollBehavior: "contain",
                  WebkitOverflowScrolling: "touch",
                  paddingBottom: 24,
                }}
              >
                {bomViewStatus === "loading" ? (
                  <div style={{ color: "#9aa0a6", fontSize: "0.9rem", textAlign: "center", padding: "20px 0" }}>
                    {t("Loading BOM…")}
                  </div>
                ) : bomViewStatus === "missing" ? (
                  <div style={{ color: "#d4a017", fontSize: "0.9rem", textAlign: "center", padding: "20px 0" }}>
                    {t("Final BOM file not uploaded for this site")}
                  </div>
                ) : bomViewStatus === "error" ? (
                  <div style={{ color: "#c62828", fontSize: "0.9rem", textAlign: "center", padding: "20px 0" }}>
                    {t("Failed to load Final BOM. Try again later.")}
                  </div>
                ) : (
                  <table className="smrtcop-bom-table">
                    <thead>
                      <tr>
                        <th>{t("Item")}</th>
                        <th>{t("Description")}</th>
                        <th>{t("UOM")}</th>
                        <th>{t("Qty")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bomViewRows.map((r, i) => (
                        <tr key={i}>
                          <td>{r.item}</td>
                          <td>{r.description}</td>
                          <td>{r.uom}</td>
                          <td>{r.plannedQty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Verify Setup modal (editable) ── */}
        {showVerifyModal && (
          <div className="smrtdocs-modal-overlay">
            <div
              className="smrtdocs-modal"
              style={{ width: 720, maxHeight: "85vh", display: "flex", flexDirection: "column" }}
            >
              <div className="smrtdocs-modal-header">
                <h2 style={{ margin: 0, fontSize: "1rem" }}>
                  {t(verifyModalFormat === "v2" ? "Verify Photo's" : "Verify Setup")}
                </h2>
                <button
                  className="btn-cancel"
                  onClick={() => setShowVerifyModal(false)}
                >
                  {t("Close")}
                </button>
              </div>
              <div
                className="smrtdocs-modal-body"
                style={{
                  overflowY: "auto",
                  flex: 1,
                  overscrollBehavior: "contain",
                  WebkitOverflowScrolling: "touch",
                  paddingBottom: 12,
                }}
              >
                {verifyModalStatus === "loading" ? (
                  <div style={{ color: "#9aa0a6", fontSize: "0.9rem", textAlign: "center", padding: "20px 0" }}>
                    {t("Loading Verify items…")}
                  </div>
                ) : verifyModalStatus === "missing" ? (
                  <div style={{ color: "#d4a017", fontSize: "0.9rem", textAlign: "center", padding: "20px 0" }}>
                    {t(
                      verifyModalFormat === "v2"
                        ? "No photo categories found for this site"
                        : "No verification items found for this site",
                    )}
                  </div>
                ) : verifyModalStatus === "error" ? (
                  <div style={{ color: "#c62828", fontSize: "0.9rem", textAlign: "center", padding: "20px 0" }}>
                    {t("Failed to load Verify setup. Try again later.")}
                  </div>
                ) : verifyModalFormat === "v2" ? (
                  // ── v2: 30-category × A/B/C exclusion grid. Ticked = included
                  // in that sector's wizard step; untick to flag it N/A. ──
                  <div>
                    <div
                      style={{
                        color: "#9aa0a6",
                        fontSize: "0.8rem",
                        lineHeight: 1.45,
                        marginBottom: 10,
                      }}
                    >
                      {t(
                        "Tick the sectors (A / B / C) that need this photo. Untick to mark it N/A — it won't appear in the wizard.",
                      )}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 40px 40px 40px",
                        alignItems: "center",
                        columnGap: 6,
                        rowGap: 2,
                      }}
                    >
                      <div />
                      {(["A", "B", "C"] as const).map((sec) => (
                        <div
                          key={`hdr_${sec}`}
                          style={{
                            color: "#9aa0a6",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            textAlign: "center",
                          }}
                        >
                          {sec}
                        </div>
                      ))}
                      {naModalCategories.map((cat) => (
                        <React.Fragment key={cat.categoryId}>
                          <div
                            style={{
                              color: "#cfcfcf",
                              fontSize: "0.83rem",
                              lineHeight: 1.35,
                              padding: "6px 0",
                              borderTop: "1px solid #222",
                            }}
                          >
                            {t(cat.label)}
                          </div>
                          {(["A", "B", "C"] as const).map((sec) => {
                            const k = naKey(cat.categoryId, sec);
                            const included = !naModalDraft[k];
                            return (
                              <div
                                key={k}
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  borderTop: "1px solid #222",
                                  padding: "6px 0",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={included}
                                  onChange={(e) => {
                                    const inc = e.target.checked;
                                    setNaModalDraft((prev) => ({
                                      ...prev,
                                      [k]: !inc, // stored value = excluded
                                    }));
                                  }}
                                  style={{
                                    accentColor: "#1faa59",
                                    transform: "scale(1.15)",
                                    cursor: "pointer",
                                  }}
                                />
                              </div>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {verifyModalItems.map((item) => {
                      const rowId = verifyItemId(item.sector, item.label || "", item.description);
                      const displayTitle = verifyRowTitle(item);
                      const atomicLines = verifyAtomicLines(item);
                      return (
                        <div
                          key={rowId}
                          style={{
                            background: "#1a1a1a",
                            border: "1px solid #2a2a2a",
                            borderRadius: 6,
                            padding: "10px 12px",
                          }}
                        >
                          <div style={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem", marginBottom: 8 }}>
                            {t(displayTitle)}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            {atomicLines.map((line, i) => {
                              const lineId = verifyLineId(rowId, i);
                              const checked = verifyModalDraft[lineId] === true;
                              const letter = String.fromCharCode(65 + i);
                              return (
                                <label
                                  key={lineId}
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 8,
                                    fontSize: "0.85rem",
                                    color: "#cfcfcf",
                                    lineHeight: 1.45,
                                    cursor: "pointer",
                                    padding: "2px 0",
                                  }}
                                  title={t("Photo required for this item")}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      const next = e.target.checked;
                                      setVerifyModalDraft((prev) => {
                                        const out = { ...prev };
                                        if (next) out[lineId] = true;
                                        else delete out[lineId];
                                        return out;
                                      });
                                    }}
                                    style={{
                                      accentColor: "#1faa59",
                                      transform: "scale(1.1)",
                                      cursor: "pointer",
                                      marginTop: 2,
                                    }}
                                  />
                                  <span style={{ color: "#9aa0a6", minWidth: 18 }}>
                                    {letter}.
                                  </span>
                                  <span style={{ flex: 1 }}>{t(line)}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  padding: "10px 14px",
                  borderTop: "1px solid #2a2a2a",
                }}
              >
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowVerifyModal(false)}
                  disabled={verifyModalSaving}
                >
                  {t("Cancel")}
                </button>
                <button
                  type="button"
                  className="smrtcop-btn-action"
                  style={{
                    flex: "none",
                    width: "auto",
                    padding: "0 16px",
                    height: 32,
                    fontSize: "0.85rem",
                    background: "#1faa59",
                    color: "#fff",
                  }}
                  disabled={
                    readOnly ||
                    verifyModalSaving ||
                    verifyModalStatus !== "loaded"
                  }
                  onClick={() => {
                    if (!viewingSite) return;
                    setVerifyModalSaving(true);
                    const isV2 = verifyModalFormat === "v2";
                    const body = isV2
                      ? { naOverrides: naModalDraft }
                      : { verifyOverrides: verifyModalDraft };
                    fetch(
                      `${process.env.REACT_APP_BACKEND_URL}/smrtcop/site-state/${encodeURIComponent(viewingSite.id)}`,
                      {
                        method: "PUT",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(body),
                      },
                    )
                      .then((r) => {
                        if (!r.ok) throw new Error(`PUT site-state failed: ${r.status}`);
                        // If the wizard is currently loaded for this same site,
                        // mirror the draft into the runtime map so the change
                        // reflects immediately without a wizard reopen.
                        if (activeSite?.id === viewingSite.id) {
                          if (isV2) setNaOverrides(naModalDraft);
                          else setVerifyOverrides(verifyModalDraft);
                        }
                        setShowVerifyModal(false);
                      })
                      .catch((err) => {
                        console.error("Failed to save Verify setup:", err);
                        alert(t("Failed to save Verify setup. Try again."));
                      })
                      .finally(() => {
                        setVerifyModalSaving(false);
                      });
                  }}
                >
                  {verifyModalSaving ? t("Saving…") : t("Save")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Wizard field renderer ────────────────────────────────────────────────────
  // Photo slots — both the photo-question case in renderWizardField AND
  // the Verify step's per-line tile — go through renderWizardField's
  // "photo" case using either the real question or a synthetic one.
  // The old renderPhotoSlot helper has been removed; the row-style
  // layout in renderWizardField is now the single source of truth.

  const renderWizardField = (q: COPQuestion) => {
    const labelStyle: React.CSSProperties = {
      fontSize: "0.75rem",
      color: "#9aa0a6",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      marginBottom: 4,
    };

    const lbl = (
      <div style={labelStyle}>
        {t(q.label)}
        {q.required ? " *" : ""}
      </div>
    );

    switch (q.type) {
      case "heading": {
        // When the heading carries atomic sub-instructions (Verification step),
        // render the title followed by a structured list. Otherwise fall back
        // to the simple bold divider.
        const headingSubItems = Array.isArray(q.subInstructions)
          ? q.subInstructions.filter(Boolean)
          : [];
        if (headingSubItems.length === 0) {
          return (
            <div style={{ fontWeight: 700, fontSize: "1rem", color: "#fff", paddingTop: 6, borderBottom: "1px solid #2a2a2a", paddingBottom: 4 }}>
              {t(q.label)}
            </div>
          );
        }
        return (
          <div
            style={{
              background: "#1a1a1a",
              border: "1px solid #2a2a2a",
              borderRadius: 6,
              padding: "10px 12px",
              fontSize: "0.88rem",
              color: "#cfcfcf",
              lineHeight: 1.5,
            }}
          >
            <div style={{ color: "#fff", fontWeight: 600, marginBottom: 8 }}>
              {t(q.label)}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {headingSubItems.map((sub, i) => (
                <div key={i}>
                  <span style={{ color: "#9aa0a6", marginRight: 6 }}>
                    {String.fromCharCode(65 + i)}.
                  </span>
                  {t(sub)}
                </div>
              ))}
            </div>
          </div>
        );
      }

      case "textarea":
        return (
          <div>
            {lbl}
            <textarea
              className="smrtcop-input"
              placeholder={t(q.helper) || ""}
              value={formData[q.key] ?? ""}
              onChange={(e) => updateField(q.key, e.target.value)}
              style={{ height: 80, padding: "8px 10px", resize: "vertical", width: "100%", boxSizing: "border-box" }}
            />
          </div>
        );

      case "date":
        return (
          <div>
            {lbl}
            <input
              className="smrtcop-input"
              type="date"
              value={formData[q.key] ?? ""}
              onChange={(e) => updateField(q.key, e.target.value)}
            />
          </div>
        );

      case "number":
        return (
          <div>
            {lbl}
            <input
              className="smrtcop-input"
              type="number"
              placeholder={t(q.helper) || ""}
              value={formData[q.key] ?? ""}
              onChange={(e) => updateField(q.key, e.target.value)}
            />
          </div>
        );

      case "select": {
        // Tape drop "Model/Technology": the options are the site's OWN
        // antennas, read from the RFDS, not a static list on the template.
        // Each of the sector's antenna columns becomes one option —
        // "NNHH-65B-R4/2100/700/850/AWS3/LTE+5G" — built from its Model row
        // and its Linked Cells (see antennaDescriptors.ts for the AT&T band /
        // tech key those cell names decode against). The crew picks the
        // antenna they are taping.
        //
        // Falls back to whatever the template carries when the site predates
        // RFDS antenna ingestion or that sector yielded no rows, so an older
        // site keeps working rather than showing an empty dropdown.
        const isModelTech =
          (q.label || "").trim().toLowerCase() === "model/technology";
        const rfdsOptions = isModelTech
          ? antennaOptionsForSector(
              activeSite?.meta?.antennaPositionsBySector,
              sectorByQuestionKey[q.key],
            )
          : [];
        const options = rfdsOptions.length
          ? rfdsOptions
          : (q.options?.filter(Boolean) ?? []);
        // Viewer: a disabled <select> hides its options behind a click the
        // admin can't make, and a child cannot opt out of the step's disabled
        // <fieldset>. So show the choice AND what was on offer — which is what
        // you actually want when checking whether the RFDS-derived
        // Model/Technology list came through correctly.
        if (readOnly) {
          const chosen = (formData[q.key] as string) || "";
          return (
            <div>
              {lbl}
              <div
                className="smrtcop-input"
                style={{
                  minHeight: 36,
                  display: "flex",
                  alignItems: "center",
                  color: chosen ? "#fff" : "#777",
                }}
              >
                {chosen || t("— Not selected —")}
              </div>
              {options.length > 0 && (
                <div style={{ marginTop: 4, paddingLeft: 2 }}>
                  <div
                    style={{
                      fontSize: "0.68rem",
                      color: "#777",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      marginBottom: 2,
                    }}
                  >
                    {options.length}{" "}
                    {options.length === 1 ? t("option") : t("options")}
                  </div>
                  {options.map((o) => (
                    <div
                      key={o}
                      style={{
                        fontSize: "0.76rem",
                        lineHeight: 1.5,
                        color: o === chosen ? "#1faa59" : "#9aa0a6",
                      }}
                    >
                      {o === chosen ? "• " : "  "}
                      {t(o)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }
        return (
          <div>
            {lbl}
            <select
              className="smrtcop-input"
              value={formData[q.key] ?? ""}
              onChange={(e) => updateField(q.key, e.target.value)}
              style={{ height: 36 }}
            >
              <option value="">{t("— Select —")}</option>
              {options.map((o) => (
                <option key={o} value={o}>{t(o)}</option>
              ))}
            </select>
          </div>
        );
      }

      case "checkbox": {
        const opts = q.options?.filter(Boolean).length ? q.options! : ["Yes"];
        const checked: string[] = formData[q.key] || [];
        return (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 4 }}>
              {opts.map((o) => (
                <input
                  key={o}
                  type="checkbox"
                  checked={checked.includes(o)}
                  onChange={() => {
                    const next = checked.includes(o) ? checked.filter((v) => v !== o) : [...checked, o];
                    updateField(q.key, next);
                  }}
                  style={{ accentColor: "#007bff", transform: "scale(1.4)", cursor: "pointer" }}
                />
              ))}
              {q.helper && (
                <span style={{ fontSize: "0.95rem", color: "#fff" }}>{t(q.helper)}</span>
              )}
            </div>
          </div>
        );
      }

      case "photo": {
        const fileEntry = fileRefs.current[q.key];
        // Preview precedence: this session's live capture → a preview already
        // fetched from blob → legacy inline base64 (older sites only).
        const persistedDataUrl = formData[`${q.key}_dataUrl`];
        const blobPreview = photoBlobPreviews.current[q.key];
        const previewUrl = fileEntry?.url || blobPreview || persistedDataUrl || null;
        // Live upload status from the durable queue (undefined once uploaded).
        const slotUploadStatus = photoUploadStatus[q.key];
        const hasFile = !!previewUrl;
        // Extras carry "__extra__" in their key — used to switch the
        // delete behaviour (extras: drop the row entirely; base slots:
        // clear the photo, keep the row for retake).
        const isExtra = q.key.includes("__extra__");

        // Default display name when the user hasn't renamed the photo yet.
        // Pattern matches the spec: {GroupLabel}_{index}_{SiteID}.jpg.
        // Spaces and "_" preserved in the group label so admins downloading
        // see the full readable name. The blob's underlying name in Azure
        // stays on the legacy {siteId}_{label}_{index}.jpg pattern; the
        // user-visible string here is what flows into formData and ends up
        // as the file's saved name when admin downloads.
        const buildFilename = (): string => {
          const siteId = activeSite?.meta?.siteId || "site";
          let sourceLabel = expandGroupLabel(
            q.groupLabel || q.label || "Photo",
          );
          const index =
            typeof q.groupIndex === "number" ? q.groupIndex + 1 : 1;
          // SPD slots used to render side-by-side (SPD 1 / SPD 2) so the
          // group label stayed "SPD 1" everywhere. Now that rows stack,
          // each row represents the next SPD instance — re-number any
          // "SPD N" inside the label to match the row index so default
          // filenames read SPD 1 / SPD 2 / SPD 3 etc.
          if (!q.fixedLabel) {
            sourceLabel = sourceLabel.replace(/SPD\s*\d+/i, `SPD ${index}`);
          }
          return `${sourceLabel}_${index}_${siteId}.jpg`;
        };

        // Current display name = whatever's in formData if set (covers
        // both initial upload and user rename), else the auto-generated
        // default so empty rows still show the eventual filename.
        const currentFilename =
          (formData[q.key] as string | undefined) || buildFilename();

        const handleCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
          const f = e.target.files?.[0];
          // Reset the input so selecting the same file again still fires change.
          e.target.value = "";
          if (!f) return;
          // Preserve the user's previously chosen rename if they're
          // re-taking after editing — only auto-generate if no name yet.
          const targetName =
            (formData[q.key] as string | undefined) || buildFilename();

          // Compress before anything else: downscale to 2000px / 0.80 JPEG so
          // a 50-70-photo site stays small and uploads reliably on weak field
          // connections. HEIC can't be processed in-browser — ask for a
          // retake rather than store a giant unviewable original. The util
          // returns a File already named targetName with a .jpg extension.
          let renamed: File;
          try {
            renamed = await compressImage(f, { fileName: targetName });
          } catch (err) {
            if (err instanceof ImageCompressionError && err.reason === "heic") {
              alert(
                t(
                  "This photo is in HEIC format and can't be processed here. Please retake or choose a JPEG.",
                ),
              );
            } else {
              console.error("Photo compression failed:", err);
              alert(t("Couldn't process this photo. Please retake."));
            }
            return;
          }

          if (fileEntry) URL.revokeObjectURL(fileEntry.url);
          const url = URL.createObjectURL(renamed);
          fileRefs.current[q.key] = { file: renamed, url };
          // A stale blob preview from a prior capture in this slot is now
          // superseded by the live object URL.
          if (photoBlobPreviews.current[q.key]) {
            URL.revokeObjectURL(photoBlobPreviews.current[q.key]);
            delete photoBlobPreviews.current[q.key];
          }

          const site = activeSite;

          // Filename shows immediately (optimistic); the upload is handed to
          // the durable queue, which persists the blob in IndexedDB, uploads
          // it with retry/backoff, and writes the `${key}_blob` reference back
          // via onPhotoUploaded once it lands. The blob survives backgrounding,
          // crashes, and offline gaps, so capture never blocks and is never
          // lost.
          setFormData((prev) => ({ ...prev, [q.key]: renamed.name }));
          clearRejection(q.key);

          if (!site) return; // no site context — keep local preview + name only

          copPhotoQueue
            .enqueue({
              localId: copPhotoQueue.newLocalId(),
              siteId: site.id,
              fieldKey: q.key,
              fileName: renamed.name,
              blob: renamed,
              attempts: 0,
              createdAt: Date.now(),
            })
            .catch((err) => console.error("Failed to queue photo:", err));
        };

        const handleRemove = () => {
    // Read-only viewer: never mutate the crew's files. A disabled <fieldset>
    // covers form controls but not click handlers on styled divs, so the
    // destructive handlers guard themselves.
    if (readOnly) return;
          if (fileEntry) {
            URL.revokeObjectURL(fileEntry.url);
            delete fileRefs.current[q.key];
          }
          if (photoBlobPreviews.current[q.key]) {
            URL.revokeObjectURL(photoBlobPreviews.current[q.key]);
            delete photoBlobPreviews.current[q.key];
          }
          // Drop any pending upload for this slot so a removed/retaken photo
          // isn't still queued.
          if (activeSite) {
            void copPhotoQueue.removePending(activeSite.id, q.key);
          }
          // Best-effort delete of the uploaded blob so removed photos don't
          // linger in storage.
          const removedBlob = formData[`${q.key}_blob`] as string | undefined;
          if (activeSite && removedBlob) {
            fetch(
              `${process.env.REACT_APP_BACKEND_URL}/smrtcop/files/${encodeURIComponent(activeSite.id)}/${encodeURIComponent(q.key)}?blobName=${encodeURIComponent(removedBlob)}`,
              { method: "DELETE", credentials: "include" },
            ).catch((err) => console.warn("Photo blob delete failed:", err));
          }
          updateField(q.key, null);
          updateField(`${q.key}_blob`, null);
          updateField(`${q.key}_dataUrl`, null);
          // Clear the validation sidecars with the photo. Left behind, a
          // `_status: "fail"` outlives the slot it describes and keeps blocking
          // the step — and for a crew-added extra the slot disappears from the
          // UI entirely, so there is nothing left to retake or override.
          updateField(`${q.key}_status`, null);
          updateField(`${q.key}_statusReason`, null);
          updateField(`${q.key}_azResult`, null);
          updateField(`${q.key}_azBlob`, null);
          updateField(`${q.key}_tapeResult`, null);
          updateField(`${q.key}_tapeBlob`, null);
          updateField(`${q.key}_tapeOverride`, null);
          updateField(`${q.key}_serial`, null);
          updateField(`${q.key}_serialBlob`, null);
          if (isExtra) {
            // Pull this extra out of the parent group's extras list so it
            // disappears from the UI on next render.
            const extrasListKey = `__extras::${q.groupKey}`;
            const id = q.key.split("__extra__").pop();
            const list = Array.isArray(formData[extrasListKey])
              ? (formData[extrasListKey] as string[])
              : [];
            updateField(
              extrasListKey,
              list.filter((x) => x !== id),
            );
          }
          setOpenPhotoMenuKey(null);
        };

        const openRename = () => {
          // Pre-fill the input with the bare name (no extension) so the
          // user can edit cleanly; the modal save logic re-attaches the ext.
          setRenamingValue(currentFilename.replace(/\.[^.]+$/, ""));
          setRenamingKey(q.key);
          setOpenPhotoMenuKey(null);
        };

        const handleView = () => {
          if (typeof previewUrl !== "string") return;
          setPhotoLightboxUrl(previewUrl);
          setOpenPhotoMenuKey(null);
        };

        // Hidden file inputs triggered by the camera / upload buttons via
        // refs — using buttons (not labels) gives us native disabled state
        // for the "photo already taken" case and keeps vertical alignment
        // consistent with the other action buttons in the row.
        const camInputRef = (el: HTMLInputElement | null) => {
          camInputRefs.current[q.key] = el;
        };
        const upInputRef = (el: HTMLInputElement | null) => {
          upInputRefs.current[q.key] = el;
        };
        const triggerCam = () => camInputRefs.current[q.key]?.click();
        const triggerUp = () => upInputRefs.current[q.key]?.click();

        const isMenuOpen = openPhotoMenuKey === q.key;

        // FCC Registration photos carry an OCR'd registration number, editable
        // below the row. Show "Scanning…" in the box while the sign OCR runs.
        const isFcc = isFccRegPhoto(q);
        const fccScanning =
          isFcc &&
          !!formData[`${q.key}_blob`] &&
          formData[`${q.key}_fccBlob`] !== formData[`${q.key}_blob`];

        return (
          <>
          <div className={`smrtcop-photo-row${hasFile ? " filled" : ""}`}>
            {renderUploadBadge(
              uploadStatus(q.key, hasFile),
              uploadStatusReason(q.key),
              // Tape Drop fails are a soft warning: tapping the badge reopens
              // the Retake/Override prompt so the crew can pass it manually.
              TAPE_DROP_SLOT_RE.test(q.key) && !formData[`${q.key}_tapeOverride`]
                ? () => setTapeWarn({ key: q.key })
                : undefined,
            )}
            <span
              className="smrtcop-photo-row-name"
              title={currentFilename}
            >
              {currentFilename}
              {q.required && !hasFile ? " *" : ""}
              {/* Whenever the badge is yellow (uploading, scan window, or RRH
                  OCR) show the "Scanning…" beat — it reads as the AI at work.
                  A weak-signal queue keeps its own honest wording. */}
              {(!!slotUploadStatus || uploadStatus(q.key, hasFile) === "pending") && (
                <span
                  style={{
                    marginLeft: 6,
                    fontSize: "0.7rem",
                    color: "#d4a017",
                  }}
                >
                  {slotUploadStatus === "waiting"
                    ? t("Waiting for signal…")
                    : slotUploadStatus === "queued"
                      ? t("Queued…")
                      : t("Scanning…")}
                </span>
              )}
            </span>
            <span className="smrtcop-photo-row-actions">
              <input
                ref={camInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                onChange={handleCapture}
              />
              <input
                ref={upInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleCapture}
              />
              <button
                type="button"
                className="smrtcop-photo-row-btn smrtcop-photo-row-cam"
                onClick={triggerCam}
                disabled={hasFile}
                title={t("Take photo")}
              >
                📷
              </button>
              <button
                type="button"
                className="smrtcop-photo-row-btn smrtcop-photo-row-up"
                onClick={triggerUp}
                disabled={hasFile}
                title={t("Upload from photos")}
              >
                ⤴
              </button>
              {/* When the menu is open the View / Edit / Delete cluster
                  renders BEFORE the ⋮ trigger so the trigger stays at the
                  right end of the row — same behaviour SMRT Tasks uses. */}
              {isMenuOpen && (
                <span className="smrtcop-photo-row-menu" role="menu">
                  <button
                    type="button"
                    className="smrtcop-photo-row-btn smrtcop-photo-row-view"
                    onClick={handleView}
                    disabled={!hasFile}
                    title={t("View")}
                    role="menuitem"
                  >
                    👁
                  </button>
                  <button
                    type="button"
                    className="smrtcop-photo-row-btn smrtcop-photo-row-edit"
                    onClick={openRename}
                    title={t("Rename")}
                    role="menuitem"
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    className="smrtcop-photo-row-btn smrtcop-photo-row-del"
                    onClick={handleRemove}
                    disabled={!hasFile && !isExtra}
                    title={isExtra ? t("Remove this photo slot") : t("Clear photo")}
                    role="menuitem"
                  >
                    🗑
                  </button>
                </span>
              )}
              <button
                type="button"
                className="smrtcop-photo-row-btn smrtcop-photo-row-more"
                onClick={(e) => {
                  e.stopPropagation();
                  togglePhotoMenu(q.key);
                }}
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
                title={isMenuOpen ? t("Hide actions") : t("More actions")}
              >
                ⋮
              </button>
            </span>
          </div>
          {isFcc && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 6,
                marginLeft: 26,
              }}
            >
              <span
                style={{
                  fontSize: "0.72rem",
                  color: "#9aa0a6",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  whiteSpace: "nowrap",
                }}
              >
                {t("FCC Registration #")}
              </span>
              <input
                className="smrtcop-input"
                style={{ maxWidth: 200 }}
                value={(formData[`${q.key}_fccNumber`] as string) ?? ""}
                placeholder={
                  fccScanning ? t("Scanning…") : t("Enter number")
                }
                onChange={(e) =>
                  updateField(`${q.key}_fccNumber`, e.target.value)
                }
              />
            </div>
          )}
          </>
        );
      }

      case "certification": {
        const fullName = effectiveUserName;
        const sig = (formData[`${q.key}_signature`] || "").trim();
        // The viewer shows what the crew CAPTURED; it does not re-run the
        // signature check against a live identity. Re-validating would flag a
        // completed sign-off as wrong over a stored-name mismatch as small as a
        // trailing space, and send an admin to chase work that is already done.
        const sigMatch = readOnly
          ? sig.length > 0 && !!formData[`${q.key}_certifiedBy`]
          : sig.length > 0 && sig === fullName;
        return (
          <div style={{ background: "#1a1a1a", border: `1px solid ${sigMatch ? "#1faa59" : "#2a2a2a"}`, borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            <div>
              <div style={labelStyle}>{t("Certified By")}</div>
              {q.helper && (
                <div style={{ fontSize: "0.95rem", color: "#fff", marginBottom: 6 }}>{t(q.helper)}</div>
              )}
            </div>
            <div>
              <input
                className="smrtcop-input"
                placeholder={
                  wizardLang === "es"
                    ? `Escriba "${fullName}" para firmar`
                    : `Type "${fullName}" to sign`
                }
                value={formData[`${q.key}_signature`] ?? ""}
                onChange={(e) => {
                  const typed = e.target.value;
                  updateField(`${q.key}_signature`, typed);
                  updateField(`${q.key}_certifiedBy`, fullName);
                  if (typed.trim() === fullName) {
                    fetch("https://api.ipify.org?format=json")
                      .then((r) => r.json())
                      .then((d) => {
                        updateField(`${q.key}_meta`, {
                          timestamp: new Date().toISOString(),
                          userAgent: navigator.userAgent,
                          userId: user?.userID,
                          name: fullName,
                          ipAddress: d.ip,
                        });
                      })
                      .catch(() => {
                        updateField(`${q.key}_meta`, {
                          timestamp: new Date().toISOString(),
                          userAgent: navigator.userAgent,
                          userId: user?.userID,
                          name: fullName,
                          ipAddress: "unavailable",
                        });
                      });
                  }
                }}
                style={{ borderColor: sig.length > 0 ? (sigMatch ? "#1faa59" : "#c62828") : undefined }}
              />
              {sig.length > 0 && !sigMatch && (
                <div style={{ fontSize: "0.72rem", color: "#c62828", marginTop: 4 }}>
                  {wizardLang === "es" ? "La firma debe coincidir:" : "Signature must match:"} {fullName}
                </div>
              )}
            </div>
            <div style={{ fontSize: "0.72rem", color: sigMatch ? "#1faa59" : "#555", marginTop: 2 }}>
              {sigMatch
                ? `${wizardLang === "es" ? "Firmado" : "Signed"}: ${new Date(formData[`${q.key}_meta`]?.timestamp || "").toLocaleString()}`
                : t("Timestamp: —")}
            </div>
          </div>
        );
      }

      default: // text
        return (
          <div>
            {lbl}
            <input
              className="smrtcop-input"
              type="text"
              placeholder={t(q.helper) || ""}
              value={formData[q.key] ?? ""}
              onChange={(e) => updateField(q.key, e.target.value)}
            />
          </div>
        );
    }
  };

  // Validate the Format constraints (minLength / maxLength / mask) for
  // text & number questions. Mirrors the Manage Profiles isFieldValid rules.
  const meetsFormat = (q: COPQuestion, value: unknown): boolean => {
    if (q.type !== "text" && q.type !== "number") return true;
    const str = String(value ?? "");
    if (q.minLength != null && str.length < q.minLength) return false;
    if (q.maxLength != null && str.length > q.maxLength) return false;
    if (q.mask) {
      const expected = (q.mask.match(/\*/g) || []).length;
      if (expected > 0 && str.length !== expected) return false;
    }
    return true;
  };

  const isSectionComplete = (section: COPSection | undefined): boolean => {
    if (!section) return false;

    // Photo steps: only one photo per segment is REQUIRED to progress (the
    // generic branch below enforces that on each segment's first slot). But a
    // rejected photo anywhere in the step — an optional 2nd-row slot or a
    // crew-added extra included — must block Continue, so the crew can't move on
    // leaving a failed photo behind. A rejection (admin ⛔ or a live vision rule)
    // writes `${slotKey}_status: "fail"`; scan for any within this step's groups.
    if (section.type === "photo") {
      const groupKeys = section.questions
        .map((q) => q.groupKey)
        .filter((g): g is string => !!g);
      const hasRejectedPhoto = Object.keys(formData).some((k) => {
        if (!k.endsWith("_status") || formData[k] !== "fail") return false;
        const base = k.slice(0, -"_status".length);
        // A flag only counts while its slot still HOLDS a photo. Removing a
        // photo does not clear its status sidecar, so a deleted crew-added
        // extra used to leave an orphan "fail" behind — blocking the step
        // forever with no slot left to fix it.
        if (!formData[base]) return false;
        return groupKeys.some((gk) => base === gk || base.startsWith(`${gk}_`));
      });
      if (hasRejectedPhoto) return false;
      // fall through to the generic required-slot presence check below.
    }

    // BOM sections: gated solely by the "Actual Installed Quantities Verified"
    // confirmation checkbox persisted in formData. No per-row validation.
    if (section.type === "bom") {
      return !!formData[`${section.id}_bomConfirmed`];
    }

    // Reports sections: every slot must have a successfully-uploaded blob.
    // The metadata object stored at formData[slot.fieldKey] carries blobName
    // when the upload landed; missing or partial entries fail the check.
    if (section.type === "reports") {
      const slots =
        section.reportKind === "alignment"
          ? [ALIGNMENT_SLOT]
          : retUploadSlots(formData, retPositionsOf(activeSite?.meta));
      return slots.every((slot) => {
        const v = formData[slot.fieldKey] as ReportFileRef | undefined;
        return !!v && typeof v === "object" && !!v.blobName;
      });
    }

    // JHA sections: at least Day 1 must be uploaded (a crew onsite files ≥1
    // JHA). The remaining day slots are optional — the day count varies per
    // deployment, so we can't require all of them. RULE: no uploaded JHA may
    // have the "Working at Heights" conflict (both the certification and the
    // hazard ticked) — a violation blocks the step (and submission) until fixed.
    if (section.type === "jha") {
      const first = formData["jha_day_1"] as ReportFileRef | undefined;
      const hasDay1 =
        !!first && typeof first === "object" && !!first.blobName;
      if (!hasDay1) return false;
      const anyViolation = Object.keys(formData).some(
        (k) => JHA_SLOT_RE.test(k) && formData[`${k}_jhaViolation`] === true,
      );
      return !anyViolation;
    }

    // Verification sections: optional / informational unless the admin
    // flagged specific lines for photo capture in Verify Setup. Continue
    // gates only on those flagged lines' photo slots — every other line is
    // a no-op. With no flags at all, the loop is empty → returns true →
    // Continue is enabled immediately.
    if (section.kind === "verification") {
      return expandPhotoQuestions(section)
        .filter((q) => isQuestionVisible(q, formData))
        .every((q) => {
          const n = q.verifyPhotoCount ?? 0;
          for (let i = 1; i <= n; i++) {
            if (!formData[`${q.key}_photo_${i}`]) return false;
          }
          return true;
        });
    }

    return expandPhotoQuestions(section)
      .filter((q) => q.required && isQuestionVisible(q, formData))
      .every((q) => {
        if (q.type === "heading") return true;
        if (q.type === "certification") {
          const sig = (formData[`${q.key}_signature`] || "").trim();
          // Viewer: presence, not a live-identity match — see renderWizardField.
          if (readOnly) return !!(formData[`${q.key}_certifiedBy`] && sig);
          return !!(formData[`${q.key}_certifiedBy`] && sig && sig === effectiveUserName);
        }
        if (q.type === "checkbox") {
          const v = formData[q.key];
          const checkboxOk = Array.isArray(v) && v.length > 0;
          if (!checkboxOk) return false;
          // Verification-step bundling: when a checkbox question carries
          // `verifyPhotoCount`, every slot must also have a captured file.
          const photoCount = q.verifyPhotoCount ?? 0;
          for (let i = 1; i <= photoCount; i++) {
            if (!formData[`${q.key}_photo_${i}`]) return false;
          }
          return true;
        }
        const v = formData[q.key];
        if (v === undefined || v === null || v === "") return false;
        return meetsFormat(q, v);
      });
  };

  const allSectionsComplete = (sections: COPSection[]): boolean =>
    sections.every((s) => isSectionComplete(s));

  // Why the photo steps won't go green.
  //
  // Every per-sector photo step folds into ONE "Photos" entry in the step bar,
  // so a single rejected shot in (say) Sector Beta left the crew hunting ~90
  // slots on a phone with nothing telling them where to look — the photos were
  // all captured and visible, the step just silently refused to complete, and
  // Submit stayed disabled. This lists exactly what is holding it, across ALL
  // photo steps, with the step to jump to.
  //
  // Two causes: a photo carrying `_status: "fail"` (admin ⛔ or a live vision
  // rule — including one that failed because the CHECK could not run), and a
  // required slot with nothing captured.
  const photoStepBlockers = (): {
    key: string;
    label: string;
    reason: string;
    stepIndex: number;
  }[] => {
    const out: {
      key: string;
      label: string;
      reason: string;
      stepIndex: number;
    }[] = [];
    runtimeSections.forEach((section, stepIndex) => {
      if (section.type !== "photo") return;
      const questions = section.questions || [];
      const groupKeys = questions
        .map((q) => q.groupKey)
        .filter((g): g is string => !!g);
      // Crew-added extras ("…__extra__ab12cd") and second slots ("…_2") are not
      // in `questions`, so match on the parent GROUP and use its human name.
      // Without this the panel printed the raw key, which tells the crew
      // nothing about which photo to go and look at.
      const labelFor = (base: string) => {
        const exact = questions.find(
          (q) => q.groupKey === base || q.key === base,
        );
        if (exact) return exact.groupLabel || exact.label;
        const group = questions.find(
          (q) => q.groupKey && base.startsWith(`${q.groupKey}_`),
        );
        if (!group) return base;
        const name = group.groupLabel || group.label;
        return base.includes("__extra__") ? `${name} (${t("extra photo")})` : name;
      };
      const prefix = section.label ? `${section.label} — ` : "";

      for (const k of Object.keys(formData)) {
        if (!k.endsWith("_status") || formData[k] !== "fail") continue;
        const base = k.slice(0, -"_status".length);
        if (!formData[base]) continue; // orphan flag, slot no longer holds a photo
        if (!groupKeys.some((gk) => base === gk || base.startsWith(`${gk}_`)))
          continue;
        out.push({
          key: base,
          label: `${prefix}${labelFor(base)}`,
          reason:
            (formData[`${base}_statusReason`] as string) ||
            t("This photo was rejected — retake it."),
          stepIndex,
        });
      }

      for (const q of expandPhotoQuestions(section).filter(
        (q) => q.required && isQuestionVisible(q, formData),
      )) {
        if (q.type === "heading") continue;
        const v = formData[q.key];
        if (v === undefined || v === null || v === "") {
          out.push({
            key: q.key,
            label: `${prefix}${q.label}`,
            reason: t("No photo captured yet."),
            stepIndex,
          });
        }
      }
    });
    return out;
  };

  // ── Per-step assignment / lock (multi-crew) ────────────────────────────────
  // The crew lead hands individual wizard steps to crew members so several can
  // work one site at once without clobbering each other. Assignment lives on
  // the site record (`stepAssignments`) and arrives live via the /sites poll, so
  // we read it from the polled `sites` copy, not the open-time `activeSite`.
  const liveActiveSite = activeSite
    ? sites.find((s) => s.id === activeSite.id) || activeSite
    : null;
  const stepAssignments: Record<string, string> =
    liveActiveSite?.stepAssignments || {};
  // Merge the admin-stamped names and the backend-resolved names, preferring a
  // REAL name (one that isn't just the raw auth0 id) from EITHER source — an
  // old/partial site can carry id-as-name placeholders that must not win.
  const crewNames: Record<string, string> = (() => {
    const stamped = liveActiveSite?.crewNames || {};
    const out: Record<string, string> = {};
    const ids = new Set([
      ...Object.keys(stamped),
      ...Object.keys(crewNamesResolved),
    ]);
    for (const id of ids) {
      const a = stamped[id];
      const b = crewNamesResolved[id];
      const real =
        a && a !== id ? a : b && b !== id ? b : a || b || undefined;
      if (real) out[id] = real;
    }
    return out;
  })();
  const siteLeadId = activeSite
    ? activeSite.crewLeadId || activeSite.assignedUserIds?.[0]
    : undefined;
  // ── Whose screen is this? ──────────────────────────────────────────────────
  // Normally the logged-in user. In viewer mode every user-dependent
  // computation runs AS the site's crew lead, so the admin sees the lead's
  // screen rather than their own — the same steps locked, and (below) the
  // lead's name against the certification. Without this an admin would see
  // their own locks and their own name, which is not what they came to check.
  const effectiveUserId = viewerMode ? siteLeadId : userId;
  const effectiveUserName = viewerMode
    ? (siteLeadId && crewNames[siteLeadId]) || ""
    : user?.name || "";
  const isSiteLead = !!effectiveUserId && effectiveUserId === siteLeadId;
  // Nobody manages assignments from the viewer — it is read-only.
  const canManageAssignments = !readOnly && (isSiteLead || isCopAdmin);

  const isCertSection = (section?: COPSection) =>
    !!section &&
    (section.questions || []).some((q) => q.type === "certification");
  const sectionAssigneeId = (section?: COPSection) =>
    section ? stepAssignments[String(section.id)] : undefined;

  // Who may EDIT a step. An ASSIGNED step is its assignee's ALONE — even the crew
  // lead (and admins) are locked out until it's handed back / revoked; that's the
  // whole point of assigning. Only once unassigned does the lead/admin regain it.
  // Certification is lead-only. Other unassigned steps are lead/admin (other crew
  // are view-only until the lead hands them something).
  const canEditSection = (section?: COPSection): boolean => {
    if (!section) return false;
    // The viewer never edits. This is what disables the step body — the wizard
    // already wraps it in a <fieldset disabled> driven by this.
    if (readOnly) return false;
    const assignee = sectionAssigneeId(section);
    if (assignee) return assignee === effectiveUserId; // assigned → assignee only, no bypass
    if (isCertSection(section)) return isSiteLead || isCopAdmin;
    return isSiteLead || isCopAdmin;
  };

  const crewMemberName = (id?: string) =>
    !id
      ? ""
      : crewNames[id] ||
        (id === effectiveUserId ? effectiveUserName || "You" : id);

  const patchStepAssignment = async (
    section: COPSection,
    action: "assign" | "revoke" | "handback",
    assignUserId?: string,
  ) => {
    if (!activeSite || readOnly) return;
    const key = String(section.id);
    // Optimistic local update so the lock flips instantly; the poll reconciles.
    setSites((prev) =>
      prev.map((s) => {
        if (s.id !== activeSite.id) return s;
        const next = { ...(s.stepAssignments || {}) };
        if (action === "assign" && assignUserId) next[key] = assignUserId;
        else delete next[key];
        return { ...s, stepAssignments: next };
      }),
    );
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/smrtcop/sites/${encodeURIComponent(activeSite.id)}/step-assignment`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ stepId: key, action, userId: assignUserId }),
        },
      );
    } catch (err) {
      console.error("Failed to update step assignment:", err);
    }
  };

  // Top-right per-step control (hidden on the certification step). Lead/admin see
  // a small yellow Assign button (→ modal) or an "Assigned to X" pill (tap to
  // reassign/unassign); the assignee sees Hand back; others see a label.
  const renderStepControl = (section?: COPSection): React.ReactNode => {
    if (!section || isCertSection(section)) return null;
    const assigneeId = sectionAssigneeId(section);
    const candidates = (activeSite?.assignedUserIds || []).filter(
      (id) => id !== siteLeadId,
    );
    if (canManageAssignments) {
      if (candidates.length === 0 && !assigneeId) return null; // no one to assign
      if (assigneeId) {
        // Yellow box showing just the assignee's name; tap to reopen the modal
        // (reassign / unassign).
        return (
          <button
            type="button"
            onClick={() => setAssignModalOpen(true)}
            style={{
              background: "#d4a017",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontSize: "0.72rem",
              fontWeight: 600,
              padding: "4px 12px",
              cursor: "pointer",
            }}
            title={t("Change assignment")}
          >
            {crewMemberName(assigneeId)}
          </button>
        );
      }
      return (
        <button
          type="button"
          onClick={() => setAssignModalOpen(true)}
          style={{
            background: "#d4a017",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            fontSize: "0.72rem",
            fontWeight: 600,
            padding: "4px 12px",
            cursor: "pointer",
          }}
        >
          {t("Assign")}
        </button>
      );
    }
    if (assigneeId === effectiveUserId) {
      return (
        <button
          type="button"
          onClick={() => setReturnConfirm(section)}
          style={{
            background: "#d4a017",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            fontSize: "0.72rem",
            padding: "3px 8px",
            cursor: "pointer",
          }}
          title={t("Return this step to the crew lead")}
        >
          {t("Return")}
        </button>
      );
    }
    return (
      <span style={{ fontSize: "0.72rem", color: "#9aa0a6" }}>
        {assigneeId
          ? `${t("Assigned to")} ${crewMemberName(assigneeId)}`
          : t("Unassigned")}
      </span>
    );
  };

  // Wizard modal — form only, one section at a time
  if (activeSite && activeTemplate) {
    const sections = runtimeSections;
    const currentSection = sections[currentStepIndex];
    // Read-only when this step is assigned to someone else, or is lead-only and
    // you're not the lead (multi-crew lock). Navigation stays enabled; only the
    // step body + its Save/Submit actions are gated.
    const stepLocked = !canEditSection(currentSection);

    // Colour the section title per sector so A→B→C transitions are visually
    // obvious. Yellow / Blue / Green; default white for non-sector sections.
    const sectionLabel = currentSection?.label ?? "";
    // Match both the legacy "Sector A/B/C" labels and the v2 "Sector
    // Alpha/Beta/Gamma" labels. Alpha→gold, Beta→blue, Gamma→green.
    const sectionTitleColor =
      /Sector A\b|Alpha/.test(sectionLabel)
        ? "#d4a017"
        : /Sector B\b|Beta/.test(sectionLabel)
        ? "#1976d2"
        : /Sector C\b|Gamma/.test(sectionLabel)
        ? "#28a745"
        : "#fff";

    // Step selector: every per-sector Checklist photo step (type "photo") folds
    // into ONE "Photos" entry so the bar doesn't clutter; every other step
    // (RET, Alignment, JHA, BOM, manual, certification…) is its own entry.
    // `target` is the step to jump to; `steps` is every runtime step it covers.
    const stepLabel = (s: COPSection): string => {
      // The certification / sign-off step is where the crew submits.
      if ((s.questions || []).some((q) => q.type === "certification"))
        return t("Submit");
      if (s.reportKind === "ret") return "RET";
      if (s.reportKind === "alignment") return t("Alignment");
      if (s.type === "bom") return "BOM";
      if (s.type === "jha") return "JHA";
      return t(s.label); // manual sections (e.g. Tape Drop) keep their label
    };
    const progressItems: {
      label: string;
      target: number;
      steps: number[];
      photo: boolean;
    }[] = [];
    sections.forEach((s, i) => {
      const photo = s.type === "photo";
      const last = progressItems[progressItems.length - 1];
      if (photo && last?.photo) {
        last.steps.push(i);
        return;
      }
      progressItems.push({
        label: photo ? t("Photos") : stepLabel(s),
        target: i,
        steps: [i],
        photo,
      });
    });
    const currentProgressIdx = progressItems.findIndex((p) =>
      p.steps.includes(currentStepIndex),
    );
    // Runtime step ids the crew has explicitly Saved (drives the green circles).
    const savedStepIds: number[] = Array.isArray(formData.__savedStepIds)
      ? formData.__savedStepIds
      : [];

    return (
      <div className="smrtcop-page">
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "min(700px, 95vw)", maxHeight: "85dvh", display: "flex", flexDirection: "column" }}>
            {/* Header — site ID only, centered */}
            <div
              className="smrtdocs-modal-header"
              style={{ flexDirection: "column", alignItems: "center", position: "relative" }}
            >
              <h2 style={{ margin: 0, fontSize: "1rem", textAlign: "center" }}>
                {activeSite.meta.siteId}
              </h2>
              <div style={{ fontSize: "0.75rem", color: "#9aa0a6", marginTop: 2, textAlign: "center" }}>
                {wizardLang === "es"
                  ? `Sección ${currentStepIndex + 1} de ${sections.length}`
                  : `Section ${currentStepIndex + 1} of ${sections.length}`}
              </div>
              {/* EN | ES segmented toggle — mirrors Exit on the opposite side. */}
              <div
                style={{
                  position: "absolute",
                  left: 12,
                  top: 10,
                  display: "inline-flex",
                  height: 28,
                  borderRadius: 6,
                  overflow: "hidden",
                  border: "1px solid #2a2a2a",
                  fontSize: "0.75rem",
                }}
              >
                {(["en", "es"] as const).map((code) => {
                  const active = wizardLang === code;
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setWizardLang(code)}
                      style={{
                        padding: "0 10px",
                        height: "100%",
                        background: active ? "#007bff" : "#1c1c1c",
                        color: active ? "#fff" : "#9aa0a6",
                        border: "none",
                        cursor: "pointer",
                        fontWeight: active ? 700 : 500,
                        fontFamily: "inherit",
                      }}
                    >
                      {code.toUpperCase()}
                    </button>
                  );
                })}
              </div>
              <button
                className="btn-cancel"
                style={{ position: "absolute", right: 12, top: 10, padding: "4px 12px", fontSize: "0.8rem", height: 28 }}
                onClick={() => {
                  if (readOnly) {
                    onCloseViewer?.();
                    return;
                  }
                  closeWizard();
                }}
              >
                {readOnly ? t("Close") : t("Exit")}
              </button>
            </div>

            {/* Viewer banner — says whose screen this is and that nothing here
                writes. Permanent rather than dismissible: an admin must never
                be in doubt about whether they are looking or acting. */}
            {readOnly && (
              <div
                style={{
                  padding: "8px 20px",
                  background: "#10243a",
                  borderBottom: "1px solid #1976d2",
                  color: "#8ab4f8",
                  fontSize: "0.78rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexShrink: 0,
                }}
              >
                <span style={{ fontWeight: 700 }}>{t("Read-only view")}</span>
                <span style={{ color: "#9aa0a6" }}>
                  {effectiveUserName
                    ? `${t("Showing this site as")} ${effectiveUserName}. ${t("Nothing here is saved.")}`
                    : t("Nothing here is saved.")}
                </span>
              </div>
            )}

            {/* Step progress bar — a horizontal connected-circle tracker (like
                the Status History progression, but sideways). A step's circle
                is GREEN once it's saved/complete, blue while it's the current
                step, grey otherwise. Tap any circle to jump to that step. All
                per-sector photo steps fold into one "Photos" circle. */}
            {progressItems.length > 1 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  padding: "12px 12px 8px",
                  overflowX: "auto",
                  borderBottom: "1px solid #2a2a2a",
                  flexShrink: 0,
                }}
              >
                {progressItems.map((p, idx) => {
                  const active = idx === currentProgressIdx;
                  // Circle is YELLOW (with a glow) once the step is complete and
                  // ready to save/send, then GREEN ✓ once it's actually saved —
                  // for the collapsed Photos circle every sub-step must be done.
                  // Grey until it's ready. The yellow → green shift makes the
                  // "these are ready, save them" state obvious at a glance.
                  const allComplete = p.steps.every((si) =>
                    isSectionComplete(sections[si]),
                  );
                  const allSaved = p.steps.every((si) =>
                    savedStepIds.includes(sections[si].id),
                  );
                  const sent = allComplete && allSaved;
                  const ready = allComplete && !allSaved;
                  const isLast = idx === progressItems.length - 1;
                  return (
                    <div
                      key={idx}
                      style={{
                        flex: "1 0 60px",
                        minWidth: 60,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        position: "relative",
                      }}
                    >
                      {/* connector to the next circle (behind the circles) */}
                      {!isLast && (
                        <div
                          style={{
                            position: "absolute",
                            top: 13,
                            left: "50%",
                            width: "100%",
                            height: 2,
                            background: sent ? "#1faa59" : "#333",
                            zIndex: 0,
                          }}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          if (idx !== currentProgressIdx)
                            setCurrentStepIndex(p.target);
                        }}
                        title={p.label}
                        style={{
                          position: "relative",
                          zIndex: 1,
                          boxSizing: "border-box",
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          padding: 0,
                          background: sent
                            ? "#1faa59"
                            : ready
                              ? "#e6a200"
                              : "#2a2a2a",
                          color: sent || ready ? "#fff" : "#9aa0a6",
                          border: active
                            ? "2px solid #7fb3ff"
                            : "2px solid transparent",
                          // Yellow "ready" steps get a halo so the crew can't
                          // miss that they're waiting to be saved/sent.
                          boxShadow: ready
                            ? "0 0 0 4px rgba(230,162,0,0.30)"
                            : "none",
                        }}
                      >
                        {sent ? "✓" : idx + 1}
                      </button>
                      <span
                        style={{
                          marginTop: 5,
                          maxWidth: 72,
                          textAlign: "center",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "0.62rem",
                          lineHeight: 1.2,
                          color: active
                            ? "#fff"
                            : sent
                              ? "#8fd4a8"
                              : ready
                                ? "#f0b93a"
                                : "#9aa0a6",
                          fontWeight: active || ready ? 700 : 500,
                        }}
                      >
                        {p.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Body — scrollable */}
            <div className="smrtdocs-modal-body" style={{ overflowY: "auto", flex: 1 }}>
              {/* Title row — step name centred, per-step Assign control top-right
                  (crew lead only; hidden on the certification step). */}
              <div style={{ position: "relative", marginBottom: 16 }}>
                <h3 style={{ margin: 0, color: sectionTitleColor, fontSize: "1rem", textAlign: "center", fontWeight: 700 }}>
                  {t(currentSection?.label)}
                </h3>
                <div style={{ position: "absolute", right: 0, top: 0 }}>
                  {renderStepControl(currentSection)}
                </div>
              </div>
              {/* When this step is assigned to someone else (or is lead-only and
                  you're not the lead), the whole body is disabled — a clean,
                  broad read-only lock via <fieldset disabled>. */}
              <fieldset
                disabled={stepLocked}
                style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
              >
              {currentSection?.kind === "verification" ? (
                /* Verification step — render checkbox lines grouped by their
                   parent row's groupKey. One card per row (label as header,
                   atomic lines as inline checkbox rows). When a line carries
                   verifyPhotoCount > 0 (set by the Verify modal), a single
                   inline photo widget renders to the right of the checkbox. */
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {(() => {
                    const visibleQuestions = expandPhotoQuestions(currentSection)
                      .filter((q) => isQuestionVisible(q, formData));
                    // Preserve emission order while collecting one bucket per
                    // groupKey. Questions without a groupKey (shouldn't happen
                    // for Verification rows, but defended) get their own bucket.
                    const buckets: { key: string; label: string; questions: COPQuestion[] }[] = [];
                    const indexByKey = new Map<string, number>();
                    for (const q of visibleQuestions) {
                      const groupKey = q.groupKey ?? q.key;
                      const i = indexByKey.get(groupKey);
                      if (i === undefined) {
                        indexByKey.set(groupKey, buckets.length);
                        buckets.push({
                          key: groupKey,
                          label: q.groupLabel || q.label,
                          questions: [q],
                        });
                      } else {
                        buckets[i].questions.push(q);
                      }
                    }
                    return buckets.map((bucket) => (
                      <div
                        key={bucket.key}
                        style={{
                          background: "#1a1a1a",
                          border: "1px solid #2a2a2a",
                          borderRadius: 6,
                          padding: "10px 12px",
                        }}
                      >
                        <div style={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem", marginBottom: 8 }}>
                          {t(bucket.label)}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {bucket.questions.map((q) => {
                            const letter = String.fromCharCode(65 + (q.groupIndex ?? 0));
                            const photoFlagged = (q.verifyPhotoCount ?? 0) > 0;
                            // Lines are informational text only — no
                            // checkbox to tick. Lines flagged in Verify
                            // Setup carry one or more Photo rows below
                            // the line text, using the same row layout
                            // as the photo step. isSectionComplete gates
                            // Continue on the base photo's presence;
                            // extras are best-effort and don't block
                            // Continue.
                            const baseSlotKey = `${q.key}_photo_1`;
                            const verifyGroupKey = `${q.key}_photos`;
                            const extrasListKey = `__extras::${verifyGroupKey}`;
                            const extras = Array.isArray(formData[extrasListKey])
                              ? (formData[extrasListKey] as string[])
                              : [];
                            // Synthetic photo questions feed the same
                            // renderWizardField "photo" path as the photo
                            // step, so renaming / extras delete / view
                            // / 3-dot menu all just work.
                            const baseQ: COPQuestion = {
                              ...q,
                              key: baseSlotKey,
                              type: "photo",
                              groupKey: verifyGroupKey,
                              groupIndex: 0,
                              groupLabel: q.groupLabel || q.label || "verify",
                              label: "Photo 1",
                              instruction: undefined,
                              subInstructions: undefined,
                            };
                            const extraQs: COPQuestion[] = extras.map(
                              (id, i) => ({
                                ...baseQ,
                                key: `${baseSlotKey}__extra__${id}`,
                                groupIndex: 1 + i,
                              }),
                            );
                            const addExtra = () => {
                              const id = Math.random().toString(36).slice(2, 8);
                              updateField(extrasListKey, [...extras, id]);
                            };
                            return (
                              <div
                                key={q.key}
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 6,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 8,
                                    fontSize: "0.85rem",
                                    color: "#cfcfcf",
                                    lineHeight: 1.45,
                                  }}
                                >
                                  <span style={{ color: "#9aa0a6", minWidth: 18, flexShrink: 0 }}>
                                    {letter}.
                                  </span>
                                  <span style={{ flex: 1, minWidth: 0 }}>{t(q.label)}</span>
                                </div>
                                {photoFlagged && (
                                  <div style={{ marginLeft: 26 }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 4,
                                      }}
                                    >
                                      {[baseQ, ...extraQs].map((sq) => (
                                        <div key={sq.key}>
                                          {renderWizardField(sq)}
                                        </div>
                                      ))}
                                    </div>
                                    <button
                                      type="button"
                                      className="smrtcop-photo-row-add"
                                      onClick={addExtra}
                                      aria-label={t("Add another photo")}
                                      title={t("Add another photo")}
                                    >
                                      +
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              ) : currentSection?.type === "reports" ? (
                currentSection.reportKind === "alignment" ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {renderReportSlot(ALIGNMENT_SLOT)}
                  </div>
                ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {/* RET reports — one row per sector with P1 / P4 side by
                      side (Alpha/Beta/Gamma, + Delta when added). */}
                  {sectorRetRows(
                    isDeltaAdded(formData),
                    retPositionsOf(activeSite?.meta),
                  ).map((pair) => (
                    <div key={pair[0].fieldKey} style={{ display: "flex", gap: 14 }}>
                      {pair.map((slot) => (
                        <div key={slot.fieldKey} style={{ flex: 1, minWidth: 0 }}>
                          {renderReportSlot(slot)}
                        </div>
                      ))}
                    </div>
                  ))}

                  {/* Delta sector add/remove — same round "+" control as the
                      photo add rows, beside a "Delta Sector" label. */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span
                      style={{
                        fontSize: "0.75rem",
                        color: "#9aa0a6",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {t("Delta Sector")}
                    </span>
                    <button
                      type="button"
                      className="smrtcop-photo-row-add"
                      style={{ marginTop: 0 }}
                      onClick={isDeltaAdded(formData) ? removeDeltaSector : addDeltaSector}
                      aria-label={isDeltaAdded(formData) ? t("Remove Delta sector") : t("Add Delta sector")}
                      title={isDeltaAdded(formData) ? t("Remove Delta sector") : t("Add Delta sector")}
                    >
                      {isDeltaAdded(formData) ? "−" : "+"}
                    </button>
                  </div>

                  {/* RRH Serial Numbers — one photo row per RET device using the
                      same capture UI as the Sector photos (📷 / ⤴ / ⋮ tools),
                      grouped by sector, with the OCR-extracted serial beside
                      each ("N/A" until a photo is captured). */}
                  <div
                    style={{
                      marginTop: 6,
                      borderTop: "1px solid #2a2a2a",
                      paddingTop: 12,
                      display: "flex",
                      flexDirection: "column",
                      gap: 14,
                    }}
                  >
                    <div style={{ fontSize: "0.8rem", color: "#c9d1d9", fontWeight: 600 }}>
                      {t("RRH Serial Numbers")}
                    </div>
                    {rrhSectors(isDeltaAdded(formData)).map((sector) => (
                      <div
                        key={sector.key}
                        style={{ display: "flex", flexDirection: "column", gap: 6 }}
                      >
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "#9aa0a6",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          {t(sector.label)}
                        </div>
                        {rrhSlotsForSector(
                          sector.key,
                          retPositionsOf(activeSite?.meta),
                        ).map((slot) => {
                          const hasFile = !!formData[slot.fieldKey];
                          const blobName = formData[`${slot.fieldKey}_blob`] as string | undefined;
                          const serialBlob = formData[`${slot.fieldKey}_serialBlob`] as string | undefined;
                          const serial = (formData[`${slot.fieldKey}_serial`] as string) || "";
                          // Serial only counts once OCR has run against the
                          // CURRENT photo — after a retake (new blob) show
                          // "Pending…" until OCR catches up, not a stale value.
                          const ocrDone = !!blobName && serialBlob === blobName;
                          const hasSerial = ocrDone && !!serial && serial !== "N/A";
                          const photoQ: COPQuestion = {
                            key: slot.fieldKey,
                            label: `${sector.label} ${rrhSlotLabel(slot, retPositionsOf(activeSite?.meta))}`,
                            type: "photo",
                            required: false,
                          };
                          return (
                            <div
                              key={slot.fieldKey}
                              style={{ display: "flex", alignItems: "center", gap: 10 }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                {renderWizardField(photoQ)}
                              </div>
                              <div
                                style={{
                                  width: 120,
                                  flex: "none",
                                  textAlign: "right",
                                  fontSize: "0.8rem",
                                  color: hasSerial ? "#fff" : "#9aa0a6",
                                }}
                                title={t("Connected RRH Serial Number")}
                              >
                                {!hasFile
                                  ? "N/A"
                                  : ocrDone
                                    ? serial || "N/A"
                                    : t("Pending…")}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}

                    {/* Delta sector add/remove — same round control as the RET
                        uploads; toggles the whole RET step's Delta sector. */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span
                        style={{
                          fontSize: "0.75rem",
                          color: "#9aa0a6",
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {t("Delta Sector")}
                      </span>
                      <button
                        type="button"
                        className="smrtcop-photo-row-add"
                        style={{ marginTop: 0 }}
                        onClick={isDeltaAdded(formData) ? removeDeltaSector : addDeltaSector}
                        aria-label={isDeltaAdded(formData) ? t("Remove Delta sector") : t("Add Delta sector")}
                        title={isDeltaAdded(formData) ? t("Remove Delta sector") : t("Add Delta sector")}
                      >
                        {isDeltaAdded(formData) ? "−" : "+"}
                      </button>
                    </div>
                  </div>

                </div>
                )
              ) : currentSection?.type === "jha" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {/* One PDF per day onsite — Day 1 required, the rest optional.
                      The "+" adds another day slot. */}
                  {jhaSlots(formData).map((slot, i) => (
                    <div key={slot.fieldKey}>{renderReportSlot(slot, i === 0)}</div>
                  ))}
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span
                      style={{
                        fontSize: "0.75rem",
                        color: "#9aa0a6",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {t("Add Day")}
                    </span>
                    <button
                      type="button"
                      className="smrtcop-photo-row-add"
                      style={{ marginTop: 0 }}
                      onClick={addJhaDay}
                      aria-label={t("Add another day")}
                      title={t("Add another day")}
                    >
                      +
                    </button>
                  </div>
                </div>
              ) : currentSection?.type === "bom" ? (
                bomStatus === "loading" ? (
                  <div style={{ color: "#9aa0a6", fontSize: "0.9rem", padding: "16px 0" }}>
                    {t("Loading BOM…")}
                  </div>
                ) : bomStatus === "missing" ? (
                  <div style={{ color: "#d4a017", fontSize: "0.9rem", padding: "16px 0" }}>
                    {t("Final BOM file not uploaded for this site")}
                  </div>
                ) : bomStatus === "error" ? (
                  <div style={{ color: "#c62828", fontSize: "0.9rem", padding: "16px 0" }}>
                    {t("Failed to load Final BOM. Try again later.")}
                  </div>
                ) : (
                  <>
                    <table className="smrtcop-bom-table">
                      <thead>
                        <tr>
                          <th>{t("Item")}</th>
                          <th>{t("Description")}</th>
                          <th>{t("UOM")}</th>
                          <th>{t("Qty")}</th>
                          <th>{t("Actual Installed")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bomRows.map((r, i) => (
                          <tr key={i}>
                            <td>{r.item}</td>
                            <td>{r.description}</td>
                            <td>{r.uom}</td>
                            <td>{r.plannedQty}</td>
                            <td>
                              <input
                                className="smrtcop-input"
                                inputMode="numeric"
                                value={r.actualInstalled}
                                onChange={(e) =>
                                  updateBomRow(
                                    currentSection.id,
                                    i,
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Confirmation checkbox — gates the Continue button */}
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginTop: 18,
                        padding: "10px 12px",
                        background: "#1a1a1a",
                        border: "1px solid #2a2a2a",
                        borderRadius: 6,
                        cursor: "pointer",
                        color: "#fff",
                        fontSize: "0.95rem",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!formData[`${currentSection.id}_bomConfirmed`]}
                        onChange={(e) =>
                          updateField(
                            `${currentSection.id}_bomConfirmed`,
                            e.target.checked,
                          )
                        }
                        style={{
                          accentColor: "#007bff",
                          transform: "scale(1.4)",
                          cursor: "pointer",
                        }}
                      />
                      {t("Actual Installed Quantities Verified")}
                    </label>
                  </>
                )
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {groupPhotoQuestions(
                    expandPhotoQuestions(currentSection).filter((q) =>
                      isQuestionVisible(q, formData),
                    ),
                  ).map((group) => {
                    const isPhotoGroup = group[0].type === "photo";

                    // Non-photo (manual) groups keep the original render
                    // path: single questions render bare, multi-question
                    // groups render with a header + side-by-side wrap.
                    // No extras / no "+" / no row-style layout.
                    if (!isPhotoGroup) {
                      if (group.length === 1) {
                        return (
                          <div key={group[0].key}>
                            {renderWizardField(group[0])}
                          </div>
                        );
                      }
                      const header = group[0].groupLabel || "";
                      return (
                        <div key={group[0].groupKey}>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "#9aa0a6",
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              marginBottom: 6,
                            }}
                          >
                            {t(header)}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 10,
                              flexWrap: "wrap",
                            }}
                          >
                            {group.map((q) => (
                              <div
                                key={q.key}
                                style={{ flex: "1 1 200px", minWidth: 180 }}
                              >
                                {renderWizardField(q)}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }

                    // Photo group — list-style layout, group-level help
                    // toggle, "+" to add extras.
                    const baseGroupKey = group[0].groupKey ?? group[0].key;
                    const header = group[0].groupLabel || group[0].label || "";
                    const extrasListKey = `__extras::${baseGroupKey}`;
                    const extras = Array.isArray(formData[extrasListKey])
                      ? (formData[extrasListKey] as string[])
                      : [];
                    const baseCount = group.length;

                    // Extras inherit the base group's metadata so the
                    // photo-row renderer treats them like any other slot,
                    // just with a unique key + a delete-row delete behaviour.
                    const extraQuestions: COPQuestion[] = extras.map(
                      (id, i) => ({
                        ...group[0],
                        key: `${baseGroupKey}__extra__${id}`,
                        groupKey: baseGroupKey,
                        groupIndex: baseCount + i,
                        instruction: undefined,
                        subInstructions: undefined,
                      }),
                    );

                    const addExtra = () => {
                      const id = Math.random().toString(36).slice(2, 8);
                      updateField(extrasListKey, [...extras, id]);
                    };

                    // Help [?] toggle + instruction block live at the group
                    // header level (per the redesign — used to be inline
                    // in the first row). Computed here from group[0] so
                    // every slot in the group sees the same toggle state.
                    const hasSubInstructions =
                      Array.isArray(group[0].subInstructions) &&
                      group[0].subInstructions.length > 0;
                    const instructionLines: string[] = group[0].instruction
                      ? group[0].instruction
                          .replace(/<br\s*\/?>/gi, "\n")
                          .split(/\r?\n/)
                          .map((s) => s.trim())
                          .filter(Boolean)
                      : [];
                    const hasInstructionContent =
                      hasSubInstructions || instructionLines.length > 0;
                    const helpKey = baseGroupKey;
                    const helpOpen = openHelp.has(helpKey);

                    return (
                      <div key={baseGroupKey}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: "0.75rem",
                            color: "#9aa0a6",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            marginBottom: 6,
                          }}
                        >
                          <span>{t(expandGroupLabel(header))}</span>
                          {hasInstructionContent && (
                            <button
                              type="button"
                              aria-label={t(
                                helpOpen
                                  ? "Hide instructions"
                                  : "Show instructions",
                              )}
                              aria-expanded={helpOpen}
                              onClick={() => toggleHelp(helpKey)}
                              className="smrtcop-help-toggle"
                            >
                              ?
                            </button>
                          )}
                        </div>
                        {hasInstructionContent && helpOpen && (
                          <div
                            style={{
                              background: "#1a1a1a",
                              border: "1px solid #2a2a2a",
                              borderRadius: 6,
                              padding: "8px 10px",
                              marginBottom: 8,
                              fontSize: "0.82rem",
                              color: "#cfcfcf",
                              lineHeight: 1.45,
                            }}
                          >
                            {hasSubInstructions ? (
                              <>
                                {group[0].groupTitle && (
                                  <div
                                    style={{
                                      color: "#fff",
                                      fontWeight: 600,
                                      marginBottom: 6,
                                    }}
                                  >
                                    {t(group[0].groupTitle)}
                                  </div>
                                )}
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 3,
                                  }}
                                >
                                  {group[0].subInstructions!.map((sub, i) => (
                                    <div key={i}>
                                      <span
                                        style={{
                                          color: "#9aa0a6",
                                          marginRight: 6,
                                        }}
                                      >
                                        {String.fromCharCode(65 + i)}.
                                      </span>
                                      {t(sub)}
                                    </div>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <div style={{ whiteSpace: "pre-wrap" }}>
                                {instructionLines.map((line, i) => (
                                  <React.Fragment key={i}>
                                    {i > 0 && <br />}
                                    {t(line)}
                                  </React.Fragment>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                          }}
                        >
                          {[...group, ...extraQuestions].map((q) => (
                            <div key={q.key}>{renderWizardField(q)}</div>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="smrtcop-photo-row-add"
                          onClick={addExtra}
                          aria-label={t("Add another photo")}
                          title={t("Add another photo")}
                        >
                          +
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              </fieldset>
            </div>

            {/* Why the Photos step won't complete. Shown on any photo step
                while something is outstanding — all photo steps fold into one
                "Photos" entry, so this covers every sector, not just the one
                on screen. Tap a row to jump straight to it. */}
            {currentSection?.type === "photo" &&
              (() => {
                const blockers = photoStepBlockers();
                if (blockers.length === 0) return null;
                return (
                  <div
                    style={{
                      margin: "0 20px 12px",
                      border: "1px solid #d4a017",
                      background: "#2a2410",
                      borderRadius: 6,
                      padding: "10px 12px",
                      flexShrink: 0,
                    }}
                  >
                    {/* Collapsed by default. A site can be short 27 photos, and
                        27 two-line rows inside an 85dvh modal buries the step
                        itself — the summary is the signal, the list is detail
                        you ask for. */}
                    <button
                      type="button"
                      onClick={() => setBlockersOpen((v) => !v)}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        width: "100%",
                        textAlign: "left",
                        cursor: "pointer",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: "#d4a017",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <span style={{ fontSize: "0.7rem" }}>
                        {blockersOpen ? "▾" : "▸"}
                      </span>
                      <span style={{ flex: 1 }}>
                        {blockers.length}{" "}
                        {blockers.length === 1
                          ? t("photo still needs attention")
                          : t("photos still need attention")}
                      </span>
                      <span style={{ color: "#9aa0a6", fontWeight: 400 }}>
                        {blockersOpen ? t("Hide") : t("Show")}
                      </span>
                    </button>
                    <div
                      style={{
                        display: blockersOpen ? "flex" : "none",
                        flexDirection: "column",
                        gap: 6,
                        marginTop: 8,
                        maxHeight: "30dvh",
                        overflowY: "auto",
                      }}
                    >
                      {blockers.map((b) => (
                        <button
                          key={b.key}
                          type="button"
                          onClick={() => setCurrentStepIndex(b.stepIndex)}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            textAlign: "left",
                            cursor: "pointer",
                            color: "#e6e6e6",
                            fontSize: "0.78rem",
                            lineHeight: 1.4,
                          }}
                        >
                          <span style={{ fontWeight: 600 }}>{b.label}</span>
                          <br />
                          <span style={{ color: "#9aa0a6" }}>{b.reason}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

            {/* Footer — navigation */}
            <div style={{ padding: "12px 20px", display: "flex", justifyContent: "space-between", borderTop: "1px solid #555" }}>
              <div>
                {currentStepIndex > 0 && (
                  <button
                    className="smrtcop-btn-action smrtcop-btn-cancel"
                    onClick={() => setCurrentStepIndex((i) => i - 1)}
                  >
                    {t("Back")}
                  </button>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {readOnly ? (
                  // Viewer: step through only. No Save, no Submit, and none of
                  // the crew's lock messaging ("only the crew lead can
                  // certify") — that would read as an error rather than as the
                  // read-only view it is.
                  <button
                    className="smrtcop-btn-action smrtcop-btn-cancel"
                    disabled={currentStepIndex >= sections.length - 1}
                    onClick={() => setCurrentStepIndex((i) => i + 1)}
                  >
                    {t("Next")}
                  </button>
                ) : currentStepIndex < sections.length - 1 ? (
                  <>
                    <button
                      className="smrtcop-btn-action smrtcop-btn-cancel"
                      onClick={() => setCurrentStepIndex((i) => i + 1)}
                    >
                      {t("Skip")}
                    </button>
                    {(() => {
                      const stepComplete = isSectionComplete(currentSection);
                      const stepSaved =
                        !!currentSection &&
                        savedStepIds.includes(currentSection.id);
                      const sent = stepComplete && stepSaved;
                      // Ready-to-save lights the button YELLOW ("Save"); once
                      // saved it reads GREEN ("Sent") so the crew knows the admin
                      // can review it — and they can still come back to edit.
                      return (
                        <button
                          className={`smrtcop-btn-action ${!stepComplete ? "smrtcop-btn-cancel" : ""}`}
                          // Only the step's owner can Save it; a view-only crew
                          // member (locked step) still sees the green "Sent"
                          // state but can't act.
                          disabled={!stepComplete || stepLocked}
                          onClick={async () => {
                            markSectionComplete(currentSection);
                            // If the person saving is the assignee of an
                            // assigned step (a Tower Crew member, not the lead),
                            // hand the step straight back to the crew lead so
                            // they don't also have to scroll up and click Return.
                            const assignee = sectionAssigneeId(currentSection);
                            const shouldReturn =
                              !!currentSection &&
                              !!assignee &&
                              assignee === effectiveUserId &&
                              effectiveUserId !== siteLeadId;
                            if (shouldReturn && activeSite) {
                              // Persist the just-saved answers BEFORE releasing
                              // the lock, so nothing is lost on hand-back.
                              try {
                                await flushAnswers(activeSite.id);
                              } catch (err) {
                                console.error(
                                  "Failed to flush before auto-return:",
                                  err,
                                );
                              }
                              await patchStepAssignment(
                                currentSection,
                                "handback",
                              );
                            }
                            setCurrentStepIndex((i) => i + 1);
                          }}
                          style={
                            stepComplete
                              ? {
                                  background: sent
                                    ? "#1faa59"
                                    : stepLocked
                                      ? "#3a3a3a"
                                      : "#d4a017",
                                  color: "#fff",
                                }
                              : undefined
                          }
                        >
                          {sent ? t("Sent") : t("Save")}
                        </button>

                      );
                    })()}
                  </>
                ) : stepLocked ? (
                  // Certification is crew-lead-only; Tower Crew see a note here.
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "#9aa0a6",
                      alignSelf: "center",
                    }}
                  >
                    {t("Only the crew lead can certify this site")}
                  </div>
                ) : (
                  <button
                    className={`smrtcop-btn-action ${allSectionsComplete(sections) && Object.keys(photoUploadStatus).length === 0 ? "smrtcop-btn-confirm" : "smrtcop-btn-cancel"}`}
                    disabled={
                      !allSectionsComplete(sections) ||
                      Object.keys(photoUploadStatus).length > 0
                    }
                    title={
                      Object.keys(photoUploadStatus).length > 0
                        ? t("Photos are still uploading — please wait.")
                        : undefined
                    }
                    onClick={async () => {
                      // Pull the merged package (incl. other crew's work) into
                      // view so the lead reviews the WHOLE COP before signing.
                      if (activeSite) {
                        const merged = await fetchMergedAnswers(activeSite.id);
                        setFormData(merged);
                        formDataRef.current = merged;
                        baselineAnswersRef.current = { ...merged };
                      }
                      setShowSubmitConfirm(true);
                    }}
                  >
                    {Object.keys(photoUploadStatus).length > 0
                      ? t("Uploading photos…")
                      : t("Submit")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {assignModalOpen && currentSection && (
          <div className="smrtdocs-modal-overlay" style={{ zIndex: 10001 }}>
            <div className="smrtdocs-modal" style={{ width: "min(420px, 95vw)" }}>
              <div className="smrtdocs-modal-header">
                <h2 style={{ margin: 0, fontSize: "1rem" }}>
                  {t("Assign Crew")}
                </h2>
                <button
                  className="btn-cancel"
                  onClick={() => setAssignModalOpen(false)}
                >
                  {t("Close")}
                </button>
              </div>
              <div className="smrtdocs-modal-body">
                <div
                  style={{ fontSize: "0.85rem", color: "#ccc", marginBottom: 10 }}
                >
                  {t("Assign this step to a crew member.")}
                </div>
                <select
                  className="manage-input"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      patchStepAssignment(currentSection, "assign", e.target.value);
                      setAssignModalOpen(false);
                    }
                  }}
                  style={{ width: "100%" }}
                >
                  <option value="">{t("Select crew member")}</option>
                  {(activeSite?.assignedUserIds || [])
                    .filter((id) => id !== siteLeadId)
                    .map((id) => (
                      <option key={id} value={id}>
                        {crewMemberName(id)}
                      </option>
                    ))}
                </select>
              </div>
              {sectionAssigneeId(currentSection) && (
                <div
                  style={{
                    padding: "10px",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    className="btn-cancel"
                    onClick={() => {
                      patchStepAssignment(currentSection, "revoke");
                      setAssignModalOpen(false);
                    }}
                  >
                    {t("Unassign")}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {returnConfirm && (
          <div className="smrtdocs-modal-overlay" style={{ zIndex: 10001 }}>
            <div className="smrtdocs-modal" style={{ width: "min(420px, 95vw)" }}>
              <div className="smrtdocs-modal-header">
                <h2 style={{ margin: 0, fontSize: "1rem" }}>
                  {t("Return step?")}
                </h2>
              </div>
              <div className="smrtdocs-modal-body">
                <p
                  style={{ fontSize: "0.9rem", lineHeight: 1.5, margin: "0 0 20px" }}
                >
                  {t(
                    "This returns the step to the crew lead. You won't be able to edit it again unless it's reassigned to you.",
                  )}
                </p>
                {Object.keys(photoUploadStatus).length > 0 && (
                  <p
                    style={{
                      fontSize: "0.85rem",
                      lineHeight: 1.5,
                      margin: "0 0 20px",
                      color: "#f0b93a",
                    }}
                  >
                    {t(
                      "A photo is still uploading. Wait for it to finish before returning, or it may not be saved.",
                    )}
                  </p>
                )}
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <button
                    className="btn-cancel"
                    onClick={() => setReturnConfirm(null)}
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    className="btn-save"
                    disabled={Object.keys(photoUploadStatus).length > 0}
                    style={
                      Object.keys(photoUploadStatus).length > 0
                        ? { opacity: 0.5, cursor: "not-allowed" }
                        : undefined
                    }
                    onClick={async () => {
                      const section = returnConfirm;
                      setReturnConfirm(null);
                      // Persist any pending answer/photo references BEFORE handing
                      // the step back, so nothing the assignee just added is lost
                      // once they lose edit access.
                      if (activeSite) await flushAnswers(activeSite.id);
                      patchStepAssignment(section, "handback");
                    }}
                  >
                    {t("Return")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {tapeWarn && (
          <div className="smrtdocs-modal-overlay" style={{ zIndex: 10002 }}>
            <div className="smrtdocs-modal" style={{ width: "min(420px, 95vw)" }}>
              <div className="smrtdocs-modal-header">
                <h2 style={{ margin: 0, fontSize: "1rem" }}>
                  {t("Photo flagged")}
                </h2>
              </div>
              <div className="smrtdocs-modal-body">
                <p
                  style={{ fontSize: "0.9rem", lineHeight: 1.5, margin: "0 0 8px" }}
                >
                  {t(
                    "The system has flagged a possible issue with this tape drop photo — it may be showing the metric tape instead of feet & inches.",
                  )}
                </p>
                <p
                  style={{
                    fontSize: "0.85rem",
                    lineHeight: 1.5,
                    margin: "0 0 20px",
                    color: "#9aa0a6",
                  }}
                >
                  {t(
                    "Retake the photo, or override the flag if the feet-and-inches tape is actually shown.",
                  )}
                </p>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <button
                    className="btn-cancel"
                    onClick={() => retakeTapePhoto(tapeWarn.key)}
                  >
                    {t("Retake")}
                  </button>
                  <button
                    className="btn-save"
                    onClick={() => overrideTapePhoto(tapeWarn.key)}
                  >
                    {t("Override")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showSubmitConfirm && (
          <div className="smrtdocs-modal-overlay" style={{ zIndex: 10000 }}>
            <div className="smrtdocs-modal" style={{ width: "min(480px, 95vw)" }}>
              <div className="smrtdocs-modal-header">
                <h2 style={{ margin: 0, fontSize: "1rem" }}>{t("Submit COP")}</h2>
              </div>
              <div className="smrtdocs-modal-body">
                <p style={{ fontSize: "0.9rem", lineHeight: 1.5, margin: "0 0 20px" }}>
                  {t("Are you sure you want to submit? You will not be able to edit after submission.")}
                </p>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <button className="btn-cancel" onClick={() => setShowSubmitConfirm(false)}>{t("Cancel")}</button>
                  <button className="btn-save" onClick={async () => {
                    setShowSubmitConfirm(false);
                    console.log("COP Wizard submitted");
                    if (activeSite) {
                      // Update site status via PATCH
                      try {
                        const patchUrl = `${process.env.REACT_APP_BACKEND_URL}/smrtcop/sites/${encodeURIComponent(activeSite.id)}/status`;
                        const patchRes = await fetch(patchUrl, {
                          method: "PATCH", credentials: "include",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ status: "Complete" }),
                        });
                        console.log("[SMRTCOP] PATCH response status:", patchRes.status);
                        // Record "Tower work Complete" in the site history.
                        recordHistory(activeSite.id, "towerComplete");
                      } catch (err) { console.error("Failed to update site status:", err); }
                      // Move answers to submitted. Snapshot the SERVER's merged
                      // answers (every crew member's work), not just the lead's
                      // local formData — otherwise a photo the lead never saw on
                      // screen would be dropped from the certified package.
                      try {
                        const merged = await fetchMergedAnswers(activeSite.id);
                        const stateRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtcop/site-state/${encodeURIComponent(activeSite.id)}`, {
                          method: "PUT", credentials: "include",
                          headers: { "Content-Type": "application/json" },
                          // Freeze the template the crew filled against, so later
                          // template edits never change this submitted site's
                          // review/output (mirrors SMRT Docs' snapshot-on-issue).
                          body: JSON.stringify({
                            answers: {},
                            submitted: merged,
                            templateSnapshot: activeTemplate,
                          }),
                        });
                        console.log("[SMRTCOP] PUT site-state response:", stateRes.status);
                      } catch (err) { console.error("Failed to save submission:", err); }
                    }
                    // Update local state
                    setSites(prev => prev.map(s =>
                      s.id === activeSite?.id
                        ? { ...s, status: "Complete" as COPSite["status"], updatedAt: new Date().toISOString() }
                        : s,
                    ));
                    setActiveSite(null);
                    setActiveTemplate(null);
                    setFormData({});
                    // Submit cleared the server answers wholesale; drop the save
                    // baseline too so a trailing autosave computes an empty delta
                    // rather than trying to re-send the just-cleared keys.
                    baselineAnswersRef.current = {};
                    setCurrentStepIndex(0);
                  }}>{t("Submit")}</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {errorModal && (
          <div
            className="smrtdocs-modal-overlay"
            style={{ zIndex: 10002 }}
            onClick={() => setErrorModal(null)}
          >
            <div
              className="smrtdocs-modal"
              style={{ width: "min(440px, 95vw)" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="smrtdocs-modal-header">
                <h2 style={{ margin: 0, fontSize: "1rem" }}>
                  {t("Resubmission requested")}
                </h2>
              </div>
              <div className="smrtdocs-modal-body">
                <p style={{ fontSize: "0.9rem", lineHeight: 1.5, margin: "0 0 20px" }}>
                  {errorModal}
                </p>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button className="btn-save" onClick={() => setErrorModal(null)}>
                    {t("Close")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {renamingKey !== null && (
          <div className="smrtdocs-modal-overlay" style={{ zIndex: 10001 }}>
            <div className="smrtdocs-modal" style={{ width: "min(420px, 95vw)" }}>
              <div className="smrtdocs-modal-header">
                <h2 style={{ margin: 0, fontSize: "1rem" }}>{t("Rename Photo")}</h2>
              </div>
              <div className="smrtdocs-modal-body">
                <p style={{ fontSize: "0.82rem", color: "#cfcfcf", margin: "0 0 10px" }}>
                  {t("File extension is preserved automatically.")}
                </p>
                <input
                  className="smrtcop-input"
                  autoFocus
                  value={renamingValue}
                  onChange={(e) => setRenamingValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      // Synthesize a Save click — same flow as the button.
                      const safe = renamingValue
                        .trim()
                        .replace(/[\\/:*?"<>|]/g, "_");
                      if (!safe || !renamingKey) return;
                      const current = (formData[renamingKey] as string) || "";
                      const ext = current.match(/\.[^.]+$/)?.[0] || ".jpg";
                      const finalName = safe.endsWith(ext) ? safe : `${safe}${ext}`;
                      updateField(renamingKey, finalName);
                      setRenamingKey(null);
                    } else if (e.key === "Escape") {
                      setRenamingKey(null);
                    }
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 16,
                  }}
                >
                  <button
                    className="btn-cancel"
                    onClick={() => setRenamingKey(null)}
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    className="btn-save"
                    onClick={() => {
                      const safe = renamingValue
                        .trim()
                        .replace(/[\\/:*?"<>|]/g, "_");
                      if (!safe || !renamingKey) return;
                      const current = (formData[renamingKey] as string) || "";
                      const ext = current.match(/\.[^.]+$/)?.[0] || ".jpg";
                      const finalName = safe.endsWith(ext) ? safe : `${safe}${ext}`;
                      updateField(renamingKey, finalName);
                      setRenamingKey(null);
                    }}
                  >
                    {t("Save")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ── Photo lightbox (in-app; iOS can't window.open blob URLs) ── */}
        {photoLightboxUrl && (
          <div
            className="smrtdocs-modal-overlay"
            style={{ zIndex: 10002 }}
            onClick={() => setPhotoLightboxUrl(null)}
          >
            <div
              style={{
                position: "relative",
                maxWidth: "95vw",
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={photoLightboxUrl}
                alt={t("Photo")}
                style={{
                  maxWidth: "95vw",
                  maxHeight: "85vh",
                  objectFit: "contain",
                  borderRadius: 8,
                }}
              />
              <button
                className="btn-cancel"
                style={{ marginTop: 12 }}
                onClick={() => setPhotoLightboxUrl(null)}
              >
                {t("Close")}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Read-only summary of submitted wizard — all sections on one page
  if (reviewSite && reviewTemplate) {
    const sections = reviewTemplate.sections.filter(
      (s) => !s.label.toLowerCase().includes("declaration"),
    );
    const labelStyle: React.CSSProperties = {
      fontSize: "0.75rem", color: "#9aa0a6", textTransform: "uppercase",
      letterSpacing: "0.04em", marginBottom: 4,
    };

    // Photos rendered via the data-driven path below are tracked so the
    // "Additional Photos" catch-all only shows ones a section missed.
    const reviewedPhotoKeys = new Set<string>();
    const isReviewPhoto = (k: string) => isSubmittedPhoto(reviewData, k);

    // Open a stored blob (photo or report) for review in a new tab via the
    // backend base64 proxy (direct browser SAS is disabled by the storage
    // firewall). Tab is opened synchronously so the popup blocker keeps the
    // click's user-gesture, then navigated once the bytes return.
    const openReviewFile = async (fieldKey: string, blobName: string) => {
      const win = window.open("", "_blank");
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/smrtcop/files/${encodeURIComponent(reviewSite.id)}/${encodeURIComponent(fieldKey)}?blobName=${encodeURIComponent(blobName)}&format=base64`,
          { credentials: "include" },
        );
        if (!r.ok) throw new Error(`download ${r.status}`);
        const data = await r.json();
        const binary = atob(data.base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const url = URL.createObjectURL(
          new Blob([bytes], { type: data.contentType || "application/octet-stream" }),
        );
        if (win) win.location.href = url;
        else window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (err) {
        console.error("Failed to open file:", err);
        if (win) win.close();
      }
    };

    // The Ericsson portal blocks clipboard paste, so instead of COPYING a photo
    // we DOWNLOAD it straight into a folder the crew picks (File System Access
    // API). Persist which keys have landed so the ✓ survives a reload.
    const persistDownloaded = (keys: string[]) => {
      if (!keys.length) return;
      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/smrtcop/downloaded-photos/${encodeURIComponent(reviewSite.id)}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ keys, scope: "crew" }),
        },
      ).catch((err) => console.error("Failed to persist downloaded photos:", err));
    };

    // Resolve the destination folder for THIS site — prompt once per site (and
    // again only if permission was lost). null = picker cancelled / unsupported.
    const ensureDirForSite = async (): Promise<any | null> => {
      if (!isFsAccessSupported()) {
        setErrorModal(
          t("Saving photos to a folder needs Google Chrome or Microsoft Edge on desktop."),
        );
        return null;
      }
      const cur = dirHandleRef.current;
      if (cur && cur.siteId === reviewSite.id) {
        if (await ensureDirPermission(cur.handle)) return cur.handle;
        // permission revoked/lost → fall through and re-pick.
      }
      try {
        const handle = await pickPhotoDirectory();
        dirHandleRef.current = { siteId: reviewSite.id, handle };
        return handle;
      } catch {
        return null; // user cancelled the folder picker
      }
    };

    // Write one photo into the chosen folder, then flip its icon to the ✓.
    const downloadReviewPhoto = async (
      k: string,
      blobName: string,
      fileName: string,
    ) => {
      const dir = await ensureDirForSite();
      if (!dir) return;
      setDownloadStatus((s) => ({ ...s, [k]: "saving" }));
      try {
        const bytes = await fetchPhotoBytes(
          process.env.REACT_APP_BACKEND_URL || "",
          reviewSite.id,
          k,
          blobName,
        );
        await writeFileToDir(dir, downloadFileName(k, fileName), bytes);
        setReviewDownloaded((prev) => new Set(prev).add(k));
        setDownloadStatus((s) => {
          const next = { ...s };
          delete next[k];
          return next;
        });
        persistDownloaded([k]);
      } catch (err) {
        console.error("Failed to download photo:", err);
        setDownloadStatus((s) => ({ ...s, [k]: "error" }));
      }
    };

    // Download the given photo keys (the CHECKLIST PHOTO section only — NOT RET
    // /RRH), one after another (no ZIP, no browser downloads). Per-photo status
    // is tracked independently so a later "Download Remaining" reuses this with a
    // pre-filtered key list.
    const downloadAllPhotos = async (candidateKeys: string[]) => {
      const keys = candidateKeys.filter((k) => !!reviewData[`${k}_blob`]);
      if (!keys.length) return;
      const dir = await ensureDirForSite();
      if (!dir) return;
      setBulkDownloading(true);
      const done: string[] = [];
      for (const k of keys) {
        const blobName = reviewData[`${k}_blob`] as string;
        const fileName = reviewData[k] as string;
        setDownloadStatus((s) => ({ ...s, [k]: "saving" }));
        try {
          const bytes = await fetchPhotoBytes(
            process.env.REACT_APP_BACKEND_URL || "",
            reviewSite.id,
            k,
            blobName,
          );
          await writeFileToDir(dir, downloadFileName(k, fileName), bytes);
          done.push(k);
          setDownloadStatus((s) => {
            const next = { ...s };
            delete next[k];
            return next;
          });
        } catch (err) {
          console.error("Failed to download photo:", k, err);
          setDownloadStatus((s) => ({ ...s, [k]: "error" }));
        }
      }
      if (done.length) {
        setReviewDownloaded((prev) => {
          const next = new Set(prev);
          done.forEach((k) => next.add(k));
          return next;
        });
        persistDownloaded(done);
      }
      setBulkDownloading(false);
    };

    // Re-prompt for a folder so the crew can point downloads somewhere else
    // (replaces the remembered handle for this site for the session).
    const changeDownloadFolder = async () => {
      if (!isFsAccessSupported()) {
        setErrorModal(
          t("Saving photos to a folder needs Google Chrome or Microsoft Edge on desktop."),
        );
        return;
      }
      try {
        const handle = await pickPhotoDirectory();
        dirHandleRef.current = { siteId: reviewSite.id, handle };
      } catch {
        // user cancelled — keep the existing folder
      }
    };

    const isPhotoSection = (section: COPSection) =>
      section.type === "photo" || sectionHasPhotos(reviewData, section.id);

    // The "Download Photos" + change-folder (📂) row shown at the bottom of the
    // photo section — reused in both the expanded card and the collapsed footer.
    const renderPhotoBulkRow = (section: COPSection): React.ReactNode => {
      const keys = groupSectionPhotos(reviewData, section.id).flatMap(
        (g) => g.keys,
      );
      if (!keys.length) return null;
      return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, borderTop: "1px solid #2a2a2a", paddingTop: 12, marginTop: 8 }}>
          <span style={{ fontSize: "0.85rem", color: "#c9d1d9" }}>{t("Download Photos")}</span>
          <button
            type="button"
            className="smrtcop-btn-icon smrtcop-btn-view"
            disabled={bulkDownloading}
            style={{ opacity: bulkDownloading ? 0.6 : 1 }}
            onClick={() => downloadAllPhotos(keys)}
            title={t("Download all photos")}
          >
            {bulkDownloading ? <span className="smrtcop-spinner" /> : <DownloadIcon />}
          </button>
          <button
            type="button"
            className="smrtcop-btn-icon smrtcop-btn-view"
            disabled={bulkDownloading}
            style={{ opacity: bulkDownloading ? 0.6 : 1, fontSize: 15 }}
            onClick={changeDownloadFolder}
            title={t("Change download folder")}
          >
            📂
          </button>
        </div>
      );
    };

    // Report generation (BOM / Tape Drop / RET / RG123) lives only in the admin
    // console (ManageSMRTCOP). The crew's View Submission is read-only for those
    // — so no generate handlers here; only the photo-portal confirm below.

    // Record (or clear) that the crew has uploaded photos / the BOM / the tape
    // drop to the Ericsson portal. Optimistic update, reverts on failure.
    const confirmPortal = async (
      kind: "photos" | "bom" | "tapeDrop" | "ret" | "alignment" | "rg123",
      confirmed: boolean,
    ) => {
      const prev = reviewPortal;
      setReviewPortal((p) => ({
        ...p,
        [kind]: confirmed ? { at: new Date().toISOString() } : null,
      }));
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/smrtcop/portal-status/${encodeURIComponent(reviewSite.id)}`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ kind, confirmed }),
          },
        );
        if (!r.ok) throw new Error(`portal-status ${r.status}`);
        const data = await r.json();
        setReviewPortal(data.portalUploads || {});
        // Mirror into the site progression history (admin Status History).
        recordHistory(
          reviewSite.id,
          kind === "photos"
            ? "photosSent"
            : kind === "bom"
              ? "bomSent"
              : kind === "ret"
                ? "retSent"
                : kind === "alignment"
                  ? "alignmentSent"
                  : kind === "rg123"
                    ? "rg123Sent"
                    : "tapeDropSent",
        );
      } catch (err) {
        console.error("Failed to update portal status:", err);
        setReviewPortal(prev);
      }
    };

    // Bottom-of-section "Confirm uploaded to portal" toggle.
    const renderPortalConfirm = (
      kind: "photos" | "bom" | "tapeDrop" | "ret" | "alignment" | "rg123",
      label: string,
    ) => {
      // This is the crew/user view (burger-bar "user view", SMRTCOP.tsx) — all
      // report generation + "Sent to Client" confirmations live in the admin
      // console (ManageSMRTCOP). The one client-portal step the field tech owns
      // is uploading photos, so ONLY "Photo's Sent" shows here, regardless of
      // whether the signed-in user also happens to hold cop.admin.
      if (kind !== "photos") return null;
      const entry = reviewPortal[kind];
      const confirmed = !!entry;
      // Grey until marked sent, then green. Only clickable once every photo has
      // finished uploading (no in-flight queue items) and at least one photo is
      // present — the crew can't mark "sent to portal" while photos are still
      // landing. An already-confirmed button stays clickable so it can be undone.
      const uploading = Object.keys(photoUploadStatus).length > 0;
      const hasPhotos = Object.keys(reviewData).some((k) =>
        isSubmittedPhoto(reviewData, k),
      );
      const enabled = confirmed || (hasPhotos && !uploading);
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
            marginTop: 16,
          }}
        >
          <button
            type="button"
            className="smrtcop-btn-action"
            disabled={!enabled}
            style={{
              flex: "none",
              width: 180,
              height: 34,
              fontSize: "0.85rem",
              background: confirmed ? "#1faa59" : "#2a2a2a",
              borderColor: confirmed ? "#1faa59" : "#2a2a2a",
              color: "#fff",
            }}
            title={
              !enabled
                ? uploading
                  ? t("Photos are still uploading — please wait.")
                  : t("Upload photos before marking them sent.")
                : undefined
            }
            onClick={() => confirmPortal(kind, !confirmed)}
          >
            {confirmed ? `✓ ${label}` : label}
          </button>
          {confirmed && entry.at && (
            <div style={{ fontSize: "0.7rem", color: "#1faa59" }}>
              {t("Confirmed")} {new Date(entry.at).toLocaleString()}
            </div>
          )}
        </div>
      );
    };

    const renderReviewPhoto = (k: string) => {
      reviewedPhotoKeys.add(k);
      const fileName = reviewData[k] as string;
      const blobName = reviewData[`${k}_blob`] as string | undefined;
      const dataUrl = reviewData[`${k}_dataUrl`] as string | undefined; // legacy
      if (!blobName && !dataUrl) {
        return (
          <div key={k} style={{ color: "#9aa0a6" }}>{fileName} (preview unavailable)</div>
        );
      }
      // Persisted ✓ once written to disk; transient "saving"/"error" otherwise.
      const dstatus = downloadStatus[k];
      const downloaded = reviewDownloaded.has(k) && !dstatus;
      return (
        <div
          key={k}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{ color: "#0d6efd", fontWeight: 600, cursor: "pointer" }}
            onClick={() => {
              if (blobName) openReviewFile(k, blobName);
              else window.open(dataUrl as string, "_blank");
            }}
          >
            {fileName}
          </span>
          {blobName && (
            <button
              type="button"
              className="smrtcop-btn-icon"
              style={{
                width: 28,
                height: 28,
                fontSize: 15,
                opacity: dstatus === "saving" ? 0.5 : 1,
                color: downloaded
                  ? "#1faa59"
                  : dstatus === "error"
                    ? "#c62828"
                    : undefined,
              }}
              disabled={dstatus === "saving" || bulkDownloading}
              onClick={() => downloadReviewPhoto(k, blobName, fileName)}
              title={dstatus === "error" ? t("Download failed") : t("Download Photo")}
            >
              {downloaded ? "✓" : dstatus === "error" ? "✕" : "⧉"}
            </button>
          )}
        </div>
      );
    };

    const renderReviewField = (q: COPQuestion) => {
      const val = reviewData[q.key];
      const lbl = (
        <div style={labelStyle}>
          {q.label}{q.required ? " *" : ""}
        </div>
      );
      if (q.type === "heading") {
        return <div style={{ fontWeight: 700, fontSize: "1rem", color: "#fff", paddingTop: 6, borderBottom: "1px solid #2a2a2a", paddingBottom: 4, textAlign: "center" }}>{q.label}</div>;
      }
      if (q.type === "certification") {
        return (
          <div style={{ background: "#1a1a1a", border: "1px solid #1faa59", borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", gap: 6, textAlign: "center" }}>
            <div><span style={labelStyle}>Certified By: </span><span style={{ color: "#fff" }}>{reviewData[`${q.key}_certifiedBy`] || "—"}</span></div>
            <div><span style={labelStyle}>Signature: </span><span style={{ color: "#1faa59" }}>{reviewData[`${q.key}_signature`] || "—"}</span></div>
            <div style={{ fontSize: "0.72rem", color: "#1faa59" }}>
              {reviewData[`${q.key}_meta`]?.timestamp ? `Signed: ${new Date(reviewData[`${q.key}_meta`].timestamp).toLocaleString()}` : ""}
            </div>
          </div>
        );
      }
      if (q.type === "checkbox") {
        const checked: string[] = val || [];
        return <div style={{ textAlign: "center" }}>{lbl}<div style={{ color: "#fff" }}>{checked.length ? "✓" : "—"}</div></div>;
      }
      if (q.type === "photo") {
        // Inline photo question — match the base key plus any crew-added
        // `__extra__<id>` slots, all discovered from the submitted data.
        const slots = Object.keys(reviewData)
          .filter(
            (k) => (k === q.key || k.startsWith(`${q.key}__`)) && isReviewPhoto(k),
          )
          .sort();
        return (
          <div style={{ textAlign: "center" }}>
            {lbl}
            {slots.length === 0 ? (
              <div style={{ color: "#555" }}>{t("No file uploaded")}</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {slots.map((k) => renderReviewPhoto(k))}
              </div>
            )}
          </div>
        );
      }
      return <div style={{ textAlign: "center" }}>{lbl}<div style={{ color: "#fff" }}>{val || "—"}</div></div>;
    };

    return (
      <div className="smrtcop-page">
        <div style={{ maxWidth: "700px", margin: "0 auto", padding: "0 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div style={{ textAlign: "center", flex: 1 }}>
              <h2 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 700, color: "#fff" }}>
                {reviewSite.name} ({reviewSite.meta.siteId})
              </h2>
              <div style={{ fontSize: "0.72rem", color: "#9aa0a6", marginTop: 4 }}>
                {reviewSite.status === "Complete" ? t("Submitted on") : t("Last saved")} {new Date(reviewSite.updatedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} at {new Date(reviewSite.updatedAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })}
              </div>
            </div>
            <button
              className="smrtcop-btn-action smrtcop-btn-cancel"
              style={{ flex: "none", width: "auto", padding: "0 12px", height: 28, fontSize: "0.78rem" }}
              onClick={closeReview}
            >
              Back
            </button>
          </div>

          {sections.map((section) => (
            <div key={section.id} className="template-card" style={{ marginBottom: 16, maxWidth: "700px", marginLeft: "auto", marginRight: "auto" }}>
              <div
                className="template-card-header"
                style={{ justifyContent: "center", cursor: "pointer" }}
                onClick={() => toggleReviewSection(section.id)}
                title={reviewCollapsed.has(section.id) ? t("Expand") : t("Collapse")}
              >
                <span style={{ marginRight: 10, color: "#9aa0a6", fontSize: "1.35rem", lineHeight: 1, userSelect: "none" }}>
                  {reviewCollapsed.has(section.id) ? "▸" : "▾"}
                </span>
                <div className="template-card-title" style={{ textAlign: "center" }}>{section.label}</div>
              </div>
              {/* Always render the body (only toggle visibility) so the photo
                  key-tracking side effects in renderReviewPhoto still run when a
                  section is collapsed — otherwise its photos would be treated as
                  orphaned and re-appear under "Additional Photos" below. */}
              <div style={{ display: reviewCollapsed.has(section.id) ? "none" : "flex", flexDirection: "column", gap: 12 }}>
                {section.type === "reports" || section.type === "alignment" ? (
                  // Reports stored at reviewData[fieldKey] as a {blobName,…} object.
                  // Include a legacy single-file `ret_report` slot for sites
                  // submitted before RET moved to per-sector/port TXTRPT files.
                  // The review reads the RAW template sections (not the expanded
                  // runtime ones), so the Alignment section still has its
                  // first-class type "alignment" — treat that as the alignment
                  // report here (else its card renders empty).
                  <>
                    {(section.type === "alignment" || section.reportKind === "alignment"
                      ? [ALIGNMENT_SLOT]
                      : [
                          ...(reviewData["ret_report"]
                            ? [{ fieldKey: "ret_report", label: "RET Report", accept: RET_ACCEPT }]
                            : []),
                          ...retUploadSlots(
                            reviewData,
                            retPositionsOf(reviewSite?.meta),
                          ),
                        ]
                    ).map((slot) => {
                      const ref = reviewData[slot.fieldKey] as ReportFileRef | undefined;
                      return (
                        <div key={slot.fieldKey} style={{ textAlign: "center" }}>
                          <div style={labelStyle}>
                            {t(retSlotLabel(slot, retPositionsOf(reviewSite?.meta)))}
                          </div>
                          {ref && ref.blobName ? (
                            isTxtrptSlot(slot.fieldKey) ? (
                              // RET .txtrpt: readable PDF view + copy raw + download original.
                              <div style={{ display: "flex", gap: 10, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
                                <span
                                  style={{ color: "#0d6efd", fontWeight: 600, cursor: "pointer" }}
                                  title={t("View Report (PDF)")}
                                  onClick={() => {
                                    const win = window.open("", "_blank");
                                    openTxtrptReportPdf(win, reviewSite.id, slot.fieldKey, ref).catch((err) => {
                                      console.error("Failed to open report PDF:", err);
                                      if (win) win.close();
                                    });
                                  }}
                                >
                                  {ref.name}
                                </span>
                                <button
                                  type="button"
                                  className="smrtcop-btn-icon"
                                  style={{
                                    width: 28,
                                    height: 28,
                                    fontSize: 15,
                                    opacity: copyStatus[slot.fieldKey] === "copying" ? 0.5 : 1,
                                    color: copyStatus[slot.fieldKey] === "done" ? "#1faa59" : copyStatus[slot.fieldKey] === "error" ? "#c62828" : undefined,
                                  }}
                                  disabled={copyStatus[slot.fieldKey] === "copying"}
                                  title={copyStatus[slot.fieldKey] === "error" ? t("Copy failed") : t("Copy TXTRPT to clipboard")}
                                  onClick={() => {
                                    setCopyStatus((s) => ({ ...s, [slot.fieldKey]: "copying" }));
                                    copyTxtrptText(reviewSite.id, slot.fieldKey, ref)
                                      .then(() => setCopyStatus((s) => ({ ...s, [slot.fieldKey]: "done" })))
                                      .catch((err) => { console.error("Failed to copy TXTRPT:", err); setCopyStatus((s) => ({ ...s, [slot.fieldKey]: "error" })); })
                                      .finally(() => setTimeout(() => setCopyStatus((s) => { const n = { ...s }; delete n[slot.fieldKey]; return n; }), 2000));
                                  }}
                                >
                                  {copyStatus[slot.fieldKey] === "done" ? "✓" : copyStatus[slot.fieldKey] === "error" ? "✕" : "⧉"}
                                </button>
                              </div>
                            ) : (
                              <span
                                style={{ color: "#0d6efd", fontWeight: 600, cursor: "pointer" }}
                                onClick={() => openReviewFile(slot.fieldKey, ref.blobName)}
                              >
                                {ref.name}
                              </span>
                            )
                          ) : (
                            <div style={{ color: "#555" }}>{t("No file uploaded")}</div>
                          )}
                        </div>
                      );
                    })}

                    {/* RRH Serial Number photos — captured per sector alongside
                        the RET scans. Render them here (like the admin view) so
                        they group with the RET section instead of spilling into
                        the "Additional Photos" catch-all at the bottom. */}
                    {!(section.type === "alignment" || section.reportKind === "alignment") && (() => {
                      const deltaAdded = isDeltaAdded(reviewData);
                      const positions = retPositionsOf(reviewSite?.meta);
                      const sectorsWithPhotos = rrhSectors(deltaAdded)
                        .map((sector) => ({
                          sector,
                          slots: rrhSlotsForSector(sector.key, positions).filter((slot) =>
                            isReviewPhoto(slot.fieldKey),
                          ),
                        }))
                        .filter((s) => s.slots.length > 0);
                      if (!sectorsWithPhotos.length) return null;
                      return (
                        <div style={{ borderTop: "1px solid #2a2a2a", paddingTop: 12, marginTop: 8 }}>
                          <div style={{ ...labelStyle, color: "#fff", fontWeight: 600, marginBottom: 8, textAlign: "center" }}>
                            {t("RRH Serial Numbers")}
                          </div>
                          {sectorsWithPhotos.map(({ sector, slots }) => (
                            <div key={sector.key} style={{ marginBottom: 10 }}>
                              <div style={labelStyle}>{sector.label}</div>
                              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                {slots.map((slot) => {
                                  const serial = (reviewData[`${slot.fieldKey}_serial`] as string) || "";
                                  return (
                                    <div key={slot.fieldKey} style={{ textAlign: "center" }}>
                                      <div style={labelStyle}>{rrhSlotLabel(slot, positions)}</div>
                                      {renderReviewPhoto(slot.fieldKey)}
                                      {serial && (
                                        <div style={{ fontSize: "0.78rem", color: "#fff" }}>{serial}</div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {/* RET/Alignment report generation + "Sent to Client"
                        confirmations are admin-console only (ManageSMRTCOP) —
                        the crew's View Submission is read-only for these. */}
                  </>
                ) : section.type === "bom" ? (
                  // BOM table persisted at reviewData[`${section.id}_bom`].
                  (() => {
                    const rows = (reviewData[`${section.id}_bom`] as BomRow[]) || [];
                    const confirmed = !!reviewData[`${section.id}_bomConfirmed`];
                    if (!rows.length) {
                      return <div style={{ textAlign: "center", color: "#9aa0a6" }}>{t("No BOM data submitted")}</div>;
                    }
                    return (
                      <>
                        <table className="smrtcop-bom-table smrtcop-bom-view">
                          <thead>
                            <tr>
                              <th>{t("Item")}</th><th>{t("Description")}</th><th>{t("UOM")}</th><th>{t("Qty")}</th><th>{t("Actual Installed")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r, i) => {
                              const planned = String(r.plannedQty ?? "").trim();
                              const actual = String(r.actualInstalled ?? "").trim();
                              const diff = actual !== "" && actual !== planned;
                              return (
                                <tr key={i}>
                                  <td>{r.item}</td><td>{r.description}</td><td>{r.uom}</td><td>{r.plannedQty}</td>
                                  <td className={diff ? "smrtcop-bom-diff" : undefined}>{r.actualInstalled || "—"}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 10 }}>
                          <span style={{ fontSize: "0.85rem", color: confirmed ? "#1faa59" : "#d4a017" }}>
                            {confirmed ? `✓ ${t("Actual Installed Quantities Verified")}` : t("Quantities not confirmed")}
                          </span>
                        </div>
                        {renderPortalConfirm("bom", t("BOM Sent"))}
                      </>
                    );
                  })()
                ) : section.type === "jha" ? (
                  // JHA — one PDF per day onsite, stored at reviewData[`jha_day_{n}`].
                  <>
                    {jhaSlots(reviewData).map((slot) => {
                      const ref = reviewData[slot.fieldKey] as ReportFileRef | undefined;
                      return (
                        <div key={slot.fieldKey} style={{ textAlign: "center" }}>
                          <div style={labelStyle}>{t(slot.label)}</div>
                          {ref && ref.blobName ? (
                            <span
                              style={{ color: "#0d6efd", fontWeight: 600, cursor: "pointer" }}
                              onClick={() => openReviewFile(slot.fieldKey, ref.blobName)}
                            >
                              {ref.name}
                            </span>
                          ) : (
                            <div style={{ color: "#555" }}>{t("No file uploaded")}</div>
                          )}
                        </div>
                      );
                    })}
                    {/* RG123 generation + "Sent to Client" is admin-console only. */}
                  </>
                ) : section.type === "photo" || sectionHasPhotos(reviewData, section.id) ? (
                  // Photo section — captured photos live under `${id}_photo_…`
                  // keys, discovered from the submitted data (not the static
                  // template questions, whose keys don't match).
                  (() => {
                    const groups = groupSectionPhotos(reviewData, section.id);
                    if (!groups.length) {
                      return <div style={{ textAlign: "center", color: "#9aa0a6" }}>{t("No photos uploaded")}</div>;
                    }
                    return (
                      <>
                        {groups.map((g) => (
                          <div key={g.label}>
                            <div style={{ ...labelStyle, color: "#fff", fontWeight: 600, marginTop: 4, textAlign: "center" }}>{g.label}</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              {g.keys.map((k) => (
                                <div key={k} style={{ textAlign: "center" }}>{renderReviewPhoto(k)}</div>
                              ))}
                            </div>
                          </div>
                        ))}
                        {/* Bulk download + change-folder, above "Photo's Sent". */}
                        {renderPhotoBulkRow(section)}
                        {renderPortalConfirm("photos", t("Photo's Sent"))}
                      </>
                    );
                  })()
                ) : (
                  // Skip conditional fields whose "show when" rule wasn't
                  // satisfied by the submitted answers — mirror the wizard so
                  // the review shows only fields the user actually completed.
                  <>
                    {expandPhotoQuestions(section)
                      .filter((q) => isQuestionVisible(q, reviewData))
                      .map((q) => (
                        <div key={q.key}>{renderReviewField(q)}</div>
                      ))}
                    {/* Tape Drop (RG114) generation + "Sent to Client" is
                        admin-console only — hidden on the crew View Submission. */}
                  </>
                )}
              </div>
              {/* Collapsed photo section still shows its footer — Download
                  Photos, change-folder, and the green "Photo's Sent" box — so
                  the crew can act on it without expanding (mirrors admin). */}
              {reviewCollapsed.has(section.id) && isPhotoSection(section) && (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {renderPhotoBulkRow(section)}
                  {renderPortalConfirm("photos", t("Photo's Sent"))}
                </div>
              )}
            </div>
          ))}

          {/* Any photos not attributed to a section above (inline question
              photos, crew-added extras). */}
          {(() => {
            const leftover = leftoverPhotos(reviewData, reviewedPhotoKeys);
            if (!leftover.length) return null;
            return (
              <div className="template-card" style={{ marginBottom: 16, maxWidth: "700px", marginLeft: "auto", marginRight: "auto" }}>
                <div className="template-card-header" style={{ justifyContent: "center" }}>
                  <div className="template-card-title" style={{ textAlign: "center" }}>{t("Additional Photos")}</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {leftover.map((k) => (
                    <div key={k} style={{ textAlign: "center" }}>{renderReviewPhoto(k)}</div>
                  ))}
                </div>
              </div>
            );
          })()}

        </div>
      </div>
    );
  }

  // Viewer mode never falls through to the crew's site list — that shell is the
  // tech's own workspace and has nothing to do with reviewing one site. Until
  // the sites + templates arrive and the wizard opens, hold on a placeholder.
  if (viewerMode) {
    const target = sites.find((s) => s.id === viewSiteId);
    const missing = sites.length > 0 && !target;
    // Submitted between the admin opening the list and clicking through. The
    // wizard would be blank (submit empties `answers`), so say so rather than
    // showing an empty COP.
    const submitted = target?.status === "Complete";
    const detail = submitted
      ? t(
          "Its answers moved into the submitted package when the crew signed off. Use Review to see what was handed in, or Reopen to put it back into the wizard.",
        )
      : t("It may have been deleted, or it has no template assigned yet.");
    return (
      <div className="smrtcop-page">
        <div style={{ maxWidth: 620, margin: "0 auto", paddingTop: 60 }}>
          <div className="smrtcop-card" style={{ padding: 24 }}>
            <div style={{ fontSize: "0.95rem", marginBottom: 12 }}>
              {submitted
                ? t("This site has already been submitted.")
                : missing
                  ? t("That site could not be opened.")
                  : t("Opening the crew's wizard…")}
            </div>
            {(missing || submitted) && (
              <div
                style={{
                  color: "#9aa0a6",
                  fontSize: "0.82rem",
                  marginBottom: 16,
                }}
              >
                {detail}
              </div>
            )}
            <button className="btn-cancel" onClick={() => onCloseViewer?.()}>
              {t("Close")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Site list
  return (
    <div className="smrtcop-page">
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <h1 className="smrtcop-title">SMRT COP</h1>

        {mySites.length === 0 && (
          <div
            className="smrtcop-card"
            style={{ padding: 30, textAlign: "center", color: "#9aa0a6" }}
          >
            No sites assigned to you.
          </div>
        )}

        {mySites.length > 0 && (
          <div className="smrtcop-card">
            <div
              className="smrtcop-overview-header"
              style={{ gridTemplateColumns: "1.5fr 1fr 1fr" }}
            >
              <div>Site ID</div>
              <div>Status</div>
              <div>Actions</div>
            </div>

            {mySites.map((site) => (
              <div
                key={site.id}
                className="smrtcop-overview-row"
                style={{ gridTemplateColumns: "1.5fr 1fr 1fr" }}
              >
                <div>{site.meta.siteId}</div>
                <div
                  className={`smrtcop-status ${progressStatusFor(site)
                    .toLowerCase()
                    .replace(/ /g, "-")}`}
                >
                  {progressStatusFor(site)}
                </div>
                <div className="smrtcop-actions">
                  <button
                    className="smrtcop-btn-icon smrtcop-btn-view"
                    title="View Site Info"
                    onClick={() => {
                      setViewingSite(site);
                      setViewDocIndex(0);
                    }}
                  >
                    👁
                  </button>
                  <button
                    className="smrtcop-btn-icon smrtcop-btn-history"
                    title={t("History")}
                    onClick={() => setHistorySite(site)}
                  >
                    <span style={{ fontSize: "20px", lineHeight: 1 }}>↻</span>
                  </button>
                  {/* Complete → View Submission only (start button gone). In
                      progress with ≥1 saved step → BOTH buttons so the crew can
                      resume the wizard OR review what's saved so far. Not yet
                      started → start button only. */}
                  {(site.status === "Complete" ||
                    startedSiteIds.has(site.id)) && (
                    <button
                      className="smrtcop-btn-icon"
                      style={{ background: "#d4a017", borderColor: "#d4a017", color: "#fff" }}
                      title="View Submission"
                      onClick={() => openReview(site)}
                    >
                      📄
                    </button>
                  )}
                  {site.status !== "Complete" && (
                    <button
                      className="smrtcop-btn-icon smrtcop-btn-issue"
                      title={startedSiteIds.has(site.id) ? "Continue COP" : "Start COP"}
                      onClick={() => openWizard(site)}
                    >
                      ▶
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Status History modal ── */}
      {historySite && (
        <div
          className="smrtdocs-modal-overlay"
          onClick={() => setHistorySite(null)}
        >
          <div
            className="smrtdocs-modal"
            style={{ width: "460px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>{t("Status History")}</h2>
              <button className="btn-cancel" onClick={() => setHistorySite(null)}>
                {t("Close")}
              </button>
            </div>
            <div className="smrtdocs-modal-body">
              <div
                style={{ marginBottom: 14, color: "#9aa0a6", fontSize: "0.8rem" }}
              >
                {historySite.meta.siteId || historySite.name} ·{" "}
                <span style={{ color: "#fff" }}>
                  {deriveProgressStatus(historySite.history)}
                </span>
              </div>
              <div className="smrtcop-timeline">
                {buildSiteTimeline(historySite.history).map((s) => (
                  <div
                    key={s.key}
                    className={`smrtcop-tl-step${s.done ? " smrtcop-tl-done" : ""}`}
                  >
                    <span className="smrtcop-tl-dot" />
                    <div className="smrtcop-tl-body">
                      <div className="smrtcop-tl-name">{t(s.label)}</div>
                      <div className="smrtcop-tl-meta">
                        {s.done
                          ? `${s.byName ? `${s.byName} · ` : ""}${
                              s.at ? new Date(s.at).toLocaleString() : ""
                            }`
                          : t("Pending")}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
